/**
  * @formwerk/core v0.10.7
  * (c) 2025 Abdelrahman Awad
  * @license MIT
  */
import { computed, defineComponent, getCurrentInstance, getCurrentScope, h, inject, markRaw, nextTick, onBeforeUnmount, onMounted, onScopeDispose, onUnmounted, provide, reactive, readonly, ref, shallowRef, toValue, useId, watch, watchEffect } from "vue";
import { registerField, registerForm } from "@formwerk/devtools";
import { klona } from "klona/full";
import { getDotPath } from "@standard-schema/utils";
import { DateFormatter, GregorianCalendar, fromDate, now, toCalendar, toCalendarDate, toTimeZone } from "@internationalized/date";

//#region packages/core/src/utils/common.ts
const isSSR = typeof window === "undefined";
function useUniqId(prefix) {
	return prefix ? `${prefix}-${useId()}` : useId() || "";
}
function createDescriptionProps(inputId) {
	return { id: `${inputId}-d` };
}
function createDescribedByProps({ inputId, description }) {
	const descriptionRef = shallowRef();
	const descriptionProps = withRefCapture(createDescriptionProps(inputId), descriptionRef);
	const describedByProps = computed(() => {
		return { "aria-describedby": descriptionRef.value && toValue(description) ? descriptionProps.id : void 0 };
	});
	return {
		describedByProps,
		descriptionProps
	};
}
function createRefCapture(elRef) {
	return function captureRef(el) {
		elRef.value = el;
	};
}
function arrayToKeys(keys) {
	const keyDict = keys.reduce((acc, key) => {
		acc[key] = true;
		return acc;
	}, {});
	return keyDict;
}
function propsToValues(props, keys) {
	const keyDict = arrayToKeys(keys);
	return Object.fromEntries(Object.entries(props).filter(([key]) => keyDict[key]).map(([key, value]) => [key, toValue(value)]));
}
function normalizeProps(props, exclude) {
	if ("__isFwNormalized__" in props) return props;
	const excludeDict = exclude ? arrayToKeys(exclude) : {};
	const normalized = Object.fromEntries(Object.keys(props).map((key) => {
		if (!excludeDict[key]) return [key, () => toValue(props[key])];
		if (isCallable(props[key])) return [key, (...args) => props[key](...args)];
		return [key, props[key]];
	}));
	normalized.__isFwNormalized__ = true;
	return normalized;
}
function getNextCycleArrIdx(idx, arr) {
	const r = idx % arr.length;
	return r < 0 ? r + arr.length : r;
}
/**
* Injects a ref capture to the props object
*/
function withRefCapture(props, inputEl, elementRef) {
	if (!elementRef) props.ref = createRefCapture(inputEl);
	return props;
}
function isCallable(fn) {
	return typeof fn === "function";
}
function isNullOrUndefined(value) {
	return value === null || value === void 0;
}
function isEmpty(value) {
	return isNullOrUndefined(value) || value === "";
}
function normalizeArrayable(value) {
	return Array.isArray(value) ? [...value] : [value];
}
/**
* Clones a value deeply. I wish we could use `structuredClone` but it's not supported because of the deep Proxy usage by Vue.
* I added some shortcuts here to avoid cloning some known types we don't plan to support.
* https://github.com/lukeed/klona/tree/master/src
*/
function cloneDeep(value) {
	if (isFileOrBlob(value) || value instanceof RegExp) return value;
	return klona(value);
}
function isPromise(value) {
	return value instanceof Promise;
}
function isFileOrBlob(a) {
	if (isSSR) return false;
	return a instanceof File || a instanceof Blob;
}
function isFile(a) {
	if (isSSR) return false;
	return a instanceof File;
}
/**
* Compares if two values are the same borrowed from:
* https://github.com/epoberezkin/fast-deep-equal
* We added a case for file matching since `Object.keys` doesn't work with Files.
**/
function isEqual(a, b) {
	if (a === b) return true;
	if (a && b && typeof a === "object" && typeof b === "object") {
		if (a.constructor !== b.constructor) return false;
		var length, i, keys;
		if (Array.isArray(a)) {
			length = a.length;
			if (length != b.length) return false;
			for (i = length; i-- !== 0;) if (!isEqual(a[i], b[i])) return false;
			return true;
		}
		if (a instanceof Map && b instanceof Map) {
			if (a.size !== b.size) return false;
			for (i of a.entries()) if (!b.has(i[0])) return false;
			for (i of a.entries()) if (!isEqual(i[1], b.get(i[0]))) return false;
			return true;
		}
		if (isFile(a) && isFile(b)) {
			if (a.size !== b.size) return false;
			if (a.name !== b.name) return false;
			if (a.lastModified !== b.lastModified) return false;
			if (a.type !== b.type) return false;
			return true;
		}
		if (a instanceof Set && b instanceof Set) {
			if (a.size !== b.size) return false;
			for (i of a.entries()) if (!b.has(i[0])) return false;
			return true;
		}
		if (ArrayBuffer.isView(a) && ArrayBuffer.isView(b)) {
			length = a.length;
			if (length != b.length) return false;
			for (i = length; i-- !== 0;) if (a[i] !== b[i]) return false;
			return true;
		}
		if (a.constructor === RegExp) return a.source === b.source && a.flags === b.flags;
		if (a.valueOf !== Object.prototype.valueOf) return a.valueOf() === b.valueOf();
		if (a.toString !== Object.prototype.toString) return a.toString() === b.toString();
		keys = Object.keys(a);
		length = keys.length;
		for (i = length; i-- !== 0;) {
			var key = keys[i];
			if (!isEqual(a[key], b[key])) return false;
		}
		return true;
	}
	return a !== a && b !== b;
}
function withLatestCall(fn, onDone) {
	let latestRun;
	return async function runLatest(...args) {
		const pending = fn(...args);
		latestRun = pending;
		const result = await pending;
		if (pending !== latestRun) return result;
		latestRun = void 0;
		return onDone(result, args);
	};
}
function batchAsync(inner, ms = 0) {
	let timer = null;
	let resolves = [];
	return function(...args) {
		if (timer) clearTimeout(timer);
		timer = window.setTimeout(() => {
			const result = inner(...args);
			resolves.forEach((r) => r(result));
			resolves = [];
		}, ms);
		return new Promise((resolve) => resolves.push(resolve));
	};
}
function warn(message) {
	if (process.env.NODE_ENV !== "production") console.warn(`[Formwerk]: ${message}`);
}
function isInputElement(el) {
	if (!el) return false;
	return [
		"INPUT",
		"TEXTAREA",
		"SELECT"
	].includes(el.tagName);
}
function isLabelElement(el) {
	return el?.tagName === "LABEL";
}
function isButtonElement(el) {
	return el?.tagName === "BUTTON";
}
function toggleValueSelection(current, value, force) {
	const nextValue = normalizeArrayable(current);
	const idx = nextValue.findIndex((v) => isEqual(v, value));
	const shouldAdd = force ?? idx === -1;
	if (!shouldAdd) {
		nextValue.splice(idx, 1);
		return nextValue;
	}
	if (idx === -1) nextValue.push(value);
	return nextValue;
}
function removeFirst(items, predicate) {
	const idx = items.findIndex(predicate);
	if (idx >= 0) {
		items.splice(idx, 1);
		return;
	}
}
/**
* VueUse uses this to dispose of watchers/events in composable hooks.
* https://vueuse.org/shared/tryOnScopeDispose/
*/
function tryOnScopeDispose(fn) {
	if (getCurrentScope()) {
		onScopeDispose(fn);
		return true;
	}
	return false;
}
function hasKeyCode(e, code) {
	return e.code === code || e.key === code;
}
function _combineIssueItems(items, getPath) {
	const issueMap = {};
	for (const item of items) {
		const path = getPath(item);
		if (!issueMap[path]) issueMap[path] = {
			path,
			messages: []
		};
		if ("messages" in item) issueMap[path].messages.push(...item.messages);
		else issueMap[path].messages.push(item.message);
	}
	return Object.values(issueMap);
}
/**
* Aggregates standard schema issues by path.
*/
function combineStandardIssues(issues) {
	return _combineIssueItems(issues, (issue) => issue.path ? getDotPath(issue) ?? "" : "");
}
/**
* Aggregates formwerk's issues by path.
*/
function combineIssues(issues) {
	return _combineIssueItems(issues, (issue) => issue.path ?? "");
}
function fromNumberish(value) {
	const innerVal = toValue(value);
	if (isNullOrUndefined(innerVal)) return void 0;
	const num = Number(innerVal);
	return Number.isNaN(num) ? void 0 : num;
}
function debounce(ms, fn) {
	if (ms <= 0) return fn;
	let timer = null;
	return function(...args) {
		if (timer) clearTimeout(timer);
		timer = window.setTimeout(() => fn(...args), ms);
	};
}
/**
* Checks if an element is in the viewport of another element.
*/
function isInViewport(el, reference) {
	if (!el || !reference) return false;
	const rect = el.getBoundingClientRect();
	const referenceRect = reference.getBoundingClientRect();
	return rect.top >= referenceRect.top && rect.bottom <= referenceRect.bottom;
}

//#endregion
//#region packages/core/src/helpers/useEventListener/useEventListener.ts
function useEventListener(targetRef, event, listener, opts) {
	let controller;
	function cleanup() {
		controller?.abort();
	}
	function setup(target) {
		if (toValue(opts?.disabled)) return;
		controller = new AbortController();
		const events = normalizeArrayable(event);
		const listenerOpts = {
			signal: controller.signal,
			passive: toValue(opts?.passive)
		};
		events.forEach((evt) => {
			normalizeArrayable(target).forEach((el) => {
				if (typeof evt === "string") {
					el.addEventListener(evt, listener, listenerOpts);
					return;
				}
				const [evtName, predicate] = evt;
				el.addEventListener(evtName, (e) => {
					if (predicate(e)) listener(e);
				}, listenerOpts);
			});
		});
	}
	const stopWatch = watch(() => [toValue(targetRef), toValue(opts?.disabled)], ([el, disabled]) => {
		cleanup();
		if (disabled) return;
		const targets = normalizeArrayable(el).filter((elm) => !!elm);
		setup(targets);
	}, { immediate: true });
	tryOnScopeDispose(() => {
		cleanup();
		stopWatch();
	});
}

//#endregion
//#region packages/shared/src/utils.ts
function isObjectLike(value) {
	return typeof value === "object" && value !== null;
}
function getTag(value) {
	if (value == null) return value === void 0 ? "[object Undefined]" : "[object Null]";
	return Object.prototype.toString.call(value);
}
function isPlainObject(value) {
	if (!isObjectLike(value) || getTag(value) !== "[object Object]") return false;
	if (Object.getPrototypeOf(value) === null) return true;
	let proto = value;
	while (Object.getPrototypeOf(proto) !== null) proto = Object.getPrototypeOf(proto);
	return Object.getPrototypeOf(value) === proto;
}
function merge(target, source) {
	Object.keys(source).forEach((key) => {
		if (isPlainObject(source[key]) && isPlainObject(target[key])) {
			if (!target[key]) target[key] = {};
			merge(target[key], source[key]);
			return;
		}
		target[key] = source[key];
	});
	return target;
}
function isIndex(value) {
	return Number(value) >= 0;
}
const isObject = (obj) => obj !== null && !!obj && typeof obj === "object" && !Array.isArray(obj);

//#endregion
//#region packages/core/src/utils/path.ts
function isContainerValue(value) {
	return isObject(value) || Array.isArray(value);
}
/**
* True if the value is an empty object or array
*/
function isEmptyContainer(value) {
	if (Array.isArray(value)) return value.length === 0;
	return isObject(value) && Object.keys(value).length === 0;
}
/**
* Checks if the path opted out of nested fields using `[fieldName]` syntax
*/
function isEscapedPath(path) {
	return /^\[.+\]$/i.test(path);
}
function cleanupNonNestedPath(path) {
	if (isEscapedPath(path)) return path.replace(/\[|\]/gi, "");
	return path;
}
function getFromPath(object, path, fallback) {
	if (!object) return fallback;
	if (isEscapedPath(path)) return object[cleanupNonNestedPath(path)];
	const normalizedPath = normalizePath(path);
	const resolvedValue = (normalizedPath || "").split(".").filter(Boolean).reduce((acc, propKey) => {
		if (isContainerValue(acc) && propKey in acc) return acc[propKey];
		return fallback;
	}, object);
	return resolvedValue;
}
function getLastReachableValue(object, path) {
	if (!object) return void 0;
	if (isEscapedPath(path)) return object[cleanupNonNestedPath(path)];
	const normalizedPath = normalizePath(path);
	let resolvedValue = object;
	for (const propKey of (normalizedPath || "").split(".").filter(Boolean)) {
		if (!isContainerValue(resolvedValue)) return resolvedValue;
		if (!(propKey in resolvedValue)) return resolvedValue;
		resolvedValue = resolvedValue[propKey];
	}
	return resolvedValue;
}
/**
* Sets a nested property value in a path, creates the path properties if it doesn't exist
*/
function setInPath(object, path, value, setAllChildren) {
	if (isEscapedPath(path)) {
		object[cleanupNonNestedPath(path)] = value;
		return;
	}
	const normalizedPath = normalizePath(path);
	const keys = normalizedPath.split(".").filter(Boolean);
	let acc = object;
	for (let i = 0; i < keys.length; i++) {
		if (i === keys.length - 1) {
			const targetKey = keys[i];
			if (setAllChildren && isObjectLike(acc[targetKey])) setAllChildrenToValue(acc[targetKey], value);
			else acc[targetKey] = value;
			return;
		}
		if (keys[i] in acc && !isNullOrUndefined(acc[keys[i]]) && !isContainerValue(acc[keys[i]])) acc[keys[i]] = isIndex(keys[i + 1]) ? [] : {};
		if (!(keys[i] in acc) || isNullOrUndefined(acc[keys[i]])) acc[keys[i]] = isIndex(keys[i + 1]) ? [] : {};
		acc = acc[keys[i]];
	}
}
function setAllChildrenToValue(obj, value) {
	for (const key in obj) if (isObjectLike(obj[key])) setAllChildrenToValue(obj[key], value);
	else obj[key] = value;
}
function del(object, key) {
	if (Array.isArray(object) && isIndex(key)) {
		object.splice(Number(key), 1);
		return;
	}
	if (isObject(object)) delete object[key];
}
function unset(object, key) {
	if (Array.isArray(object) && isIndex(key)) {
		object[key] = void 0;
		return;
	}
	if (isObject(object)) object[key] = void 0;
}
/**
* Removes a nested property from object
*/
function unsetPath(object, path, destroy) {
	if (isEscapedPath(path)) {
		delete object[cleanupNonNestedPath(path)];
		return;
	}
	const mut = destroy ? del : unset;
	const normalizedPath = normalizePath(path);
	const keys = normalizedPath.split(".").filter(Boolean);
	let acc = object;
	for (let i = 0; i < keys.length; i++) {
		if (i === keys.length - 1) {
			mut(acc, keys[i]);
			break;
		}
		if (!(keys[i] in acc) || isNullOrUndefined(acc[keys[i]])) break;
		acc = acc[keys[i]];
	}
	const pathValues = keys.map((_, idx) => {
		return getFromPath(object, keys.slice(0, idx).join("."));
	});
	for (let i = pathValues.length - 1; i >= 0; i--) {
		if (!isEmptyContainer(pathValues[i])) continue;
		if (i === 0) {
			mut(object, keys[0]);
			continue;
		}
		mut(pathValues[i - 1], keys[i - 1]);
	}
}
const ABSENT_VALUE = Symbol("ABSENT_VALUE");
function isPathSet(object, path) {
	return getFromPath(object, path, ABSENT_VALUE) !== ABSENT_VALUE;
}
function escapePath(path) {
	return isEscapedPath(path) ? path : `[${path}]`;
}
function findLeaf(object, predicate, acc = "") {
	const entries = Object.entries(object);
	const path = acc ? `${acc}.` : "";
	for (const [key, value] of entries) {
		if (isObject(value)) {
			const nested = findLeaf(value, predicate, path);
			if (nested) return `${path}${key}.${nested}`;
		}
		const fullPath = `${path}${key}`;
		if (predicate(value, fullPath)) return fullPath;
	}
}
const ARRAY_INDICES_RE = /\[(\d+)]$/;
/**
* Swaps out array brackets for dots in a path.
*/
function normalizePath(path) {
	const pathArr = path.split(".");
	if (!pathArr.length) return "";
	let fullPath = String(pathArr[0]);
	for (let i = 1; i < pathArr.length; i++) {
		if (ARRAY_INDICES_RE.test(pathArr[i])) {
			fullPath += `.${pathArr[i].replace(ARRAY_INDICES_RE, ".$1")}`;
			continue;
		}
		fullPath += `.${pathArr[i]}`;
	}
	return fullPath;
}
function prefixPath(prefix, path) {
	if (!path) return path;
	prefix = prefix ? `${prefix}.` : "";
	return `${prefix}${path}`;
}

//#endregion
//#region packages/core/src/useForm/formContext.ts
function createFormContext({ id, values, disabled, errors, dirty, submitErrors, schema, touched, snapshots }) {
	function setValue(path, value) {
		setInPath(values, path, cloneDeep(value));
		const oldValue = getFieldOriginalValue(path);
		setDirty(path, !isEqual(oldValue, value));
	}
	function setTouched(pathOrValue, valueOrUndefined) {
		if (typeof pathOrValue === "boolean") {
			for (const key in touched) setInPath(touched, key, pathOrValue, true);
			return;
		}
		setInPath(touched, pathOrValue, valueOrUndefined, true);
	}
	function getValue(path) {
		return getFromPath(values, path);
	}
	function isTouched(path) {
		if (!path) return !!findLeaf(touched, (l) => l === true);
		const value = getFromPath(touched, path);
		if (isObject(value)) return !!findLeaf(value, (v) => !!v);
		return !!value;
	}
	function isDirty(path) {
		if (!path) return !!findLeaf(dirty, (l) => l === true);
		const value = getFromPath(dirty, path);
		if (isObject(value)) return !!findLeaf(value, (v) => !!v);
		return !!value;
	}
	function setDirty(pathOrValue, valueOrUndefined) {
		if (typeof pathOrValue === "boolean") {
			for (const key in dirty) setInPath(dirty, key, pathOrValue, true);
			return;
		}
		setInPath(dirty, pathOrValue, valueOrUndefined, true);
	}
	function isFieldSet(path) {
		return isPathSet(values, path);
	}
	function destroyPath(path) {
		unsetPath(values, path, true);
		unsetPath(touched, path, true);
		unsetPath(disabled, escapePath(path), true);
		unsetPath(errors.value, escapePath(path), true);
	}
	function unsetPath$1(path) {
		unsetPath(values, path, false);
		unsetPath(touched, path, false);
		unsetPath(disabled, escapePath(path), false);
		unsetPath(errors.value, escapePath(path), false);
	}
	function getFieldInitialValue(path) {
		return getFromPath(snapshots.values.initials.value, path);
	}
	function getFieldOriginalValue(path) {
		return getFromPath(snapshots.values.originals.value, path);
	}
	function unsetInitialValue(path) {
		unsetPath(snapshots.values.initials.value, path);
	}
	function setFieldDisabled(path, value) {
		setInPath(disabled, escapePath(path), value);
	}
	function isPathDisabled(path) {
		const value = getLastReachableValue(disabled, path);
		return typeof value === "boolean" ? value : false;
	}
	function getSubmitErrors() {
		return Object.entries(submitErrors.value).map(([key, value]) => ({
			path: key,
			messages: value
		})).filter((e) => e.messages.length > 0);
	}
	function setInitialValues(newValues, opts) {
		if (opts?.behavior === "merge") {
			snapshots.values.initials.value = merge(cloneDeep(snapshots.values.initials.value), cloneDeep(newValues));
			snapshots.values.originals.value = cloneDeep(snapshots.values.initials.value);
			return;
		}
		snapshots.values.initials.value = cloneDeep(newValues);
		snapshots.values.originals.value = cloneDeep(newValues);
	}
	function setInitialValuesPath(path, newValues, opts) {
		if (opts?.behavior === "merge") {
			const currentInitials = getFromPath(snapshots.values.initials.value, path);
			const currentOriginals = getFromPath(snapshots.values.originals.value, path);
			setInPath(snapshots.values.initials.value, path, merge(cloneDeep(currentInitials), cloneDeep(newValues)));
			setInPath(snapshots.values.originals.value, path, merge(cloneDeep(currentOriginals), cloneDeep(newValues)));
			return;
		}
		setInPath(snapshots.values.initials.value, path, cloneDeep(newValues));
		setInPath(snapshots.values.originals.value, path, cloneDeep(newValues));
	}
	function setInitialTouched(newTouched, opts) {
		if (opts?.behavior === "merge") {
			snapshots.touched.initials.value = merge(cloneDeep(snapshots.touched.initials.value), cloneDeep(newTouched));
			snapshots.touched.originals.value = cloneDeep(snapshots.touched.initials.value);
			return;
		}
		snapshots.touched.initials.value = cloneDeep(newTouched);
		snapshots.touched.originals.value = cloneDeep(newTouched);
	}
	/**
	* Set values on the form.
	*/
	function setValues(newValues, opts) {
		if (opts?.behavior !== "merge") Object.keys(values).forEach((key) => {
			delete values[key];
		});
		merge(values, newValues);
	}
	function getErrors(path) {
		return getIssues(path).map((e) => e.messages).flat();
	}
	function getFieldSubmitErrors(path) {
		return [...getFromPath(submitErrors.value, escapePath(path), []) || []];
	}
	function setErrors(pathOrIssues, messageOrUndefined) {
		if (Array.isArray(pathOrIssues)) {
			pathOrIssues.forEach((issue) => {
				setInPath(errors.value, issue.path, issue.messages);
			});
			return;
		}
		setInPath(errors.value, escapePath(pathOrIssues), messageOrUndefined ? normalizeArrayable(messageOrUndefined) : []);
	}
	function setFieldSubmitErrors(path, message) {
		setInPath(submitErrors.value, escapePath(path), message ? normalizeArrayable(message) : []);
	}
	function updateTouched(newTouched, opts) {
		if (opts?.behavior === "merge") {
			merge(touched, newTouched);
			return;
		}
		Object.keys(touched).forEach((key) => {
			delete touched[key];
		});
		merge(touched, newTouched);
	}
	function updateTouchedPath(path, newTouched, opts) {
		const pathExists = isPathSet(touched, path);
		if (opts?.behavior === "merge" || !pathExists) {
			const newFullTouched = {};
			setInPath(newFullTouched, path, newTouched);
			merge(touched, newFullTouched);
			return;
		}
		setInPath(touched, path, newTouched);
	}
	function setInitialTouchedPath(path, newTouched, opts) {
		const pathExists = isPathSet(snapshots.touched.initials.value, path);
		if (opts?.behavior === "merge" || !pathExists) {
			const newFullTouched = {};
			setInPath(newFullTouched, path, newTouched);
			merge(snapshots.touched.initials.value, newFullTouched);
			merge(snapshots.touched.originals.value, newFullTouched);
			return;
		}
		setInPath(snapshots.touched.initials.value, path, newTouched);
		setInPath(snapshots.touched.originals.value, path, newTouched);
	}
	function updateDirty(newDirty, opts) {
		if (opts?.behavior === "merge") {
			merge(dirty, newDirty);
			return;
		}
		Object.keys(dirty).forEach((key) => {
			delete dirty[key];
		});
		merge(dirty, newDirty);
	}
	function updateDirtyPath(path, newDirty, opts) {
		const pathExists = isPathSet(dirty, path);
		if (opts?.behavior === "merge" || !pathExists) {
			const newFullDirty = {};
			setInPath(newFullDirty, path, newDirty);
			merge(dirty, newFullDirty);
			return;
		}
		setInPath(dirty, path, newDirty);
	}
	function clearErrors(path) {
		if (!path) {
			errors.value = {};
			return;
		}
		Object.keys(errors.value).forEach((key) => {
			if (key === path || key.startsWith(path + ".")) delete errors.value[key];
		});
	}
	function clearSubmitErrors(path) {
		if (!path) {
			submitErrors.value = {};
			return;
		}
		Object.keys(submitErrors.value).forEach((key) => {
			if (key === path || key.startsWith(path + ".")) delete submitErrors.value[key];
		});
	}
	function revertValues(path) {
		if (!path) {
			setValues(cloneDeep(snapshots.values.originals.value), { behavior: "replace" });
			return;
		}
		const originalValue = getFieldOriginalValue(path);
		setValue(path, originalValue);
	}
	function revertTouched(path) {
		if (!path) {
			updateTouched(cloneDeep(snapshots.touched.originals.value), { behavior: "replace" });
			return;
		}
		updateTouchedPath(path, getFromPath(snapshots.touched.originals.value, path), { behavior: "replace" });
	}
	function revertDirty(path) {
		if (!path) {
			updateDirty(cloneDeep(snapshots.dirty.originals.value), { behavior: "replace" });
			return;
		}
		updateDirtyPath(path, getFromPath(snapshots.dirty.originals.value, path), { behavior: "replace" });
	}
	function getValidationMode() {
		return schema ? "schema" : "aggregate";
	}
	function getIssues(path) {
		const allErrors = Object.entries(errors.value).map(([key, value]) => ({
			path: key,
			messages: value
		})).filter((e) => e.messages.length > 0);
		if (!path) return allErrors;
		const pathPrefixErrors = allErrors.filter((e) => {
			if (e.path === path) return true;
			return e.path.startsWith(path + ".");
		});
		if (pathPrefixErrors.length > 0) return pathPrefixErrors;
		return [];
	}
	return {
		id,
		getValues: () => cloneDeep(values),
		setValue,
		getFieldInitialValue,
		setTouched,
		getValue,
		isTouched,
		isDirty,
		setDirty,
		isFieldSet,
		destroyPath,
		unsetPath: unsetPath$1,
		unsetInitialValue,
		setValues,
		revertValues,
		revertTouched,
		setInitialValues,
		setInitialValuesPath,
		revertDirty,
		setInitialTouched,
		setInitialTouchedPath,
		updateTouchedPath,
		getFieldOriginalValue,
		setFieldDisabled,
		setErrors,
		setFieldSubmitErrors,
		getErrors,
		getFieldSubmitErrors,
		getSubmitErrors,
		clearErrors,
		clearSubmitErrors,
		getValidationMode,
		isPathDisabled,
		getIssues
	};
}

//#endregion
//#region packages/core/src/useForm/useFormTransactions.ts
/**
* Transaction kinds, we use numbers for faster comparison and easier sorting.
*/
const TransactionKind = {
	INIT_PATH: 3,
	SET_PATH: 2,
	UNSET_PATH: 1,
	DESTROY_PATH: 0
};
function useFormTransactions(form) {
	const transactions = new Set([]);
	let tick;
	function transaction(tr) {
		const commit = tr(form, TransactionKind);
		if (commit) transactions.add(commit);
		processTransactions();
	}
	async function processTransactions() {
		const upcomingTick = nextTick();
		tick = upcomingTick;
		await upcomingTick;
		if (tick !== upcomingTick || !transactions.size) return;
		/**
		* Unset transactions should be processed first to ensure that any fields that reclaim the same path maintain their value.
		*/
		const trs = cleanTransactions(transactions);
		for (const tr of trs) {
			if (tr.kind === TransactionKind.SET_PATH) {
				form.setValue(tr.path, tr.value);
				form.setTouched(tr.path, tr.touched);
				form.setDirty(tr.path, tr.dirty);
				form.setFieldDisabled(tr.path, tr.disabled);
				form.setErrors(tr.path, tr.errors);
				continue;
			}
			if (tr.kind === TransactionKind.DESTROY_PATH) {
				form.destroyPath(tr.path);
				continue;
			}
			if (tr.kind === TransactionKind.UNSET_PATH) {
				form.unsetPath(tr.path);
				continue;
			}
			if (tr.kind === TransactionKind.INIT_PATH) {
				const formInit = form.getFieldInitialValue(tr.path);
				form.setValue(tr.path, tr.value ?? formInit);
				form.setFieldDisabled(tr.path, tr.disabled);
				form.setTouched(tr.path, tr.touched);
				form.setDirty(tr.path, tr.dirty);
				form.unsetInitialValue(tr.path);
				form.setErrors(tr.path, tr.errors);
				continue;
			}
		}
		transactions.clear();
	}
	const ctx = { transaction };
	return ctx;
}
function cleanTransactions(transactions) {
	const SET_OPTS = [TransactionKind.SET_PATH, TransactionKind.INIT_PATH];
	const trs = Array.from(transactions).filter((t, _, ops) => {
		if (t.kind === TransactionKind.UNSET_PATH || t.kind === TransactionKind.DESTROY_PATH) return !ops.some((op) => SET_OPTS.includes(op.kind) && op.path === t.path);
		return true;
	}).map((t) => {
		if (t.kind === TransactionKind.UNSET_PATH) return {
			kind: TransactionKind.DESTROY_PATH,
			path: t.path
		};
		return t;
	}).sort((a, b) => {
		return a.kind - b.kind;
	});
	return trs;
}

//#endregion
//#region packages/core/src/constants/index.ts
const FieldTypePrefixes = {
	NumberField: "nf",
	TextField: "tf",
	Switch: "sw",
	Checkbox: "cb",
	CheckboxGroup: "cbg",
	RadioButton: "rb",
	RadioButtonGroup: "rbg",
	Slider: "sl",
	SearchField: "sf",
	FormGroup: "fg",
	Select: "se",
	Option: "opt",
	OptionGroup: "og",
	Form: "f",
	SliderThumb: "st",
	FormRepeater: "fr",
	CustomField: "cf",
	ComboBox: "cbx",
	ListBox: "lb",
	DateTimeField: "dtf",
	DateTimeSegment: "dts",
	Calendar: "cal",
	OTPField: "otp",
	OTPSlot: "otps",
	FileField: "ff"
};
const NOOP = () => {};
const SCHEMA_BATCH_MS = 10;
const version = "0.10.7";
const FormIdAttr = "data-fw-form-id";

//#endregion
//#region packages/core/src/utils/events.ts
function createEventDispatcher(eventName) {
	const evtName = `formwerk:${useUniqId(eventName)}`;
	const controller = new AbortController();
	function dispatch(payload) {
		if (isSSR) return Promise.resolve();
		document.dispatchEvent(new CustomEvent(evtName, { detail: payload }));
		return nextTick();
	}
	function addListener(handler, vm) {
		if (isSSR) return NOOP;
		const handlerWrapper = (e) => {
			handler(e.detail);
		};
		document.addEventListener(evtName, handlerWrapper, { signal: controller.signal });
		const removeListener = () => document.removeEventListener(evtName, handlerWrapper);
		onBeforeUnmount(removeListener, vm);
		return removeListener;
	}
	tryOnScopeDispose(() => {
		controller.abort();
	});
	return [dispatch, addListener];
}
function onlyMainMouseButton(cb) {
	return (event) => {
		if (event.button === 0) cb();
	};
}
function blockEvent(evt) {
	evt.preventDefault();
	evt.stopPropagation();
	evt.stopImmediatePropagation();
}

//#endregion
//#region packages/core/src/validation/useValidationProvider.ts
function useValidationProvider({ schema, getValues, type, getPath }) {
	const [dispatchValidate, onValidationDispatch] = createEventDispatcher("validate");
	const [dispatchValidateDone, onValidationDone] = createEventDispatcher("validate-done");
	/**
	* Validates but tries to not mutate anything if possible.
	*/
	async function validate() {
		const validationQueue = [];
		const enqueue = (promise) => validationQueue.push(promise);
		await dispatchValidate(enqueue);
		const results = await Promise.all(validationQueue);
		const fieldIssues = results.flatMap((r) => r.errors.map((e) => ({
			...e,
			path: r.path
		}))).filter((e) => e.messages.length);
		if (!schema) return createValidationResult({
			isValid: !fieldIssues.length,
			errors: fieldIssues,
			output: stitchOutput(getValues(), results)
		});
		const result = await schema["~standard"]["validate"](getValues());
		let errors = combineStandardIssues(result.issues || []);
		const prefix = getPath?.();
		if (prefix) errors = errors.map((e) => {
			return {
				messages: e.messages,
				path: prefixPath(prefix, e.path) || ""
			};
		});
		const allErrors = combineIssues([...errors, ...fieldIssues]);
		const output = "value" in result ? result.value : void 0;
		dispatchValidateDone();
		return createValidationResult({
			isValid: !allErrors.length,
			errors: allErrors,
			output: stitchOutput(output ?? getValues(), results)
		});
	}
	function defineValidationRequest(mutator) {
		return withLatestCall(batchAsync(validate, SCHEMA_BATCH_MS), (result) => {
			mutator(result);
			return result;
		});
	}
	function createValidationResult(result) {
		const base = {
			output: result.output,
			errors: result.errors,
			isValid: result.isValid
		};
		if (type === "FORM") return {
			type,
			mode: schema ? "schema" : "aggregate",
			...base
		};
		return {
			type: "GROUP",
			path: getPath?.() || "",
			mode: schema ? "schema" : "aggregate",
			...base
		};
	}
	function stitchOutput(base, results) {
		const all = cloneDeep(base);
		const sorted = [...results].sort((a, b) => {
			if (a.type === b.type) return 0;
			return a.type === "FIELD" ? 1 : -1;
		});
		for (const result of sorted) {
			const hasOutput = "output" in result;
			if (!result.path || !hasOutput) continue;
			setInPath(all, result.path, result.output);
		}
		return all;
	}
	return {
		validate,
		onValidationDispatch,
		onValidationDone,
		defineValidationRequest,
		dispatchValidateDone
	};
}

//#endregion
//#region packages/core/src/utils/formData.ts
function appendToFormData(jsonObject, formData, parentKey = "") {
	for (const key in jsonObject) {
		if (!Object.prototype.hasOwnProperty.call(jsonObject, key)) continue;
		const value = jsonObject[key];
		const newKey = parentKey ? `${parentKey}[${key}]` : key;
		if (isFileOrBlob(value)) {
			formData.append(newKey, value, isFile(value) ? value.name : void 0);
			continue;
		}
		if (isNullOrUndefined(value)) {
			formData.append(newKey, "");
			continue;
		}
		if (Array.isArray(value)) {
			value.forEach((item, index) => {
				appendToFormData({ [`${newKey}[${index}]`]: item }, formData);
			});
			continue;
		}
		if (isObject(value) && !(value instanceof File)) {
			appendToFormData(value, formData, newKey);
			continue;
		}
		formData.append(newKey, String(value));
	}
	return formData;
}
function clearFormData(formData) {
	formData.forEach((_, key) => {
		formData.delete(key);
	});
	return formData;
}

//#endregion
//#region packages/core/src/useForm/useFormActions.ts
function useFormActions(form, { disabled, schema, scrollToInvalidFieldOnSubmit }) {
	const isSubmitting = shallowRef(false);
	const submitAttemptsCount = shallowRef(0);
	const isSubmitAttempted = shallowRef(false);
	const wasSubmitted = shallowRef(false);
	const [dispatchSubmit, onSubmitAttempt] = createEventDispatcher("submit");
	const { validate: _validate, onValidationDispatch, defineValidationRequest, onValidationDone, dispatchValidateDone } = useValidationProvider({
		schema,
		getValues: () => form.getValues(),
		type: "FORM"
	});
	const requestValidation = defineValidationRequest(updateValidationStateFromResult);
	function handleSubmit(onSuccess) {
		return async function onSubmit(e) {
			e?.preventDefault();
			isSubmitting.value = true;
			isSubmitAttempted.value = true;
			submitAttemptsCount.value += 1;
			dispatchSubmit();
			const { isValid, output, errors } = await validate();
			updateSubmitValidationStateFromResult(errors);
			if (!isValid) {
				isSubmitting.value = false;
				scrollToFirstInvalidField(form.id, scrollToInvalidFieldOnSubmit);
				return;
			}
			const disabledPaths = Object.entries(disabled).filter(([, v]) => !!v).map(([k]) => k).sort((a, b) => b.localeCompare(a));
			for (const path of disabledPaths) unsetPath(output, path, true);
			const result = await onSuccess(withConsumers(output), {
				event: e,
				form: e?.target
			});
			isSubmitting.value = false;
			wasSubmitted.value = true;
			return result;
		};
	}
	function handleReset(afterReset) {
		return async function resetHandler(e) {
			e?.preventDefault();
			await reset();
			await afterReset?.();
		};
	}
	function updateSubmitValidationStateFromResult(errors) {
		form.clearSubmitErrors();
		applySubmitErrors(errors);
	}
	function updateValidationStateFromResult(result) {
		form.clearErrors();
		applyErrors(result.errors);
		dispatchValidateDone();
		return result;
	}
	async function validate() {
		const result = await _validate();
		updateValidationStateFromResult(result);
		return result;
	}
	function applyErrors(errors) {
		for (const entry of errors) form.setErrors(entry.path, entry.messages);
	}
	function applySubmitErrors(errors) {
		for (const entry of errors) form.setFieldSubmitErrors(entry.path, entry.messages);
	}
	async function reset(pathOrStateOrUndefined, stateOrOptsOrUndefined, optsOrUndefined) {
		/**
		* CASE 1: reset()
		* Reset the form to its initial state.
		*/
		if (pathOrStateOrUndefined === void 0) {
			wasSubmitted.value = false;
			form.revertValues();
			form.revertTouched();
			form.revertDirty();
			submitAttemptsCount.value = 0;
			isSubmitAttempted.value = false;
			form.clearErrors();
			form.clearSubmitErrors();
			await validate();
			return Promise.resolve();
		}
		/**
		* CASE 2: reset(state, opts?)
		* Reset the form to a specific state.
		*/
		if (typeof pathOrStateOrUndefined !== "string") {
			const state = pathOrStateOrUndefined;
			const opts = stateOrOptsOrUndefined;
			if (state.value) form.setInitialValues(state.value);
			if (state.touched) form.setInitialTouched(state.touched, opts);
			wasSubmitted.value = false;
			submitAttemptsCount.value = 0;
			isSubmitAttempted.value = false;
			form.revertValues();
			form.revertTouched();
			form.revertDirty();
			if (state.revalidate ?? true) {
				await validate();
				return;
			}
			form.clearErrors();
			form.clearSubmitErrors();
			return Promise.resolve();
		}
		/**
		* Case 3 and 4.
		*
		* Extract the path from the first argument.
		*/
		const path = pathOrStateOrUndefined;
		/**
		* CASE 3: reset(path)
		* Reset a specific part of the form.
		*/
		if (stateOrOptsOrUndefined === void 0) {
			form.revertValues(path);
			form.revertTouched(path);
			form.revertDirty(path);
			form.clearErrors(path);
			return Promise.resolve();
		}
		/**
		* CASE 4: reset(path, state, opts?)
		* Reset a specific part of the form to a specific state.
		*/
		if (typeof stateOrOptsOrUndefined === "object") {
			const state = stateOrOptsOrUndefined;
			const opts = optsOrUndefined;
			if (state.value) form.setInitialValuesPath(path, state.value, opts);
			if (state.touched) form.setInitialTouchedPath(path, state.touched, opts);
			form.revertValues(path);
			form.revertTouched(path);
			form.revertDirty(path);
			form.clearErrors(path);
			if (state.revalidate ?? true) {
				await validate();
				return;
			}
			form.clearErrors(path);
			return Promise.resolve();
		}
	}
	const actions = {
		handleSubmit,
		handleReset,
		reset,
		validate
	};
	return {
		actions,
		requestValidation,
		onSubmitAttempt,
		onValidationDispatch,
		onValidationDone,
		isSubmitting,
		submitAttemptsCount,
		wasSubmitted,
		isSubmitAttempted
	};
}
function withConsumers(data) {
	const toObject = () => data;
	const toFormData = () => {
		const formData = new FormData();
		appendToFormData(data, formData);
		return formData;
	};
	function toJSON() {
		return JSON.parse(JSON.stringify(toObject()));
	}
	return {
		toObject,
		toFormData,
		toJSON
	};
}
function scrollToFirstInvalidField(formId, options) {
	if (!options) return;
	const scrollOpts = typeof options === "object" ? options : {
		behavior: "smooth",
		block: "center",
		inline: "start"
	};
	const invalidField = document.querySelector(`[aria-invalid="true"][aria-errormessage][${FormIdAttr}="${formId}"]`);
	if (invalidField) invalidField.scrollIntoView(scrollOpts);
}

//#endregion
//#region packages/core/src/useForm/formSnapshot.ts
function useFormSnapshots(provider, opts) {
	const initials = shallowRef({});
	const originals = shallowRef({});
	const provided = toValue(provider);
	if (isPromise(provided)) provided.then((resolved) => {
		const inits = resolved;
		initials.value = cloneDeep(inits || {});
		originals.value = cloneDeep(inits || {});
		opts?.onAsyncInit?.(cloneDeep(inits));
	});
	else {
		const inits = provided;
		initials.value = cloneDeep(inits || {});
		originals.value = cloneDeep(inits || {});
	}
	return {
		initials,
		originals
	};
}

//#endregion
//#region packages/core/src/i18n/getSiteLocale.ts
/**
* Tries to guess the site locale based on a few strategies.
*/
function getSiteLocale() {
	if (isSSR) return "en-US";
	const lang = document.documentElement.lang;
	return lang || "en-US";
}

//#endregion
//#region packages/core/src/config.ts
const currentConfig = shallowRef({
	locale: getSiteLocale(),
	detectDirection: true,
	disableHtmlValidation: false
});
const evaluatedConfig = computed(() => {
	const config = currentConfig.value;
	return Object.fromEntries(Object.entries(config).map(([key, value]) => {
		if (key === "validation") return [key, value];
		return [key, toValue(value)];
	}));
});
function configure(config) {
	currentConfig.value = merge({ ...currentConfig.value }, config);
}
function getConfig() {
	return evaluatedConfig.value;
}

//#endregion
//#region packages/core/src/helpers/createDisabledContext/createDisabledContext.ts
const DisabledContextKey = Symbol("disabledContextKey");
/**
* Create a disabled context.
* @param isDisabled - The disabled state.
* @param terminate - Whether to terminate the context and not provide it further.
* @returns The disabled state.
*/
function createDisabledContext(isDisabled) {
	const parentContext = inject(DisabledContextKey, null);
	const context = { isDisabled: computed(() => parentContext?.isDisabled.value || toValue(isDisabled) || false) };
	provide(DisabledContextKey, context);
	return context.isDisabled;
}

//#endregion
//#region packages/core/src/useForm/useForm.ts
const FormKey = Symbol("Formwerk FormKey");
const FormContextKey = Symbol("Formwerk FormContextKey");
function useForm(props) {
	const schemaProps = props;
	const touchedSnapshot = useFormSnapshots(props?.initialTouched);
	const dirtySnapshot = useFormSnapshots(props?.initialDirty);
	const valuesSnapshot = useFormSnapshots(props?.initialValues, {
		onAsyncInit,
		schema: schemaProps?.schema
	});
	const id = props?.id || useUniqId(FieldTypePrefixes.Form);
	const isDisabled = createDisabledContext(props?.disabled);
	const isHtmlValidationDisabled = () => props?.disableHtmlValidation ?? getConfig().disableHtmlValidation;
	const values = reactive(cloneDeep(valuesSnapshot.originals.value));
	const touched = reactive(cloneDeep(touchedSnapshot.originals.value));
	const dirty = reactive(cloneDeep(dirtySnapshot.originals.value));
	const disabled = reactive({});
	const errors = ref({});
	const submitErrors = ref({});
	const ctx = createFormContext({
		id,
		values,
		touched,
		disabled,
		dirty,
		schema: schemaProps?.schema,
		errors,
		submitErrors,
		snapshots: {
			values: valuesSnapshot,
			touched: touchedSnapshot,
			dirty: dirtySnapshot
		}
	});
	function isValid(path) {
		const pathErrors = ctx.getErrors(path);
		return pathErrors.length === 0;
	}
	function onAsyncInit(v) {
		ctx.setValues(v, { behavior: "merge" });
	}
	const transactionsManager = useFormTransactions(ctx);
	const { actions, isSubmitting, submitAttemptsCount, wasSubmitted, isSubmitAttempted,...privateActions } = useFormActions(ctx, {
		disabled,
		schema: schemaProps?.schema,
		scrollToInvalidFieldOnSubmit: props?.scrollToInvalidFieldOnSubmit ?? true
	});
	function getError(path) {
		return ctx.isPathDisabled(path) ? void 0 : ctx.getErrors(path)[0];
	}
	function getSubmitError(path) {
		return ctx.isPathDisabled(path) ? void 0 : ctx.getFieldSubmitErrors(path)[0];
	}
	function displayError(path) {
		return ctx.isTouched(path) && !ctx.isPathDisabled(path) ? getError(path) : void 0;
	}
	provide(FormKey, {
		...ctx,
		...transactionsManager,
		...privateActions,
		isHtmlValidationDisabled
	});
	if (ctx.getValidationMode() === "schema") onMounted(privateActions.requestValidation);
	function onFormdata(e) {
		const form$1 = e.target;
		clearFormData(e.formData);
		appendToFormData(form$1.__formOut || values, e.formData);
	}
	const onSubmit = actions.handleSubmit((output, { form: form$1 }) => {
		if (form$1) {
			form$1.__formOut = output.toObject();
			form$1.submit();
		}
	});
	const formProps = {
		id,
		novalidate: true,
		onSubmit,
		onFormdata
	};
	const baseReturns = {
		values: readonly(values),
		context: ctx,
		isSubmitting,
		isValid,
		isDisabled,
		submitAttemptsCount,
		wasSubmitted,
		isSubmitAttempted,
		isDirty: ctx.isDirty,
		setValue: ctx.setValue,
		getValue: ctx.getValue,
		isTouched: ctx.isTouched,
		setTouched: ctx.setTouched,
		setErrors: ctx.setErrors,
		setValues: ctx.setValues,
		getError,
		displayError,
		getErrors: ctx.getErrors,
		getIssues: ctx.getIssues,
		getSubmitError,
		getSubmitErrors: ctx.getSubmitErrors,
		formProps
	};
	const form = {
		...baseReturns,
		...actions
	};
	if (process.env.NODE_ENV !== "production") registerForm(form);
	provide(FormContextKey, form);
	return form;
}
function useFormContext() {
	const ctx = inject(FormContextKey, null);
	if (process.env.NODE_ENV !== "production") {
		if (!ctx) warn("useFormContext must be used within a Formwerk form or one of its descendants.");
	}
	return ctx;
}

//#endregion
//#region packages/core/src/a11y/useLabel.ts
function useLabel(props) {
	const labelRef = ref();
	const refCapture = createRefCapture(labelRef);
	const labelProps = computed(() => {
		return {
			ref: refCapture,
			id: `${toValue(props.for)}-l`,
			for: isLabelElement(labelRef.value) && isInputElement(toValue(props.targetRef)) ? toValue(props.for) : void 0,
			onClick: props.handleClick || void 0
		};
	});
	const labelledByProps = computed(() => {
		if (isLabelElement(labelRef.value) && isInputElement(toValue(props.targetRef))) return {};
		if (labelRef.value && toValue(props.label) && toValue(props.targetRef)) return { "aria-labelledby": toValue(props.label) && labelRef.value ? labelProps.value.id : void 0 };
		return { "aria-label": toValue(props.label) || void 0 };
	});
	return {
		labelProps,
		labelledByProps
	};
}

//#endregion
//#region packages/core/src/helpers/usePathPrefixer/index.ts
const FormPathPrefixerKey = Symbol("FormPathPrefixerKey");
function createPathPrefixer(prefix) {
	const prefixer = { prefixPath: prefix };
	provide(FormPathPrefixerKey, prefixer);
	return prefixer;
}
function usePathPrefixer() {
	return inject(FormPathPrefixerKey, null);
}

//#endregion
//#region packages/core/src/useFormGroup/useFormGroup.ts
const FormGroupKey = Symbol("FormGroup");
function useFormGroup(_props, elementRef) {
	const id = useUniqId(FieldTypePrefixes.FormGroup);
	const props = normalizeProps(_props, ["schema"]);
	const pathPrefixer = usePathPrefixer();
	const getPath = () => {
		const path = toValue(_props.name);
		const prefixPath$2 = pathPrefixer ? pathPrefixer.prefixPath(path) : path;
		if (!prefixPath$2) return path;
		return prefixPath$2;
	};
	const groupEl = elementRef || shallowRef();
	const form = inject(FormKey, null);
	const parentGroup = inject(FormGroupKey, null);
	const isDisabled = createDisabledContext(props.disabled);
	const isHtmlValidationDisabled = () => toValue(props.disableHtmlValidation) ?? form?.isHtmlValidationDisabled() ?? getConfig().disableHtmlValidation;
	const { validate, onValidationDispatch, defineValidationRequest, onValidationDone, dispatchValidateDone } = useValidationProvider({
		getValues: () => getValue(),
		getPath,
		schema: props.schema,
		type: "GROUP"
	});
	const requestValidation = defineValidationRequest(async (res) => {
		form?.clearErrors(toValue(props.name));
		for (const entry of res.errors) form?.setErrors(entry.path, entry.messages);
		const parent = parentGroup || form;
		if (parent) await parent.requestValidation();
		dispatchValidateDone();
	});
	if (!form) warn("Form groups must have a parent form. Please make sure to call `useForm` at a parent component.");
	const { labelProps, labelledByProps } = useLabel({
		for: id,
		label: props.label,
		targetRef: groupEl
	});
	const groupProps = computed(() => {
		const isFieldSet = groupEl.value?.tagName === "FIELDSET";
		return withRefCapture({
			id,
			...isFieldSet ? {} : labelledByProps.value,
			role: isFieldSet ? void 0 : "group"
		}, groupEl, elementRef);
	});
	function getErrors() {
		return form?.getErrors(getPath()) ?? [];
	}
	function getValue(path) {
		if (!path) return form?.getValue(getPath()) ?? {};
		return form?.getValue(prefixPath$1(path) || "");
	}
	const isValid = computed(() => getErrors().length === 0);
	const isTouched = computed(() => form?.isTouched(getPath()) ?? false);
	const isDirty = computed(() => form?.isDirty(getPath()) ?? false);
	function getError(path) {
		return form?.getErrors(prefixPath$1(path) ?? "")?.[0];
	}
	function displayError(name) {
		const msg = getError(name);
		const path = prefixPath$1(name) ?? "";
		return form?.isTouched(path) ? msg : void 0;
	}
	function prefixPath$1(path) {
		return prefixPath(getPath(), path);
	}
	createPathPrefixer(prefixPath$1);
	const ctx = {
		onValidationDispatch,
		onValidationDone,
		requestValidation,
		getValidationMode: () => props.schema ? "schema" : "aggregate",
		isHtmlValidationDisabled
	};
	form?.onValidationDispatch((enqueue) => {
		enqueue(validate().then((result) => {
			return {
				...result,
				errors: result.errors
			};
		}));
	});
	form?.onValidationDone(dispatchValidateDone);
	provide(FormGroupKey, ctx);
	return {
		groupEl,
		labelProps,
		groupProps,
		isDirty,
		isValid,
		isTouched,
		isDisabled,
		displayError,
		validate,
		getErrors,
		getValue,
		getValues: () => getValue(),
		getError
	};
}

//#endregion
//#region packages/core/src/i18n/getDirection.ts
function getDirection(locale) {
	if (!getConfig().detectDirection) return "ltr";
	try {
		if ("textInfo" in locale) return locale.textInfo.direction || "ltr";
		if ("getTextInfo" in locale && isCallable(locale.getTextInfo)) return locale.getTextInfo().direction || "ltr";
		throw new Error(`Cannot determine direction for locale ${locale}`);
	} catch {
		warn(`Cannot determine direction for locale ${locale}, defaulting to LTR`);
		return "ltr";
	}
}

//#endregion
//#region packages/core/src/i18n/getUserLocale.ts
function getUserLocale() {
	const locale = new Intl.DateTimeFormat().resolvedOptions().locale;
	return locale;
}

//#endregion
//#region packages/core/src/i18n/getWeekInfo.ts
function getWeekInfo(locale) {
	const fallbackInfo = {
		firstDay: 7,
		weekend: [6, 7]
	};
	try {
		if ("weekInfo" in locale) return locale.weekInfo || fallbackInfo;
		if ("getWeekInfo" in locale && isCallable(locale.getWeekInfo)) return locale.getWeekInfo() || fallbackInfo;
		throw new Error(`Cannot determine week info for locale ${locale}`);
	} catch {
		warn(`Cannot determine week info for locale ${locale}`);
		return fallbackInfo;
	}
}

//#endregion
//#region packages/core/src/i18n/getTimezone.ts
function getTimeZone(locale) {
	return new Intl.DateTimeFormat(locale).resolvedOptions().timeZone;
}

//#endregion
//#region packages/core/src/i18n/useLocale.ts
/**
* Composable that resolves the currently configured locale and direction.
*/
function useLocale(localeCode, extensions = {}) {
	const localeString = computed(() => {
		let code = toValue(localeCode) || getConfig().locale;
		const calExt = toValue(extensions.calendar);
		const numExt = toValue(extensions.number);
		if (!code.includes("-u-") && (numExt || calExt)) code += "-u-";
		if (!code.includes("-nu-") && numExt) code += `-nu-${numExt}`;
		if (!code.includes("-ca-") && calExt?.identifier) code += `-ca-${calExt.identifier}`;
		code = code.replaceAll("--", "-");
		return code;
	});
	const localeInstance = computed(() => new Intl.Locale(localeString.value));
	const direction = computed(() => getDirection(localeInstance.value));
	const weekInfo = computed(() => getWeekInfo(localeInstance.value));
	const calendar = computed(() => toValue(extensions.calendar) ?? new GregorianCalendar());
	const timeZone = computed(() => toValue(extensions.timeZone) ?? getTimeZone(localeInstance.value));
	const locale = computed(() => localeInstance.value.toString());
	return {
		locale,
		direction,
		weekInfo,
		calendar,
		timeZone
	};
}

//#endregion
//#region packages/core/src/i18n/useNumberParser.ts
/**
* Stuff that are considered "literals" that's not part of the number itself and should be stripped out when parsing/validating.
*/
const LITERAL_PART_TYPES = {
	percentSign: true,
	currency: true,
	literal: true
};
/**
* Numbering systems that are supported by Intl.NumberFormat, user input may not match their own locale/site accepted locale.
*/
const NUMBERING_SYSTEMS = [
	"latn",
	"arab",
	"hanidec"
];
/**
* Needed for cleaning up the currency accounting sign from the number (e.g: `($ 123)`).
*/
const CURRENCY_SIGN_RE = /^.*\(.*\).*$/;
/**
* Zero widths and RTL and LTR markers are produced sometimes with Intl.NumberFormat, we need to remove them to get as clean as a number as possible.
*/
const NON_PRINTABLE_RE = /\p{C}/gu;
const SPACES_RE = /[\p{White_Space}]/gu;
const numberParserCache = new Map();
function getParser(locale, options) {
	const cacheKey = locale + JSON.stringify(options);
	let parser = numberParserCache.get(cacheKey);
	if (!parser) {
		parser = defineNumberParser(locale, options);
		numberParserCache.set(cacheKey, parser);
	}
	return parser;
}
function defineNumberParser(locale, options) {
	const formatter = new Intl.NumberFormat(locale, options);
	const parts = formatter.formatToParts(-12345.6789);
	const positiveParts = formatter.formatToParts(1);
	const decimal = parts.find((part) => part.type === "decimal")?.value || ".";
	const group = parts.find((part) => part.type === "group")?.value || ",";
	const literals = new Set(parts.filter((part) => LITERAL_PART_TYPES[part.type]).map((p) => p.value));
	const minusSign = parts.find((part) => part.type === "minusSign")?.value;
	const plusSign = positiveParts.find((part) => part.type === "plusSign")?.value;
	const numerals = [...new Intl.NumberFormat(toValue(locale), { useGrouping: false }).format(9876543210)].reverse();
	const resolvedOptions = formatter.resolvedOptions();
	const numeralMap = new Map(numerals.map((d, i) => [d, i]));
	const numeralRE = new RegExp(`[${numerals.join("")}]`, "g");
	const literalsRE = literals.size > 0 ? new RegExp(`${[...literals].map(escapeStringRegexp).join("|")}`, "g") : void 0;
	const symbols = {
		decimal,
		group,
		literalsRE,
		numeralRE,
		minusSign,
		plusSign,
		resolveNumber: (number) => {
			return String(numeralMap.get(number)) || "";
		}
	};
	/**
	* Cleans up the value from some quirks around some locales to get as much as a clean number as possible.
	*/
	function sanitize(value) {
		let sanitized = value;
		if (symbols.literalsRE) sanitized = sanitized.replace(symbols.literalsRE, "");
		if (symbols.minusSign) sanitized = sanitized.replace("-", symbols.minusSign);
		if (resolvedOptions.numberingSystem === "arab") {
			if (symbols.group) sanitized = sanitized.replaceAll(String.fromCharCode(1548), symbols.group);
			if (symbols.decimal) sanitized = sanitized.replace(String.fromCharCode(44), symbols.decimal);
		}
		return sanitized.replace(SPACES_RE, "");
	}
	function parse(value) {
		let sanitized = sanitize(value);
		if (symbols.group) sanitized = sanitized.replaceAll(symbols.group, "");
		if (symbols.decimal) sanitized = sanitized.replace(symbols.decimal, ".");
		sanitized = sanitized.replace(symbols.numeralRE, symbols.resolveNumber);
		const parsed = Number(sanitized);
		if (Number.isNaN(parsed)) return NaN;
		if (options.currencySign === "accounting" && CURRENCY_SIGN_RE.test(value)) return -parsed;
		if (options.style === "percent") return parsed / 100;
		return parsed;
	}
	function format(value) {
		return formatter.format(value).replace(NON_PRINTABLE_RE, "").trim();
	}
	function isValidNumberPart(value) {
		let sanitized = sanitize(value);
		if (symbols.group) sanitized = sanitized.replaceAll(symbols.group, "");
		if (symbols.decimal) sanitized = sanitized.replace(symbols.decimal, "");
		if (symbols.minusSign) sanitized = sanitized.replace(symbols.minusSign, "");
		if (symbols.plusSign) sanitized = sanitized.replace(symbols.plusSign, "");
		sanitized = sanitized.replace(symbols.numeralRE, "");
		return sanitized.length === 0;
	}
	return {
		formatter,
		locale,
		options: resolvedOptions,
		symbols,
		parse,
		format,
		isValidNumberPart
	};
}
function useNumberParser(locale, opts) {
	const resolvedLocale = getUserLocale();
	let lastResolvedParser = null;
	function findParser(value) {
		const defaultLocale = toValue(locale) ?? toValue(resolvedLocale);
		const defaultOpts = toValue(opts) || {};
		const defaultParser = getParser(defaultLocale, defaultOpts);
		if (defaultParser.isValidNumberPart(value) || defaultLocale.includes("-nu-")) return defaultParser;
		for (const numberingSystem of NUMBERING_SYSTEMS) {
			if (defaultParser.options.numberingSystem === numberingSystem) continue;
			const tryLocale = defaultLocale.includes("-u-") ? `${defaultLocale}-nu-${numberingSystem}` : `${defaultLocale}-u-nu-${numberingSystem}`;
			const parser = getParser(tryLocale, defaultOpts);
			if (parser.isValidNumberPart(value)) return parser;
		}
		return defaultParser;
	}
	function resolveParser(value) {
		if (lastResolvedParser && lastResolvedParser.isValidNumberPart(value)) return lastResolvedParser;
		const parser = findParser(value);
		lastResolvedParser = parser;
		return parser;
	}
	function getNumberingSystem(value) {
		return resolveParser(value).options.numberingSystem;
	}
	function parse(value) {
		return resolveParser(value).parse(value);
	}
	function isValidNumberPart(value) {
		return resolveParser(value).isValidNumberPart(value);
	}
	function getDefaultParser() {
		return getParser(toValue(locale) ?? toValue(resolvedLocale), toValue(opts) || {});
	}
	function format(value) {
		return (lastResolvedParser ?? getDefaultParser()).format(value);
	}
	watch([() => toValue(locale), () => toValue(opts)], () => {
		lastResolvedParser = getDefaultParser();
	});
	return {
		parse,
		format,
		isValidNumberPart,
		getNumberingSystem
	};
}
/**
* https://github.com/sindresorhus/escape-string-regexp
*/
function escapeStringRegexp(string) {
	return string.replace(/[|\\{}()[\]^$+*?.]/g, "\\$&").replace(/-/g, "\\x2d");
}

//#endregion
//#region packages/core/src/i18n/checkLocaleMismatch.ts
function checkLocaleMismatch() {
	const configLocale = getConfig().locale;
	const userLocale = getUserLocale();
	return {
		matches: configLocale === userLocale,
		configLocale,
		userLocale
	};
}

//#endregion
//#region packages/core/src/i18n/useDateFormatter.ts
const dateFormatterCache = new Map();
function getFormatter(locale, options = {}) {
	const cacheKey = locale + JSON.stringify(options);
	let formatter = dateFormatterCache.get(cacheKey);
	if (!formatter) {
		formatter = new DateFormatter(locale, options);
		dateFormatterCache.set(cacheKey, formatter);
	}
	return formatter;
}
function useDateFormatter(locale, opts) {
	const resolvedLocale = getUserLocale();
	const formatter = shallowRef(getFormatter(toValue(locale) || resolvedLocale, toValue(opts)));
	watch(() => ({
		locale: toValue(locale) || resolvedLocale,
		opts: toValue(opts)
	}), (config, oldConfig) => {
		if (!isEqual(config, oldConfig)) formatter.value = getFormatter(config.locale, config.opts);
	});
	return formatter;
}

//#endregion
//#region packages/core/src/validation/useInputValidity.ts
function useInputValidity(opts) {
	const form = inject(FormKey, null);
	const formGroup = inject(FormGroupKey, null);
	const { setErrors, errorMessage, schema, validate: validateField, getPath } = opts.field;
	const validityDetails = shallowRef();
	useMessageCustomValiditySync(errorMessage, opts.inputEl, form, formGroup);
	const isHtmlValidationDisabled = () => toValue(opts.disableHtmlValidation) ?? (formGroup || form)?.isHtmlValidationDisabled() ?? getConfig().disableHtmlValidation;
	function validateNative(mutate) {
		const baseReturns = {
			type: "FIELD",
			path: getPath() || ""
		};
		const inputs = normalizeArrayable(opts.inputEl?.value).filter((el) => isInputElement(el));
		if (!inputs.length || isHtmlValidationDisabled()) return {
			...baseReturns,
			isValid: true,
			errors: [{
				messages: [],
				path: getPath() || ""
			}]
		};
		let messages = [];
		let validityIdx = -1;
		for (let i = 0; i < inputs.length; i++) {
			const input = inputs[i];
			input.setCustomValidity("");
			if (opts.groupValidityBehavior === "some" && input.validity.valid) {
				messages = [];
				validityIdx = i;
				break;
			}
			if (input.validationMessage) {
				messages.push(input.validationMessage);
				validityIdx = i;
			}
		}
		if (mutate) setErrors(messages);
		validityIdx = validityIdx === -1 ? 0 : validityIdx;
		validityDetails.value = inputs[validityIdx].validity;
		if (process.env.NODE_ENV !== "production") {
			const { matches, configLocale, userLocale } = checkLocaleMismatch();
			if (!matches && messages.length) warn(`HTML validation messages were generated using the browser's language (${userLocale}) and it does not match your site's language (${configLocale}). Consider disabling HTML Validation messages.`);
		}
		return {
			...baseReturns,
			isValid: !messages.length,
			errors: [{
				messages,
				path: getPath() || ""
			}]
		};
	}
	async function _updateValidity() {
		let result = validateNative(true);
		if (schema && result.isValid) result = await validateField(true);
		if (!result.isValid) return;
		(formGroup || form)?.requestValidation();
	}
	async function updateValidity() {
		await nextTick();
		_updateValidity();
	}
	useEventListener(opts.inputEl, opts?.events || ["change", "blur"], updateValidity);
	watch(opts.field.isDisabled, async () => {
		await nextTick();
		validateNative(true);
	});
	(formGroup || form)?.onValidationDispatch((enqueue) => {
		const result = validateNative(false);
		if (schema && result.isValid) {
			enqueue(validateField(false));
			return;
		}
		enqueue(Promise.resolve(result));
	});
	if (!schema) useEventListener(opts.inputEl, opts?.events || ["invalid"], () => validateNative(true));
	if (!opts.inputEl) watch(opts.field.fieldValue, updateValidity);
	/**
	* Validity is always updated on mount.
	*/
	onMounted(() => {
		nextTick(() => _updateValidity());
	});
	return {
		validityDetails,
		updateValidity
	};
}
/**
* Syncs the message with the input's native validation message.
*/
function useMessageCustomValiditySync(message, input, form, formGroup) {
	if (!input) return;
	function applySync(el, msg) {
		const inputMsg = el.validationMessage;
		if (inputMsg !== msg) el.setCustomValidity(msg || "");
	}
	function syncMessage(msg) {
		normalizeArrayable(toValue(input)).filter(isInputElement).forEach((el) => applySync(el, msg));
	}
	const syncMessageInline = () => syncMessage(message.value);
	(formGroup || form)?.onValidationDone(syncMessageInline);
	watch(message, syncMessage);
}

//#endregion
//#region packages/core/src/a11y/useErrorMessage.ts
function createErrorProps(inputId) {
	return computed(() => ({
		id: `${toValue(inputId)}-r`,
		"aria-live": "polite",
		"aria-atomic": true
	}));
}
function useErrorMessage({ inputId, errorMessage }) {
	const form = inject(FormKey, null);
	const errorMessageRef = shallowRef();
	const errorMessageProps = withRefCapture(createErrorProps(inputId), errorMessageRef);
	const accessibleErrorProps = computed(() => {
		const isInvalid = !!toValue(errorMessage);
		return {
			"aria-invalid": isInvalid,
			[FormIdAttr]: form?.id,
			"aria-errormessage": isInvalid ? errorMessageProps.value.id : void 0
		};
	});
	return {
		errorMessageProps,
		accessibleErrorProps
	};
}

//#endregion
//#region packages/core/src/reactivity/useModelSync.ts
function useSyncModel(opts) {
	const vm = getCurrentInstance();
	if (!vm) return;
	const { model, modelName = "modelValue", onModelPropUpdated } = opts;
	const getModelPropValue = () => vm.props[modelName];
	watch(() => toValue(model), (value) => {
		if (!isEqual(getModelPropValue(), value)) vm.emit(`update:${modelName}`, value);
	});
	watch(() => vm.props[modelName], (modelProp) => {
		if (!isEqual(modelProp, toValue(model))) onModelPropUpdated?.(modelProp);
	});
}

//#endregion
//#region packages/core/src/useFormField/useErrorDisplay.ts
function useErrorDisplay(errorMessage, isTouched) {
	function displayError(msg) {
		const error = msg || errorMessage.value;
		return isTouched.value ? error : "";
	}
	return { displayError };
}

//#endregion
//#region packages/core/src/useFormField/useFormField.ts
function useFormField(opts) {
	const form = inject(FormKey, null);
	const formGroup = inject(FormGroupKey, null);
	const pathPrefixer = usePathPrefixer();
	const isDisabled = createDisabledContext(opts?.disabled);
	const getPath = () => {
		const path = toValue(opts?.path);
		return pathPrefixer ? pathPrefixer.prefixPath(path) : path;
	};
	const initialValue = opts?.initialValue;
	const { fieldValue, pathlessValue, setValue } = useFieldValue(getPath, form, initialValue);
	const { isTouched, pathlessTouched, setTouched } = useFieldTouched(getPath, form);
	const { errors, setErrors, isValid, errorMessage, pathlessValidity, submitErrors, submitErrorMessage } = useFieldValidity(getPath, isDisabled, form);
	const { displayError } = useErrorDisplay(errorMessage, isTouched);
	const isDirty = computed(() => {
		if (!form) return !isEqual(fieldValue.value, initialValue);
		const path = getPath();
		if (!path) return !isEqual(pathlessValue.value, initialValue);
		return !isEqual(fieldValue.value, form.getFieldOriginalValue(path));
	});
	if (opts?.syncModel ?? true) useSyncModel({
		model: fieldValue,
		modelName: opts?.modelName ?? "modelValue",
		onModelPropUpdated: setValue
	});
	function createValidationResult(result) {
		return {
			type: "FIELD",
			path: (formGroup ? toValue(opts?.path) : getPath()) || "",
			...result
		};
	}
	async function validate(mutate) {
		const schema = opts?.schema;
		if (!schema) return Promise.resolve(createValidationResult({
			isValid: true,
			errors: [],
			output: cloneDeep(fieldValue.value)
		}));
		if (process.env.NODE_ENV !== "production") {
			if (isDisabled.value) warn("Field is disabled, the validation call will not have an immediate effect.");
		}
		const result = await schema["~standard"]["validate"](fieldValue.value);
		const errors$1 = combineStandardIssues(result.issues || []);
		const output = "value" in result ? result.value : void 0;
		if (mutate) setErrors(errors$1.map((e) => e.messages).flat());
		return createValidationResult({
			isValid: errors$1.length === 0,
			output,
			errors: errors$1
		});
	}
	const field = {
		fieldValue: readonly(fieldValue),
		isTouched: readonly(isTouched),
		isDirty,
		isValid,
		errors,
		errorMessage,
		isDisabled,
		schema: opts?.schema,
		validate,
		getPath,
		getName: () => toValue(opts?.path),
		setValue,
		setTouched,
		setErrors,
		displayError,
		submitErrors,
		submitErrorMessage
	};
	if (!form) return field;
	initFormPathIfNecessary({
		form,
		getPath,
		initialValue,
		initialTouched: opts?.initialTouched ?? false,
		initialDirty: opts?.initialDirty ?? false,
		isDisabled
	});
	form.onSubmitAttempt(() => {
		setTouched(true);
	});
	tryOnScopeDispose(() => {
		const path = getPath();
		if (!path) return null;
		form.transaction((_, { DESTROY_PATH }) => {
			return {
				kind: DESTROY_PATH,
				path
			};
		});
	});
	watch(getPath, (newPath, oldPath) => {
		if (oldPath) form.transaction((_, { UNSET_PATH }) => {
			return {
				kind: UNSET_PATH,
				path: oldPath
			};
		});
		if (newPath) form.transaction((tf, { SET_PATH }) => {
			return {
				kind: SET_PATH,
				path: newPath,
				value: cloneDeep(oldPath ? tf.getValue(oldPath) : pathlessValue.value),
				touched: oldPath ? tf.isTouched(oldPath) : pathlessTouched.value,
				dirty: oldPath ? tf.isDirty(oldPath) : isDirty.value,
				disabled: isDisabled.value,
				errors: [...oldPath ? tf.getErrors(oldPath) : pathlessValidity.errors.value]
			};
		});
	});
	watch(isDisabled, (disabled) => {
		const path = getPath();
		if (!path) return;
		form.setFieldDisabled(path, disabled);
	});
	return {
		...field,
		form
	};
}
function useFieldValidity(getPath, isDisabled, form) {
	const validity = form ? createFormValidityRef(getPath, form) : createLocalValidity();
	const errorMessage = computed(() => isDisabled.value ? "" : validity.errors.value[0] ?? "");
	const submitErrorMessage = computed(() => isDisabled.value ? "" : validity.submitErrors.value[0] ?? "");
	const isValid = computed(() => isDisabled.value ? true : validity.errors.value.length === 0);
	return {
		...validity,
		errors: computed(() => isDisabled.value ? [] : validity.errors.value),
		isValid,
		errorMessage,
		submitErrorMessage
	};
}
function useFieldValue(getPath, form, initialValue) {
	return form ? createFormValueRef(getPath, form, initialValue) : createLocalValueRef(initialValue);
}
function useFieldTouched(getPath, form) {
	return form ? createFormTouchedRef(getPath, form) : createLocalTouchedRef(false);
}
function createLocalTouchedRef(initialTouched) {
	const isTouched = shallowRef(initialTouched ?? false);
	return {
		isTouched,
		pathlessTouched: isTouched,
		setTouched(value) {
			isTouched.value = value;
		}
	};
}
function createFormTouchedRef(getPath, form) {
	const pathlessTouched = shallowRef(false);
	const isTouched = computed(() => {
		const path = getPath();
		return path ? form.isTouched(path) : pathlessTouched.value;
	});
	function setTouched(value) {
		const path = getPath();
		const isDifferent = pathlessTouched.value !== value;
		pathlessTouched.value = value;
		if (path && isDifferent) form.setTouched(path, value);
	}
	return {
		isTouched,
		pathlessTouched,
		setTouched
	};
}
function createFormValueRef(getPath, form, initialValue) {
	const pathlessValue = shallowRef(toValue(initialValue ?? void 0));
	const fieldValue = computed(() => {
		const path = getPath();
		return path ? form.getValue(path) : pathlessValue.value;
	});
	function setValue(value) {
		const path = getPath();
		pathlessValue.value = value;
		if (path) form.setValue(path, value);
	}
	return {
		fieldValue,
		pathlessValue,
		setValue
	};
}
function createLocalValueRef(initialValue) {
	const fieldValue = shallowRef(toValue(initialValue ?? void 0));
	return {
		fieldValue,
		pathlessValue: fieldValue,
		setValue(value) {
			fieldValue.value = cloneDeep(value);
		}
	};
}
/**
* Sets the initial value of the form if not already set and if an initial value is provided.
*/
function initFormPathIfNecessary({ form, getPath, initialValue, initialTouched, initialDirty, isDisabled }) {
	const path = getPath();
	if (!path) return;
	nextTick(() => {
		const formInitialValue = form.getFieldInitialValue(path);
		const currentValue = form.getValue(path);
		form.transaction((tf, { INIT_PATH }) => ({
			kind: INIT_PATH,
			path,
			value: initialValue ?? currentValue ?? formInitialValue,
			touched: initialTouched,
			dirty: initialDirty,
			disabled: toValue(isDisabled),
			errors: [...tf.getErrors(path)]
		}));
	});
}
function createFormValidityRef(getPath, form) {
	const pathlessValidity = createLocalValidity();
	const errors = computed(() => {
		const path = getPath();
		return path ? form.getErrors(path) : pathlessValidity.errors.value;
	});
	const submitErrors = computed(() => {
		const path = getPath();
		return path ? form.getFieldSubmitErrors(path) : [];
	});
	function setErrors(messages) {
		pathlessValidity.setErrors(messages);
		const path = getPath();
		if (path) form.setErrors(path, messages);
	}
	return {
		pathlessValidity,
		errors,
		setErrors,
		submitErrors
	};
}
function createLocalValidity() {
	const errors = shallowRef([]);
	const submitErrors = shallowRef([]);
	const api = {
		errors,
		submitErrors,
		setErrors(messages) {
			errors.value = messages ? normalizeArrayable(messages) : [];
		}
	};
	return {
		pathlessValidity: api,
		...api
	};
}
function exposeField(obj, field) {
	const exposedField = {
		displayError: field.displayError,
		errorMessage: field.errorMessage,
		errors: field.errors,
		submitErrors: field.submitErrors,
		submitErrorMessage: field.submitErrorMessage,
		fieldValue: field.fieldValue,
		isDirty: field.isDirty,
		isTouched: field.isTouched,
		isValid: field.isValid,
		isDisabled: field.isDisabled,
		setErrors: process.env.NODE_ENV !== "production" ? (messages) => {
			if (field.isDisabled.value) warn("This field is disabled, setting errors will not take effect until the field is enabled.");
			field.setErrors(messages);
		} : field.setErrors,
		setTouched: field.setTouched,
		setValue: field.setValue,
		validate: (mutate = true) => field.validate(mutate)
	};
	return {
		...obj,
		...exposedField
	};
}

//#endregion
//#region packages/core/src/useTextField/useTextField.ts
function useTextField(_props, elementRef) {
	const props = normalizeProps(_props, ["schema"]);
	const inputId = useUniqId(FieldTypePrefixes.TextField);
	const inputEl = elementRef || shallowRef();
	const field = useFormField({
		path: props.name,
		initialValue: toValue(props.modelValue) ?? toValue(props.value),
		disabled: props.disabled,
		schema: props.schema
	});
	const { validityDetails } = useInputValidity({
		inputEl,
		field,
		disableHtmlValidation: props.disableHtmlValidation
	});
	const { fieldValue, setValue, setTouched, errorMessage, isDisabled } = field;
	const { labelProps, labelledByProps } = useLabel({
		for: inputId,
		label: props.label,
		targetRef: inputEl
	});
	const { descriptionProps, describedByProps } = createDescribedByProps({
		inputId,
		description: props.description
	});
	const { accessibleErrorProps, errorMessageProps } = useErrorMessage({
		inputId,
		errorMessage
	});
	const handlers = {
		onInput(evt) {
			setValue(evt.target.value);
		},
		onChange(evt) {
			setValue(evt.target.value);
		},
		onBlur() {
			setTouched(true);
		}
	};
	const inputProps = computed(() => {
		return withRefCapture({
			...propsToValues(props, [
				"name",
				"type",
				"placeholder",
				"autocomplete",
				"required",
				"readonly"
			]),
			...labelledByProps.value,
			...describedByProps.value,
			...accessibleErrorProps.value,
			...handlers,
			id: inputId,
			value: fieldValue.value,
			maxlength: toValue(props.maxLength),
			minlength: toValue(props.minLength),
			disabled: isDisabled.value ? true : void 0,
			pattern: inputEl.value?.tagName === "TEXTAREA" ? void 0 : toValue(props.pattern)?.toString()
		}, inputEl, elementRef);
	});
	if (process.env.NODE_ENV !== "production") registerField(field, "Text");
	return exposeField({
		descriptionProps,
		errorMessageProps,
		inputEl,
		inputProps,
		labelProps,
		validityDetails
	}, field);
}

//#endregion
//#region packages/core/src/useSearchField/useSearchField.ts
function useSearchField(_props, elementRef) {
	const props = normalizeProps(_props, ["onSubmit", "schema"]);
	const inputId = useUniqId(FieldTypePrefixes.SearchField);
	const inputEl = elementRef || ref();
	const field = useFormField({
		path: props.name,
		initialValue: toValue(props.modelValue) ?? toValue(props.value),
		disabled: props.disabled,
		schema: props.schema
	});
	const isMutable = () => !toValue(props.readonly) && !field.isDisabled.value;
	const { validityDetails, updateValidity } = useInputValidity({
		inputEl,
		field,
		disableHtmlValidation: props.disableHtmlValidation
	});
	const { fieldValue, setValue, setTouched, errorMessage, isValid } = field;
	const { labelProps, labelledByProps } = useLabel({
		for: inputId,
		label: props.label,
		targetRef: inputEl
	});
	const { descriptionProps, describedByProps } = createDescribedByProps({
		inputId,
		description: props.description
	});
	const { accessibleErrorProps, errorMessageProps } = useErrorMessage({
		inputId,
		errorMessage
	});
	const clearBtnProps = computed(() => {
		return {
			tabindex: "-1",
			type: "button",
			ariaLabel: toValue(props.clearButtonLabel) ?? "Clear search",
			onClick() {
				if (!isMutable()) return;
				setValue("");
				updateValidity();
			}
		};
	});
	const handlers = {
		onInput: (event) => {
			setValue(event.target.value);
		},
		onChange: (event) => {
			setValue(event.target.value);
		},
		onBlur() {
			setTouched(true);
		},
		onKeydown(e) {
			if (hasKeyCode(e, "Escape")) {
				e.preventDefault();
				if (!isMutable()) return;
				setValue("");
				setTouched(true);
				updateValidity();
				return;
			}
			if (hasKeyCode(e, "Enter") && !inputEl.value?.form && props.onSubmit) {
				e.preventDefault();
				setTouched(true);
				if (isValid.value) props.onSubmit(fieldValue.value || "");
			}
		}
	};
	const inputProps = computed(() => withRefCapture({
		...propsToValues(props, [
			"name",
			"pattern",
			"placeholder",
			"required",
			"readonly"
		]),
		...labelledByProps.value,
		...describedByProps.value,
		...accessibleErrorProps.value,
		id: inputId,
		value: fieldValue.value,
		disabled: field.isDisabled.value ? true : void 0,
		type: "search",
		maxlength: toValue(props.maxLength),
		minlength: toValue(props.minLength),
		...handlers
	}, inputEl, elementRef));
	if (process.env.NODE_ENV !== "production") registerField(field, "Search");
	return exposeField({
		inputEl,
		inputProps,
		labelProps,
		errorMessageProps,
		descriptionProps,
		clearBtnProps,
		validityDetails
	}, field);
}

//#endregion
//#region packages/core/src/validation/useConstraintsValidator.ts
function useConstraintsValidator(constraints) {
	const element = shallowRef();
	onMounted(() => {
		element.value = document.createElement("input");
		element.value.type = constraints.type === "select" ? "text" : constraints.type;
	});
	watchEffect(() => {
		if (!element.value) return;
		element.value.required = toValue(constraints.required) ?? false;
		if (constraints.type === "text") {
			element.value.setAttribute("minlength", toValue(constraints.minLength)?.toString() ?? "");
			element.value.setAttribute("maxlength", toValue(constraints.maxLength)?.toString() ?? "");
			return;
		}
		if (constraints.type === "number") {
			element.value.setAttribute("min", toValue(constraints.min)?.toString() ?? "");
			element.value.setAttribute("max", toValue(constraints.max)?.toString() ?? "");
			return;
		}
		if (constraints.type === "date") {
			const min = toValue(constraints.min);
			const max = toValue(constraints.max);
			element.value.setAttribute("min", min ? dateToString(min) : "");
			element.value.setAttribute("max", max ? dateToString(max) : "");
			return;
		}
		if (constraints.type === "time") {
			const min = toValue(constraints.min);
			const max = toValue(constraints.max);
			element.value.setAttribute("min", min || "");
			element.value.setAttribute("max", max || "");
			element.value.setAttribute("step", "1");
			return;
		}
	});
	watchEffect(() => {
		if (!element.value) return;
		nextTick(() => {
			element.value?.dispatchEvent(new Event("change"));
		});
		if (constraints.type === "text" || element.value.type === "text") {
			const val = toValue(constraints.value);
			element.value.value = String(val ?? "");
			return;
		}
		if (constraints.type === "number") {
			const val = toValue(constraints.value);
			element.value.value = String(val ?? "");
			return;
		}
		if (constraints.type === "date") {
			const date = toValue(constraints.value);
			element.value.value = date ? dateToString(date) : "";
		}
		if (constraints.type === "time") {
			const time = toValue(constraints.value);
			element.value.value = time ? time : "";
		}
	});
	useEventListener(constraints.source, ["blur", "input"], (evt) => {
		element.value?.dispatchEvent(new Event(evt.type));
	});
	return { element };
}
function dateToString(date) {
	return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

//#endregion
//#region packages/core/src/useSwitch/useSwitch.ts
function useSwitch(_props, elementRef) {
	const props = normalizeProps(_props, ["schema"]);
	const inputId = useUniqId(FieldTypePrefixes.Switch);
	const inputEl = elementRef || shallowRef();
	const { labelProps, labelledByProps } = useLabel({
		for: inputId,
		label: props.label,
		targetRef: inputEl
	});
	const field = useFormField({
		path: props.name,
		initialValue: toValue(props.modelValue) ?? toValue(props.falseValue) ?? false,
		disabled: props.disabled,
		schema: props.schema
	});
	const { updateValidity } = useInputValidity({
		field,
		inputEl,
		disableHtmlValidation: props.disableHtmlValidation
	});
	const { fieldValue, setValue, setTouched, errorMessage, isDisabled } = field;
	const isMutable = () => !toValue(props.readonly) && !isDisabled.value;
	const { errorMessageProps, accessibleErrorProps } = useErrorMessage({
		inputId,
		errorMessage
	});
	/**
	* Normalizes in the incoming value to be either one of the given toggled values or a boolean.
	*/
	function normalizeValue(nextValue) {
		if (typeof nextValue === "boolean") return nextValue ? toValue(props.trueValue) ?? true : toValue(props.falseValue) ?? false;
		const trueValue = toValue(props.trueValue);
		if (isEqual(nextValue, trueValue)) return trueValue;
		const falseValue = toValue(props.falseValue);
		if (isEqual(nextValue, falseValue)) return falseValue;
		return !!nextValue;
	}
	function setValueFromEvent(e) {
		if (!isMutable()) return;
		setValue(normalizeValue(e.target.checked));
		setTouched(true);
	}
	const handlers = {
		onKeydown: (evt) => {
			if (hasKeyCode(evt, "Space") || hasKeyCode(evt, "Enter")) {
				evt.preventDefault();
				if (!isMutable()) return;
				togglePressed();
				setTouched(true);
				if (!isInputElement(inputEl.value)) updateValidity();
			}
		},
		onChange: setValueFromEvent,
		onInput: setValueFromEvent,
		onClick(e) {
			if (!isMutable()) {
				e.preventDefault();
				return;
			}
		}
	};
	function onClick(e) {
		if (!isMutable()) {
			e.preventDefault();
			return;
		}
		togglePressed();
		setTouched(true);
		updateValidity();
	}
	const isPressed = computed({
		get() {
			return isEqual(fieldValue.value, toValue(props.trueValue) ?? true);
		},
		set(value) {
			setValue(normalizeValue(value));
		}
	});
	function createBindings(isInput) {
		const base = {
			id: inputId,
			...labelledByProps.value,
			...accessibleErrorProps.value,
			[isInput ? "checked" : "aria-checked"]: isPressed.value || false,
			[isInput ? "required" : "aria-required"]: toValue(props.required) || void 0,
			[isInput ? "readonly" : "aria-readonly"]: toValue(props.readonly) || void 0,
			[isInput ? "disabled" : "aria-disabled"]: isDisabled.value || void 0,
			role: "switch"
		};
		if (isInput) return {
			...base,
			...handlers,
			name: toValue(props.name),
			type: "checkbox"
		};
		return {
			...base,
			onClick,
			tabindex: isDisabled.value ? "-1" : "0",
			onKeydown: handlers.onKeydown
		};
	}
	/**
	* Use this if you are using a native input[type=checkbox] element.
	*/
	const inputProps = computed(() => withRefCapture(createBindings(isInputElement(inputEl.value)), inputEl, elementRef));
	function togglePressed(force) {
		isPressed.value = force ?? !isPressed.value;
	}
	if (process.env.NODE_ENV !== "production") registerField(field, "Switch");
	return exposeField({
		errorMessageProps,
		inputEl,
		inputProps,
		isPressed,
		labelProps,
		togglePressed
	}, field);
}

//#endregion
//#region packages/core/src/useRadio/useRadioGroup.ts
const RadioGroupKey = Symbol("RadioGroupKey");
function getOrientationArrows(dir) {
	const nextKeys = ["ArrowDown"];
	const prevKeys = ["ArrowUp"];
	if (dir === "rtl") {
		nextKeys.push("ArrowLeft");
		prevKeys.push("ArrowRight");
		return {
			prev: prevKeys,
			next: nextKeys
		};
	}
	nextKeys.push("ArrowRight");
	prevKeys.push("ArrowLeft");
	return {
		prev: prevKeys,
		next: nextKeys
	};
}
function useRadioGroup(_props) {
	const props = normalizeProps(_props, ["schema"]);
	const groupId = useUniqId(FieldTypePrefixes.RadioButtonGroup);
	const { direction } = useLocale();
	const radios = ref([]);
	const { labelProps, labelledByProps } = useLabel({
		for: groupId,
		label: props.label
	});
	const field = useFormField({
		path: props.name,
		initialValue: toValue(props.modelValue),
		disabled: props.disabled,
		schema: props.schema
	});
	const { validityDetails } = useInputValidity({
		field,
		events: [
			"blur",
			"click",
			["keydown", (e) => hasKeyCode(e, "Space")]
		],
		groupValidityBehavior: "some",
		inputEl: computed(() => radios.value.map((r) => r.getElem())),
		disableHtmlValidation: props.disableHtmlValidation
	});
	const { fieldValue, setValue, setTouched, errorMessage, isDisabled } = field;
	const { descriptionProps, describedByProps } = createDescribedByProps({
		inputId: groupId,
		description: props.description
	});
	const { accessibleErrorProps, errorMessageProps } = useErrorMessage({
		inputId: groupId,
		errorMessage
	});
	function handleArrowNext() {
		let currentIdx = radios.value.findIndex((radio) => radio.isChecked());
		if (currentIdx === -1 || radios.value.length <= 1) {
			radios.value.find((radio) => !radio.isDisabled())?.setChecked();
			return;
		}
		const candidates = radios.value.filter((radio) => !radio.isDisabled());
		currentIdx = candidates.findIndex((radio) => radio.isChecked());
		if (currentIdx === -1) {
			candidates[0].setChecked();
			return;
		}
		const nextIdx = getNextCycleArrIdx(currentIdx + 1, candidates);
		candidates[nextIdx].setChecked();
	}
	function handleArrowPrevious() {
		let currentIdx = radios.value.findIndex((radio) => radio.isChecked());
		if (currentIdx === -1 || radios.value.length <= 1) {
			radios.value.find((radio) => !radio.isDisabled())?.setChecked();
			return;
		}
		const candidates = radios.value.filter((radio) => !radio.isDisabled());
		currentIdx = candidates.findIndex((radio) => radio.isChecked());
		if (currentIdx === -1) {
			candidates[0].setChecked();
			return;
		}
		const nextIdx = getNextCycleArrIdx(currentIdx - 1, candidates);
		candidates[nextIdx].setChecked();
	}
	const groupProps = computed(() => {
		return {
			...labelledByProps.value,
			...describedByProps.value,
			...accessibleErrorProps.value,
			dir: toValue(props.dir) ?? direction.value,
			role: "radiogroup",
			"aria-orientation": toValue(props.orientation) ?? void 0,
			onKeydown(e) {
				if (isDisabled.value) return;
				const { next, prev } = getOrientationArrows(toValue(props.dir));
				if (next.includes(e.code)) {
					e.preventDefault();
					handleArrowNext();
					setTouched(true);
					return;
				}
				if (prev.includes(e.code)) {
					e.preventDefault();
					handleArrowPrevious();
					setTouched(true);
					return;
				}
			}
		};
	});
	function useRadioRegistration(radio) {
		const id = radio.id;
		radios.value.push(radio);
		onBeforeUnmount(() => {
			removeFirst(radios.value, (reg) => reg.id === id);
		});
		return { canReceiveFocus() {
			return radios.value[0].id === radio.id && isEmpty(fieldValue.value);
		} };
	}
	function setGroupValue(value) {
		if (isDisabled.value || toValue(props.readonly)) return;
		setValue(value);
	}
	const context = reactive({
		name: computed(() => toValue(props.name) ?? groupId),
		readonly: computed(() => toValue(props.readonly) ?? false),
		required: computed(() => toValue(props.required) ?? false),
		modelValue: fieldValue,
		setGroupValue,
		setTouched,
		useRadioRegistration
	});
	provide(RadioGroupKey, context);
	if (process.env.NODE_ENV !== "production") registerField(field, "Radio");
	return exposeField({
		descriptionProps,
		errorMessageProps,
		groupProps,
		labelProps,
		validityDetails
	}, field);
}

//#endregion
//#region packages/core/src/useRadio/useRadio.ts
function useRadio(_props, elementRef) {
	const props = normalizeProps(_props);
	const inputId = useUniqId(FieldTypePrefixes.RadioButton);
	const group = inject(RadioGroupKey, null);
	const inputEl = elementRef || ref();
	const checked = computed(() => isEqual(group?.modelValue, toValue(props.value)));
	const isDisabled = createDisabledContext(props.disabled);
	const { labelProps, labelledByProps } = useLabel({
		for: inputId,
		label: props.label,
		targetRef: inputEl
	});
	if (!group) warn("A Radio component must be a part of a Radio Group. Make sure you have called useRadioGroup at a parent component");
	const isReadOnly = () => group?.readonly ?? false;
	const isMutable = () => !isReadOnly() && !isDisabled.value;
	function focus() {
		inputEl.value?.focus();
	}
	function setChecked() {
		if (!isMutable()) return false;
		group?.setGroupValue(toValue(props.value));
		group?.setTouched(true);
		focus();
		return true;
	}
	const registration = group?.useRadioRegistration({
		id: inputId,
		getElem: () => inputEl.value,
		isChecked: () => checked.value,
		isDisabled: () => isDisabled.value,
		setChecked
	});
	const handlers = {
		onClick(e) {
			if (!isMutable()) {
				e.preventDefault();
				return;
			}
			setChecked();
		},
		onKeydown(e) {
			if (hasKeyCode(e, "Space")) {
				e.preventDefault();
				setChecked();
			}
		},
		onBlur() {
			group?.setTouched(true);
		}
	};
	function createBindings(isInput) {
		const base = {
			...labelledByProps.value,
			...handlers,
			id: inputId,
			[isInput ? "checked" : "aria-checked"]: checked.value,
			[isInput ? "readonly" : "aria-readonly"]: group?.readonly || void 0,
			[isInput ? "disabled" : "aria-disabled"]: isDisabled.value || void 0,
			[isInput ? "required" : "aria-required"]: group?.required
		};
		if (isInput) return {
			...base,
			name: group?.name,
			type: "radio"
		};
		return {
			...base,
			role: "radio",
			tabindex: checked.value ? "0" : registration?.canReceiveFocus() && !isDisabled.value ? "0" : "-1"
		};
	}
	const inputProps = computed(() => withRefCapture(createBindings(isInputElement(inputEl.value)), inputEl, elementRef));
	return {
		inputEl,
		inputProps,
		isChecked: checked,
		isDisabled,
		labelProps
	};
}

//#endregion
//#region packages/core/src/utils/math.ts
function toNearestMultipleOf(value, multiple, round) {
	const smallerMultiple = value / multiple * multiple;
	const largerMultiple = smallerMultiple + multiple;
	const result = value - smallerMultiple >= largerMultiple - value ? largerMultiple : smallerMultiple;
	return round ? Math.round(result) : Math.trunc(result);
}

//#endregion
//#region packages/core/src/useSlider/useSlider.ts
const SliderInjectionKey = Symbol("Slider");
function useSlider(_props) {
	const props = normalizeProps(_props, ["schema"]);
	const inputId = useUniqId(FieldTypePrefixes.Slider);
	const trackEl = ref();
	const thumbs = ref([]);
	const { direction } = useLocale();
	const field = useFormField({
		path: props.name,
		initialValue: toValue(props.modelValue) ?? toValue(props.value),
		disabled: props.disabled,
		schema: props.schema
	});
	if (process.env.NODE_ENV !== "production") checkValidProps(_props);
	const { fieldValue, setValue, setTouched, isDisabled } = field;
	const isReadonly = () => toValue(props.readonly) ?? false;
	const isMutable = () => !isDisabled.value && !isReadonly();
	const { updateValidity } = useInputValidity({ field });
	const { labelProps, labelledByProps } = useLabel({
		for: inputId,
		label: props.label,
		targetRef: trackEl,
		handleClick: () => thumbs.value[0]?.focus()
	});
	const { errorMessageProps, accessibleErrorProps } = useErrorMessage({
		inputId,
		errorMessage: field.errorMessage
	});
	const groupProps = computed(() => ({
		...labelledByProps.value,
		id: inputId,
		role: "group",
		dir: toValue(props.dir)
	}));
	function getThumbValue(idx) {
		if (Array.isArray(fieldValue.value)) return mapStopToNumber(fieldValue.value[idx]);
		if (idx === 0) return mapStopToNumber(fieldValue.value);
		return void 0;
	}
	function mapNumberToStop(value) {
		const stops = getOptions();
		return stops?.length ? stops[value] : value;
	}
	function mapStopToNumber(stop) {
		if (isNullOrUndefined(stop)) return stop || void 0;
		const stops = getOptions();
		if (stops?.length) {
			const idx = stops.findIndex((s) => isEqual(s, stop));
			if (process.env.NODE_ENV !== "production") {
				if (idx === -1) warn(`Could not find stop for value ${stop}`);
			}
			return idx;
		}
		if (process.env.NODE_ENV !== "production") {
			if (typeof stop !== "number") warn(`Could not find stop for value ${stop}`);
		}
		return stop;
	}
	function getOptions() {
		return toValue(props.options);
	}
	function setThumbValue(idx, value) {
		if (!isMutable()) return;
		if (thumbs.value.length <= 1) {
			setValue(mapNumberToStop(value));
			updateValidity();
			return;
		}
		const nextValue = normalizeArrayable(fieldValue.value).filter((v) => !isNullOrUndefined(v));
		nextValue[idx] = mapNumberToStop(value);
		setValue(nextValue);
		updateValidity();
	}
	const trackProps = computed(() => {
		const isVertical = toValue(props.orientation) === "vertical";
		return withRefCapture({
			style: {
				"container-type": isVertical ? "size" : "inline-size",
				position: "relative"
			},
			onMousedown(e) {
				if (!trackEl.value || !isMutable()) return;
				const targetValue = getValueForPagePosition({
					x: e.clientX,
					y: e.clientY
				});
				const closest = thumbs.value.reduce((candidate, curr, idx) => {
					const { min, max } = getThumbRange(curr);
					if (targetValue < min || targetValue > max) return candidate;
					const currentThumbValue = getThumbValue(idx);
					if (isNullOrUndefined(currentThumbValue)) return candidate;
					const diff = Math.abs(currentThumbValue - targetValue);
					return diff < candidate.diff ? {
						thumb: curr,
						diff,
						idx
					} : candidate;
				}, {
					thumb: thumbs.value[0],
					idx: 0,
					diff: Infinity
				});
				setThumbValue(closest.idx, targetValue);
				setTouched(true);
			}
		}, trackEl);
	});
	function getValueForPagePosition({ x, y }) {
		if (!trackEl.value) return 0;
		const orientation = toValue(props.orientation) || "horizontal";
		const rect = trackEl.value.getBoundingClientRect();
		let percent = orientation === "horizontal" ? (x - rect.left) / rect.width : (y - rect.top) / rect.height;
		if (toValue(props.dir) === "rtl" || orientation === "vertical") percent = 1 - percent;
		const { min, max } = getSliderRange();
		const value = percent * (max - min) + min;
		return toNearestMultipleOf(value, getSliderStep(), !!getOptions());
	}
	function getSliderRange() {
		const stops = getOptions();
		if (stops?.length) return {
			min: 0,
			max: stops.length - 1
		};
		return {
			min: fromNumberish(props.min) ?? 0,
			max: fromNumberish(props.max) ?? 100
		};
	}
	function getThumbRange(thumbCtx) {
		const { min: absoluteMin, max: absoluteMax } = getSliderRange();
		const idx = thumbs.value.indexOf(thumbCtx);
		const nextThumb = getThumbValue(idx + 1);
		const prevThumb = getThumbValue(idx - 1);
		const min = prevThumb ?? absoluteMin;
		const max = nextThumb ?? absoluteMax;
		return {
			min,
			max,
			absoluteMin,
			absoluteMax
		};
	}
	function getSliderStep() {
		const stops = getOptions();
		if (stops?.length) return 1;
		return fromNumberish(props.step) ?? 1;
	}
	function useSliderThumbRegistration(ctx) {
		const id = ctx.id;
		thumbs.value.push(ctx);
		onBeforeUnmount(() => {
			removeFirst(thumbs.value, (reg$1) => reg$1.id === id);
		});
		function getThumbIndex() {
			return thumbs.value.findIndex((t) => t.id === id);
		}
		const reg = {
			getThumbRange: () => getThumbRange(ctx),
			getSliderRange,
			getSliderLabelProps() {
				return labelledByProps.value;
			},
			getSliderStep,
			getValueForPagePosition,
			getOrientation: () => toValue(props.orientation) || "horizontal",
			getInlineDirection: () => toValue(props.dir) || direction.value,
			getIndex: getThumbIndex,
			getThumbValue: () => {
				const { absoluteMin } = getThumbRange(ctx);
				return getThumbValue(getThumbIndex()) ?? absoluteMin;
			},
			getRealThumbValue() {
				return mapNumberToStop(this.getThumbValue());
			},
			setThumbValue(value) {
				setThumbValue(getThumbIndex(), value);
			},
			setTouched,
			getAccessibleErrorProps: () => accessibleErrorProps.value
		};
		return reg;
	}
	const outputProps = { "aria-live": "off" };
	provide(SliderInjectionKey, { useSliderThumbRegistration });
	/**
	* Gets the metadata for a given thumb.
	*/
	function useThumbMetadata(idx) {
		return computed(() => {
			const value = getThumbValue(idx);
			if (isNullOrUndefined(value)) return {
				value: 0,
				percent: 0,
				min: 0,
				max: 0,
				sliderPercent: 0,
				sliderMin: 0,
				sliderMax: 0
			};
			const { min, max, absoluteMax, absoluteMin } = getThumbRange(thumbs.value[idx]);
			const absolutePercent = (value - absoluteMin) / (absoluteMax - absoluteMin);
			const percent = (value - min) / (max - min);
			return {
				value,
				percent,
				min,
				max,
				sliderPercent: absolutePercent,
				sliderMin: absoluteMin,
				sliderMax: absoluteMax
			};
		});
	}
	if (process.env.NODE_ENV !== "production") registerField(field, "Slider");
	return exposeField({
		trackEl,
		labelProps,
		groupProps,
		outputProps,
		trackProps,
		errorMessageProps,
		useThumbMetadata
	}, field);
}
function checkValidProps(props) {
	const isDiscreteStepsPresent = !!props.options;
	const isMinMaxPresent = !!props.min || !!props.max;
	const isStepPresent = props.step ?? false;
	if (isDiscreteStepsPresent && isMinMaxPresent) warn("Cannot use discrete steps with min/max, the min/max will be ignored.");
	if (isDiscreteStepsPresent && isStepPresent) warn("Cannot use discrete steps with explicit step value, the step value will be ignored.");
}

//#endregion
//#region packages/core/src/helpers/useButtonHold/useButtonHold.ts
function useButtonHold(opts) {
	let interval;
	let timeout;
	let isTicking = false;
	function executeHoldTick(e) {
		if (toValue(opts.disabled)) {
			clearAll();
			return;
		}
		opts.onHoldTick(e);
	}
	function onMousedown(e) {
		if (isTicking || toValue(opts.disabled)) return;
		clearAll();
		opts.onClick(e);
		const tickRate = opts.tickRate || 100;
		document.addEventListener("mouseup", onMouseup, { once: true });
		timeout = window.setTimeout(() => {
			isTicking = true;
			interval = window.setInterval(() => executeHoldTick(e), tickRate);
		}, tickRate);
	}
	function onMouseup() {
		clearAll();
	}
	function clearAll() {
		window.clearInterval(interval);
		window.clearTimeout(timeout);
		isTicking = false;
	}
	const buttonHoldProps = { onMousedown };
	tryOnScopeDispose(() => {
		document.removeEventListener("mouseup", onMouseup);
		clearAll();
	});
	return buttonHoldProps;
}

//#endregion
//#region packages/core/src/useSpinButton/useSpinButton.ts
function getDirectionalStepKeys(orientation, direction) {
	const horizontalDirKeys = {
		ltr: {
			incrKeys: ["ArrowRight"],
			decrKeys: ["ArrowLeft"]
		},
		rtl: {
			incrKeys: ["ArrowLeft"],
			decrKeys: ["ArrowRight"]
		}
	};
	const horizontalKeys = horizontalDirKeys[direction];
	const verticalKeys = {
		incrKeys: ["ArrowUp"],
		decrKeys: ["ArrowDown"]
	};
	const incrKeys = [];
	const decrKeys = [];
	if (orientation === "horizontal" || orientation === "both") {
		incrKeys.push(...horizontalKeys.incrKeys);
		decrKeys.push(...horizontalKeys.decrKeys);
	}
	if (orientation === "vertical" || orientation === "both") {
		incrKeys.push(...verticalKeys.incrKeys);
		decrKeys.push(...verticalKeys.decrKeys);
	}
	return {
		incrKeys,
		decrKeys
	};
}
function useSpinButton(_props) {
	const props = normalizeProps(_props, ["onChange"]);
	const { direction } = useLocale();
	const getStep = () => fromNumberish(props.step) || 1;
	const getMin = () => fromNumberish(props.min) ?? void 0;
	const getMax = () => fromNumberish(props.max) ?? void 0;
	const incrBtnEl = shallowRef();
	const decrBtnEl = shallowRef();
	/**
	* Dynamically calculate the multiplier for the page up/down keys.
	*/
	const getPageMultiplier = () => {
		const max = getMax();
		const min = getMin();
		if (isNullOrUndefined(max) || isNullOrUndefined(min)) return 10;
		return Math.max(Math.floor(Math.abs((max - min) / min)), getStep());
	};
	function onKeydown(e) {
		if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || toValue(props.disabled) || toValue(props.readonly)) return;
		if (hasKeyCode(e, "PageUp")) {
			e.preventDefault();
			pageIncrement();
			return;
		}
		if (hasKeyCode(e, "PageDown")) {
			e.preventDefault();
			pageDecrement();
			return;
		}
		const { incrKeys, decrKeys } = getDirectionalStepKeys(toValue(props.orientation) || "both", toValue(props.dir) || direction.value);
		if (incrKeys.includes(e.code)) {
			e.preventDefault();
			increment();
			return;
		}
		if (decrKeys.includes(e.code)) {
			e.preventDefault();
			decrement();
			return;
		}
		if (hasKeyCode(e, "Home")) {
			e.preventDefault();
			incrementToMax();
			return;
		}
		if (hasKeyCode(e, "End")) {
			e.preventDefault();
			decrementToMin();
			return;
		}
	}
	function applyClamp(value) {
		const max = getMax();
		const min = getMin();
		if (!isNullOrUndefined(max) && value >= max) return max;
		if (!isNullOrUndefined(min) && value <= min) return min;
		return value;
	}
	function tryChange(diff) {
		const current = toValue(props.current) || 0;
		const step = getStep();
		const next = applyClamp(toNearestMultipleOf(current + diff, step));
		props.onChange?.(next);
	}
	function increment() {
		tryChange(getStep());
	}
	function decrement() {
		tryChange(-getStep());
	}
	function pageIncrement() {
		tryChange(getStep() * getPageMultiplier());
	}
	function pageDecrement() {
		tryChange(-getStep() * getPageMultiplier());
	}
	function incrementToMax() {
		const max = getMax();
		if (!isNullOrUndefined(max)) props.onChange?.(max);
	}
	function decrementToMin() {
		const min = getMin();
		if (!isNullOrUndefined(min)) props.onChange?.(min);
	}
	const isIncrementDisabled = computed(() => {
		const max = getMax();
		const current = toValue(props.current);
		return !isNullOrUndefined(max) && current !== void 0 && max <= current;
	});
	const isDecrementDisabled = computed(() => {
		const min = getMin();
		const current = toValue(props.current);
		return !isNullOrUndefined(min) && current !== void 0 && min >= current;
	});
	const incrementHoldProps = useButtonHold({
		onClick: onlyMainMouseButton(increment),
		onHoldTick: onlyMainMouseButton(increment),
		disabled: isIncrementDisabled
	});
	const decrementHoldProps = useButtonHold({
		onClick: onlyMainMouseButton(decrement),
		onHoldTick: onlyMainMouseButton(decrement),
		disabled: isDecrementDisabled
	});
	function getButtonProps(btnEl, baseProps) {
		const isBtn = isButtonElement(btnEl);
		if (!isBtn) return {
			role: "button",
			["aria-disabled"]: baseProps.disabled || void 0
		};
		return {
			type: "button",
			disabled: baseProps.disabled
		};
	}
	const incrementButtonProps = computed(() => {
		return withRefCapture({
			...incrementHoldProps,
			...getButtonProps(incrBtnEl.value, { disabled: toValue(props.disabled) || isIncrementDisabled.value }),
			"aria-label": toValue(props.incrementLabel) || "Increment",
			tabindex: toValue(props.preventTabIndex) ? "-1" : void 0
		}, incrBtnEl);
	});
	const decrementButtonProps = computed(() => {
		return withRefCapture({
			...decrementHoldProps,
			...getButtonProps(decrBtnEl.value, { disabled: toValue(props.disabled) || isDecrementDisabled.value }),
			"aria-label": toValue(props.decrementLabel) || "Decrement",
			tabindex: toValue(props.preventTabIndex) ? "-1" : void 0
		}, decrBtnEl);
	});
	const spinButtonProps = computed(() => {
		return {
			onKeydown,
			"aria-valuenow": toValue(props.current),
			"aria-valuemin": toValue(props.min),
			"aria-valuemax": toValue(props.max),
			"aria-valuetext": toValue(props.currentText)
		};
	});
	return {
		increment,
		decrement,
		pageDecrement,
		pageIncrement,
		incrementToMax,
		decrementToMin,
		applyClamp,
		incrementButtonProps,
		decrementButtonProps,
		spinButtonProps
	};
}

//#endregion
//#region packages/core/src/useSlider/useSliderThumb.ts
function useSliderThumb(_props, elementRef) {
	const props = normalizeProps(_props, ["formatValue"]);
	const thumbEl = elementRef || ref();
	const isDisabled = createDisabledContext(props.disabled);
	const isDragging = ref(false);
	const { direction } = useLocale();
	const id = useUniqId(FieldTypePrefixes.SliderThumb);
	const thumbContext = {
		id,
		focus() {
			thumbEl.value?.focus();
		}
	};
	const mockSlider = () => ({ useSliderThumbRegistration: () => ({
		getThumbRange: () => ({
			min: 0,
			max: 100
		}),
		getSliderRange: () => ({
			min: 0,
			max: 100
		}),
		getSliderStep: () => 1,
		getSliderLabelProps: () => ({}),
		getValueForPagePosition: () => 0,
		getRealThumbValue: () => 0,
		getOrientation: () => "horizontal",
		getInlineDirection: () => direction.value,
		getIndex: () => 0,
		getThumbValue: () => 0,
		setThumbValue: NOOP,
		setTouched: NOOP,
		isDisabled: () => false,
		getAccessibleErrorProps: () => ({
			"aria-invalid": false,
			"aria-errormessage": void 0
		}),
		__isMock: true
	}) });
	const slider = inject(SliderInjectionKey, mockSlider, true).useSliderThumbRegistration(thumbContext);
	const thumbValue = computed(() => slider.getThumbValue());
	const thumbRealValue = computed(() => slider.getRealThumbValue());
	if ("__isMock" in slider) warn("A Thumb must be used within a slider component. Make sure you have called `useSlider` in a parent component.");
	const textValue = computed(() => {
		const realValue = slider.getRealThumbValue();
		return props.formatValue ? props.formatValue(realValue) : String(realValue);
	});
	const { spinButtonProps, applyClamp } = useSpinButton({
		current: thumbValue,
		disabled: isDisabled,
		orientation: "both",
		currentText: textValue,
		min: () => slider.getThumbRange().min,
		max: () => slider.getThumbRange().max,
		step: () => slider.getSliderStep(),
		dir: () => slider.getInlineDirection(),
		onChange: (next) => {
			slider.setThumbValue(next);
			slider.setTouched(true);
		}
	});
	function setValue(value) {
		slider.setThumbValue(applyClamp(value));
	}
	const thumbProps = computed(() => {
		const ownLabel = toValue(props.label);
		return withRefCapture({
			tabindex: isDisabled.value ? "-1" : "0",
			role: "slider",
			...slider.getAccessibleErrorProps(),
			"aria-orientation": slider.getOrientation(),
			"aria-label": ownLabel ?? void 0,
			...ownLabel ? {} : slider.getSliderLabelProps(),
			...spinButtonProps.value,
			onMousedown: onPointerdown,
			onTouchstart: onPointerdown,
			style: getPositionStyle()
		}, thumbEl);
	});
	function getPositionStyle() {
		const value = slider.getThumbValue();
		const { min, max } = slider.getSliderRange();
		const dir = slider.getInlineDirection();
		let percent = (value - min) / (max - min) * 100;
		const orientation = slider.getOrientation();
		if (dir === "rtl" || orientation === "vertical") percent = 1 - percent;
		const inlineBound = dir === "rtl" ? "right" : "left";
		const positionProp = orientation === "vertical" ? "bottom" : inlineBound;
		const translateX = orientation === "vertical" ? "0" : `calc(${percent}cqw ${dir === "rtl" ? "+" : "-"} 50%)`;
		const translateY = orientation === "vertical" ? `calc(${percent}cqh + 50%)` : "0";
		return {
			position: "absolute",
			[positionProp]: "0",
			willChange: "translate",
			translate: `${translateX} ${translateY}`
		};
	}
	function onPointerdown(e) {
		e.preventDefault();
		e.stopPropagation();
		thumbEl.value?.focus();
		if ("button" in e && e.button !== 0) return;
		if ("touches" in e && e.touches.length > 1) return;
		if (e.type === "touchstart") {
			document.addEventListener("touchmove", onPointermove);
			document.addEventListener("touchend", onPointerup);
		} else {
			document.addEventListener("mousemove", onPointermove);
			document.addEventListener("mouseup", onPointerup);
		}
		isDragging.value = true;
	}
	function onPointermove(e) {
		const clientX = "clientX" in e ? e.clientX : e.touches[0].clientX;
		const clientY = "clientY" in e ? e.clientY : e.touches[0].clientY;
		e.preventDefault();
		e.stopPropagation();
		setValue(slider.getValueForPagePosition({
			x: clientX,
			y: clientY
		}));
	}
	function onPointerup(e) {
		if (e.type === "touchend") {
			document.removeEventListener("touchmove", onPointermove);
			document.removeEventListener("touchend", onPointerup);
		} else {
			document.removeEventListener("mousemove", onPointermove);
			document.removeEventListener("mouseup", onPointerup);
		}
		isDragging.value = false;
		slider.setTouched(true);
	}
	return {
		thumbProps,
		currentValue: thumbRealValue,
		isDragging,
		isDisabled,
		thumbEl,
		currentText: textValue
	};
}

//#endregion
//#region packages/core/src/useCheckbox/useCheckboxGroup.ts
const CheckboxGroupKey = Symbol("CheckboxGroupKey");
function useCheckboxGroup(_props) {
	const props = normalizeProps(_props, ["schema"]);
	const groupId = useUniqId(FieldTypePrefixes.CheckboxGroup);
	const { direction } = useLocale();
	const checkboxes = ref([]);
	const { labelProps, labelledByProps } = useLabel({
		for: groupId,
		label: props.label
	});
	const field = useFormField({
		path: props.name,
		initialValue: toValue(props.modelValue),
		schema: props.schema,
		disabled: props.disabled
	});
	const { validityDetails, updateValidity } = useInputValidity({
		field,
		inputEl: computed(() => checkboxes.value.map((v) => v.getElem())),
		events: [
			"blur",
			"click",
			["keydown", (e) => hasKeyCode(e, "Space")]
		],
		groupValidityBehavior: "some",
		disableHtmlValidation: props.disableHtmlValidation
	});
	const { fieldValue, setValue, isTouched, setTouched, errorMessage, isDisabled } = field;
	const { describedByProps, descriptionProps } = createDescribedByProps({
		inputId: groupId,
		description: props.description
	});
	const { accessibleErrorProps, errorMessageProps } = useErrorMessage({
		inputId: groupId,
		errorMessage
	});
	const groupProps = computed(() => {
		return {
			...labelledByProps.value,
			...describedByProps.value,
			...accessibleErrorProps.value,
			dir: toValue(props.dir) ?? direction.value,
			role: "group"
		};
	});
	function useCheckboxRegistration(checkbox) {
		const id = checkbox.id;
		checkboxes.value.push(checkbox);
		onBeforeUnmount(() => {
			removeFirst(checkboxes.value, (reg) => reg.id === id);
		});
	}
	function toggleValue(value, force) {
		if (isDisabled.value || toValue(props.readonly)) return;
		const nextValue = toggleValueSelection(fieldValue.value ?? [], value, force);
		setValue(nextValue);
		if (checkboxes.value.some((c) => !isInputElement(c.getElem()))) updateValidity();
	}
	function hasValue(value) {
		return (fieldValue.value ?? []).some((v) => isEqual(v, value));
	}
	const groupState = computed({
		get() {
			if (!fieldValue.value || !fieldValue.value.length) return "unchecked";
			if (fieldValue.value.length > 0 && fieldValue.value.length < checkboxes.value.length) return "mixed";
			return "checked";
		},
		set(value) {
			if (value === "mixed") {
				if (process.env.NODE_ENV !== "production") warn("You cannot set the group state to \"mixed\"");
				return;
			}
			checkboxes.value.forEach((c) => c.setChecked(value === "checked"));
		}
	});
	const context = reactive({
		name: computed(() => toValue(props.name) ?? groupId),
		readonly: computed(() => toValue(props.readonly) ?? false),
		required: computed(() => toValue(props.required) ?? false),
		field: markRaw(field),
		groupState,
		modelValue: fieldValue,
		isTouched,
		setErrors: field.setErrors,
		useCheckboxRegistration,
		toggleValue,
		hasValue,
		setTouched
	});
	provide(CheckboxGroupKey, context);
	return exposeField({
		descriptionProps,
		errorMessageProps,
		groupProps,
		groupState,
		labelProps,
		validityDetails
	}, field);
}

//#endregion
//#region packages/core/src/useCheckbox/useCheckbox.ts
function useCheckbox(_props, elementRef) {
	const props = normalizeProps(_props, ["schema"]);
	const inputId = useUniqId(FieldTypePrefixes.Checkbox);
	const getTrueValue = createTrueValueGetter(props);
	const getFalseValue = () => toValue(props.falseValue) ?? false;
	const group = toValue(props.standalone) ? null : inject(CheckboxGroupKey, null);
	const inputEl = elementRef || ref();
	const field = useCheckboxField(props);
	if (!group) useInputValidity({
		inputEl,
		field,
		events: [
			"blur",
			"click",
			["keydown", (e) => hasKeyCode(e, "Space")]
		],
		disableHtmlValidation: props.disableHtmlValidation
	});
	const { fieldValue, setTouched, setValue, errorMessage, setErrors, isDisabled } = field;
	const checked = computed({
		get() {
			if (group) return group.hasValue(getTrueValue());
			return isEqual(fieldValue.value, getTrueValue());
		},
		set(value) {
			setChecked(value);
		}
	});
	const { labelProps, labelledByProps } = useLabel({
		for: inputId,
		label: props.label,
		targetRef: inputEl
	});
	const { errorMessageProps, accessibleErrorProps } = useErrorMessage({
		inputId,
		errorMessage
	});
	const isReadOnly = () => (toValue(props.readonly) || group?.readonly) ?? false;
	const isMutable = () => !isDisabled.value && !isReadOnly() && !toValue(props.indeterminate);
	function createHandlers(isInput) {
		const baseHandlers = {
			onClick(e) {
				if (!isMutable()) {
					if (isInput) {
						e.stopPropagation();
						e.preventDefault();
					}
					return;
				}
				toggleValue();
				setTouched(true);
			},
			onKeydown(e) {
				if (!isMutable()) return;
				if (hasKeyCode(e, "Space")) {
					e.preventDefault();
					toggleValue();
					setTouched(true);
				}
			},
			onBlur() {
				setTouched(true);
			}
		};
		if (isInput) return { ...baseHandlers };
		return baseHandlers;
	}
	function focus() {
		if (toValue(props.disabled)) return;
		inputEl.value?.focus();
	}
	function createBindings(isInput) {
		const base = {
			...labelledByProps.value,
			...createHandlers(isInput),
			id: inputId,
			[isInput ? "checked" : "aria-checked"]: checked.value,
			[isInput ? "required" : "aria-required"]: (group ? group.required : toValue(props.required)) || void 0,
			[isInput ? "readonly" : "aria-readonly"]: isReadOnly() || void 0,
			[isInput ? "disabled" : "aria-disabled"]: isDisabled.value || void 0,
			...group ? {} : { ...accessibleErrorProps.value }
		};
		if (isInput) return {
			...base,
			type: "checkbox",
			name: group?.name || toValue(props.name),
			indeterminate: toValue(props.indeterminate) || false
		};
		return {
			...base,
			role: "checkbox",
			tabindex: isDisabled.value ? "-1" : "0"
		};
	}
	group?.useCheckboxRegistration({
		id: inputId,
		getElem: () => inputEl.value,
		isDisabled: () => isDisabled.value,
		isChecked: () => checked.value,
		setChecked: (force) => {
			if (!isMutable()) return false;
			focus();
			group?.toggleValue(getTrueValue(), force);
			nextTick(() => {
				if (isInputElement(inputEl.value)) setErrors(inputEl.value?.validationMessage ?? "");
			});
			return true;
		}
	});
	const inputProps = computed(() => withRefCapture(createBindings(isInputElement(inputEl.value)), inputEl, elementRef));
	function setChecked(force) {
		if (toValue(props.indeterminate)) return;
		if (group) {
			group?.toggleValue(getTrueValue(), force);
			return;
		}
		setValue(force ? getTrueValue() : getFalseValue());
	}
	function toggleValue(force) {
		if (toValue(props.indeterminate)) return;
		const shouldTrue = force ?? !checked.value;
		setValue(shouldTrue ? getTrueValue() : getFalseValue());
	}
	const isGrouped = !!group;
	if (process.env.NODE_ENV !== "production") registerField(field, "Checkbox");
	return exposeField({
		errorMessageProps,
		inputEl,
		inputProps,
		isChecked: checked,
		isGrouped,
		labelProps,
		toggle: toggleValue
	}, field);
}
function useCheckboxField(props) {
	const group = inject(CheckboxGroupKey, null);
	if (group) {
		const getTrueValue = createTrueValueGetter(props);
		return createGroupField(group, getTrueValue);
	}
	return useFormField({
		path: props.name,
		initialValue: toValue(props.modelValue),
		disabled: props.disabled,
		schema: props.schema
	});
}
function createGroupField(group, getTrueValue) {
	function setValue() {
		group.toggleValue(getTrueValue());
	}
	return {
		...group.field,
		errors: computed(() => []),
		errorMessage: computed(() => ""),
		displayError: () => void 0,
		setValue
	};
}
function createTrueValueGetter(props) {
	return () => toValue(props.trueValue) ?? toValue(props.value) ?? true;
}

//#endregion
//#region packages/core/src/useNumberField/useNumberField.ts
function useNumberField(_props, elementRef) {
	const props = normalizeProps(_props, ["schema"]);
	const inputId = useUniqId(FieldTypePrefixes.NumberField);
	const inputEl = elementRef || shallowRef();
	const { locale } = useLocale(props.locale);
	const parser = useNumberParser(locale, props.formatOptions);
	const field = useFormField({
		path: props.name,
		initialValue: toValue(props.modelValue) ?? fromNumberish(props.value),
		disabled: props.disabled,
		schema: props.schema
	});
	const formattedText = shallowRef("");
	const { validityDetails, updateValidity } = useInputValidity({
		inputEl,
		field,
		disableHtmlValidation: props.disableHtmlValidation
	});
	const { fieldValue, setValue, setTouched, errorMessage, isDisabled } = field;
	watch([
		locale,
		() => toValue(props.formatOptions),
		fieldValue
	], () => {
		if (Number.isNaN(fieldValue.value) || isEmpty(fieldValue.value)) {
			formattedText.value = "";
			return;
		}
		formattedText.value = parser.format(fieldValue.value);
	}, { immediate: true });
	const { labelProps, labelledByProps } = useLabel({
		for: inputId,
		label: props.label,
		targetRef: inputEl
	});
	const { descriptionProps, describedByProps } = createDescribedByProps({
		inputId,
		description: props.description
	});
	const { accessibleErrorProps, errorMessageProps } = useErrorMessage({
		inputId,
		errorMessage
	});
	const { incrementButtonProps, decrementButtonProps, increment, decrement, spinButtonProps, applyClamp } = useSpinButton({
		current: fieldValue,
		currentText: formattedText,
		step: props.step,
		min: props.min,
		max: props.max,
		readonly: props.readonly,
		disabled: () => isDisabled.value || toValue(props.readonly),
		incrementLabel: props.incrementLabel,
		decrementLabel: props.decrementLabel,
		orientation: "vertical",
		preventTabIndex: true,
		onChange: (value) => {
			setValue(value);
			setTouched(true);
			updateValidity();
		}
	});
	const handlers = {
		onBeforeinput: (event) => {
			const inputEvent = event;
			if (isNullOrUndefined(inputEvent.data)) return;
			const el = inputEvent.target;
			const nextValue = el.value.slice(0, el.selectionStart ?? void 0) + inputEvent.data + el.value.slice(el.selectionEnd ?? void 0);
			const isValid = parser.isValidNumberPart(nextValue);
			if (!isValid) {
				inputEvent.preventDefault();
				inputEvent.stopPropagation();
				return;
			}
		},
		onChange: (event) => {
			setValue(applyClamp(parser.parse(event.target.value)));
			nextTick(() => {
				if (inputEl.value && inputEl.value?.value !== formattedText.value) inputEl.value.value = formattedText.value;
			});
		},
		onBlur: () => {
			setTouched(true);
		}
	};
	const inputMode = computed(() => {
		const intlOpts = toValue(props.formatOptions);
		const step = fromNumberish(props.step) || 1;
		const hasDecimals = (intlOpts?.maximumFractionDigits ?? 0) > 0 || String(step).includes(".");
		if (hasDecimals) return "decimal";
		return "numeric";
	});
	const inputProps = computed(() => {
		return withRefCapture({
			...propsToValues(props, [
				"name",
				"placeholder",
				"required",
				"readonly"
			]),
			...labelledByProps.value,
			...describedByProps.value,
			...accessibleErrorProps.value,
			...handlers,
			onKeydown: spinButtonProps.value.onKeydown,
			id: inputId,
			inputmode: inputMode.value,
			value: formattedText.value,
			disabled: isDisabled.value ? true : void 0,
			max: toValue(props.max),
			min: toValue(props.min),
			type: "text",
			spellcheck: false
		}, inputEl, elementRef);
	});
	useEventListener(inputEl, "wheel", (e) => {
		if (e.deltaY > 0) {
			increment();
			return;
		}
		decrement();
	}, {
		disabled: () => isDisabled.value || toValue(props.disableWheel),
		passive: true
	});
	if (process.env.NODE_ENV !== "production") registerField(field, "Number");
	return exposeField({
		decrement,
		decrementButtonProps,
		descriptionProps,
		errorMessageProps,
		increment,
		incrementButtonProps,
		inputEl,
		inputProps,
		labelProps,
		validityDetails,
		formattedText
	}, field);
}

//#endregion
//#region packages/core/src/helpers/useKeyPressed/useKeyPressed.ts
function useKeyPressed(codes, disabled) {
	const isPressed = shallowRef(false);
	const predicate = isCallable(codes) ? codes : (e) => normalizeArrayable(codes).includes(e.code);
	function onKeydown(e) {
		if (predicate(e)) isPressed.value = true;
	}
	function onKeyup(e) {
		if (predicate(e)) isPressed.value = false;
	}
	if (!isSSR) {
		useEventListener(window, "keydown", (e) => {
			onKeydown(e);
		}, { disabled });
		useEventListener(window, "keyup", (e) => {
			const keyEvt = e;
			onKeyup(keyEvt);
		}, { disabled: () => !isPressed.value });
	}
	return isPressed;
}

//#endregion
//#region packages/core/src/utils/platform.ts
function isMac() {
	const macRE = /^Mac/i;
	if (isSSR) return false;
	let platform = navigator.platform;
	if ("userAgentData" in navigator) platform = navigator.userAgentData.platform;
	return macRE.test(platform);
}
function isFirefox() {
	return /Firefox/i.test(navigator.userAgent);
}

//#endregion
//#region packages/core/src/helpers/usePopoverController/usePopoverController.ts
function usePopoverController(popoverEl, opts) {
	const isOpen = shallowRef(false);
	watch(isOpen, (value) => {
		const el = popoverEl.value;
		if (!el || !el.popover || toValue(opts?.disabled)) return;
		if (value === el.matches(":popover-open")) return;
		if (value) {
			el.showPopover();
			return;
		}
		el.hidePopover();
	});
	useEventListener(popoverEl, "toggle", (e) => {
		const shouldBeOpen = e.newState === "open";
		if (isOpen.value === shouldBeOpen) return;
		isOpen.value = shouldBeOpen;
	}, { disabled: opts?.disabled });
	return { isOpen };
}

//#endregion
//#region packages/core/src/useListBox/basicOptionFinder.ts
const SEARCH_CLEAR_TIMEOUT = 500;
function useBasicOptionFinder(options) {
	let keysSoFar = "";
	let clearKeysTimeout = null;
	function findOption(key) {
		const lowerKey = key.toLowerCase();
		let startIdx = 0;
		if (!keysSoFar) {
			const focusedIdx = options.value.findIndex((o) => o.isFocused());
			startIdx = focusedIdx === -1 ? 0 : focusedIdx;
		}
		keysSoFar += lowerKey;
		scheduleClearKeys();
		let match = findWithinRange(startIdx + 1, options.value.length);
		if (!match) match = findWithinRange(0, startIdx);
		return match;
	}
	function findWithinRange(startIdx, endIdx) {
		for (let i = startIdx; i < endIdx; i++) {
			const option = options.value[i];
			if (option.getLabel().toLowerCase().startsWith(keysSoFar)) return option;
		}
		return null;
	}
	function handleKeydown(e) {
		if (e.key.length === 1) findOption(e.key)?.focus();
	}
	function scheduleClearKeys() {
		if (clearKeysTimeout) clearTimeout(clearKeysTimeout);
		clearKeysTimeout = window.setTimeout(clearKeys, SEARCH_CLEAR_TIMEOUT);
	}
	function clearKeys() {
		keysSoFar = "";
		clearKeysTimeout = null;
	}
	return {
		keysSoFar,
		handleKeydown,
		clearKeys
	};
}

//#endregion
//#region packages/core/src/useListBox/useListBox.ts
const ListManagerKey = Symbol("ListManagerKey");
function useListBox(_props, elementRef) {
	const props = normalizeProps(_props, ["isValueSelected", "handleToggleValue"]);
	const listBoxId = useUniqId(FieldTypePrefixes.ListBox);
	const listBoxEl = elementRef || ref();
	const renderedOptions = ref([]);
	const finder = useBasicOptionFinder(renderedOptions);
	const { isOpen } = usePopoverController(listBoxEl, { disabled: props.disabled });
	const isShiftPressed = useKeyPressed(["ShiftLeft", "ShiftRight"], () => !isOpen.value);
	const isMetaPressed = useKeyPressed(isMac() ? ["MetaLeft", "MetaRight"] : ["ControlLeft", "ControlRight"], () => !isOpen.value);
	const listManager = {
		useOptionRegistration(init) {
			const id = init.id;
			renderedOptions.value.push(init);
			onBeforeUnmount(() => {
				removeFirst(renderedOptions.value, (reg) => reg.id === id);
			});
			return init;
		},
		isMultiple() {
			return toValue(props.multiple) ?? false;
		},
		isValueSelected: props.isValueSelected,
		toggleValue: props.handleToggleValue,
		getFocusStrategy: () => toValue(props.focusStrategy) ?? "FOCUS_DOM",
		isPopupOpen: () => isOpen.value,
		isInViewport: (element) => isInViewport(element, listBoxEl.value)
	};
	provide(ListManagerKey, listManager);
	const handlers = { onKeydown(e) {
		if (toValue(props.disabled)) return;
		if (hasKeyCode(e, "ArrowDown")) {
			e.preventDefault();
			e.stopPropagation();
			focusNext();
			return;
		}
		if (hasKeyCode(e, "ArrowUp")) {
			e.preventDefault();
			e.stopPropagation();
			focusPrev();
			return;
		}
		if (hasKeyCode(e, "KeyA") && isMetaPressed.value) {
			e.preventDefault();
			e.stopPropagation();
			props.onToggleAll?.();
			return;
		}
		if (hasKeyCode(e, "Home") || hasKeyCode(e, "PageUp")) {
			e.preventDefault();
			e.stopPropagation();
			if (isShiftPressed.value) props.onToggleBefore?.();
			getSortedOptions().at(0)?.focus();
			return;
		}
		if (hasKeyCode(e, "End") || hasKeyCode(e, "PageDown")) {
			e.preventDefault();
			e.stopPropagation();
			if (isShiftPressed.value) props.onToggleAfter?.();
			getSortedOptions().at(-1)?.focus();
			return;
		}
		if (hasKeyCode(e, "Tab")) {
			isOpen.value = false;
			return;
		}
		finder.handleKeydown(e);
	} };
	function focusFirst() {
		findFocusedOption()?.unfocus();
		getSortedOptions().at(0)?.focus();
	}
	function focusLast() {
		findFocusedOption()?.unfocus();
		getSortedOptions().at(-1)?.focus();
	}
	function focusAndToggleIfShiftPressed(option) {
		if (listManager.getFocusStrategy() !== "FOCUS_DOM") findFocusedOption()?.unfocus();
		option.focus();
		if (isShiftPressed.value) option.toggleSelected();
	}
	function findFocusedOption() {
		return renderedOptions.value.find((o) => o.isFocused());
	}
	function getSortedOptions() {
		const domOptions = getDomOptions();
		return domOptions.map((opt) => opt._fwOption);
	}
	function getDomOptions() {
		return Array.from(listBoxEl.value?.querySelectorAll("[role=\"option\"]:not([hidden])") || []);
	}
	function findFocusedIdx() {
		const focusStrategy = listManager.getFocusStrategy();
		const domOptions = getDomOptions();
		const focusedOptionIdx = focusStrategy === "FOCUS_DOM" ? domOptions.findIndex((opt) => opt.tabIndex === 0) : domOptions.findIndex((opt) => opt.ariaSelected === "true");
		return focusedOptionIdx;
	}
	function focusNext() {
		const currentlyFocusedIdx = findFocusedIdx();
		if (currentlyFocusedIdx === -1) {
			focusFirst();
			return;
		}
		const sortedOptions = getSortedOptions();
		for (let i = currentlyFocusedIdx + 1; i < sortedOptions.length; i++) {
			const option = sortedOptions[i];
			if (option && !option.isDisabled()) {
				focusAndToggleIfShiftPressed(option);
				return;
			}
		}
	}
	function focusPrev() {
		const currentlyFocusedIdx = findFocusedIdx();
		const sortedOptions = getSortedOptions();
		if (currentlyFocusedIdx === -1) {
			focusFirst();
			return;
		}
		for (let i = currentlyFocusedIdx - 1; i >= 0; i--) {
			const option = sortedOptions[i];
			if (option && !option.isDisabled()) {
				focusAndToggleIfShiftPressed(option);
				return;
			}
		}
	}
	const listBoxProps = computed(() => {
		const isMultiple = toValue(props.multiple);
		const labeledBy = toValue(props.labeledBy);
		return withRefCapture({
			id: listBoxId,
			role: "listbox",
			"aria-label": labeledBy ? void 0 : toValue(props.label),
			"aria-labelledby": labeledBy ?? void 0,
			"aria-multiselectable": isMultiple ?? void 0,
			...handlers
		}, listBoxEl, elementRef);
	});
	watch(isOpen, async (value) => {
		if (!value || toValue(props.disabled) || !toValue(props.autofocusOnOpen)) {
			if (listManager.getFocusStrategy() !== "FOCUS_ATTR_SELECTED") findFocusedOption()?.unfocus();
			return;
		}
		await nextTick();
		const currentlySelected = renderedOptions.value.find((o) => o.isSelected());
		if (currentlySelected && !currentlySelected?.isDisabled()) {
			currentlySelected.focus();
			return;
		}
		focusNext();
	});
	function mapOption(opt) {
		return {
			id: opt.id,
			value: opt.getValue(),
			label: opt.getLabel()
		};
	}
	const selectedOption = computed(() => {
		const opt = renderedOptions.value.find((opt$1) => opt$1.isSelected());
		return opt ? mapOption(opt) : void 0;
	});
	const selectedOptions = computed(() => {
		return renderedOptions.value.filter((opt) => opt.isSelected()).map((opt) => mapOption(opt));
	});
	const isEmpty$1 = computed(() => {
		return !renderedOptions.value.length || renderedOptions.value.every((opt) => opt.isHidden());
	});
	return {
		listBoxId,
		listBoxProps,
		isPopupOpen: isOpen,
		renderedOptions,
		isShiftPressed,
		listBoxEl,
		selectedOption,
		selectedOptions,
		isEmpty: isEmpty$1,
		focusNext,
		focusPrev,
		focusFirst,
		focusLast,
		findFocusedOption
	};
}

//#endregion
//#region packages/core/src/useOption/useOption.ts
function useOption(_props, elementRef) {
	const props = normalizeProps(_props);
	const optionEl = elementRef || ref();
	const isFocused = shallowRef(false);
	const isDisabled = createDisabledContext(props.disabled);
	const isHidden = shallowRef(false);
	const listManager = inject(ListManagerKey, null);
	if (!listManager) warn("An option component must exist within a ListBox Context. Did you forget to call `useSelect` / `useListBox` / `useComboBox` in a parent component?");
	function getValue() {
		return toValue(props.value);
	}
	const isSelected = computed(() => listManager?.isValueSelected(getValue()) ?? false);
	const optionId = useUniqId(FieldTypePrefixes.Option);
	function unfocus() {
		isFocused.value = false;
	}
	const reg = listManager?.useOptionRegistration({
		id: optionId,
		toggleSelected,
		isDisabled: () => isDisabled.value,
		isSelected: () => isSelected.value,
		isFocused: () => isFocused.value,
		getLabel: () => toValue(props.label) ?? "",
		getValue,
		isHidden: () => isHidden.value,
		setHidden: (value) => {
			isHidden.value = value;
			if (value) unfocus();
		},
		unfocus,
		focus: () => {
			isFocused.value = true;
			nextTick(() => {
				if (listManager?.getFocusStrategy() === "FOCUS_DOM") {
					optionEl.value?.focus();
					return;
				}
				if (!listManager?.isInViewport(optionEl.value)) optionEl.value?.scrollIntoView();
			});
		}
	});
	function toggleSelected() {
		listManager?.toggleValue(getValue());
	}
	const handlers = {
		onClick() {
			if (isDisabled.value) return;
			listManager?.toggleValue(getValue());
		},
		onKeydown(e) {
			if (isDisabled.value) return;
			if (hasKeyCode(e, "Space") || hasKeyCode(e, "Enter")) {
				e.preventDefault();
				e.stopPropagation();
				toggleSelected();
			}
		},
		onBlur() {
			isFocused.value = false;
		}
	};
	const optionProps = computed(() => {
		const isMultiple = listManager?.isMultiple() ?? false;
		const focusStrategy = listManager?.getFocusStrategy();
		const isVirtuallyFocused = focusStrategy === "FOCUS_ATTR_SELECTED" && isFocused.value && listManager?.isPopupOpen();
		const focusAttr = focusStrategy === "FOCUS_ATTR_SELECTED" ? "aria-selected" : void 0;
		let selectedAttr = isMultiple ? "aria-checked" : "aria-selected";
		const domProps = {
			id: optionId,
			role: "option",
			tabindex: isFocused.value && focusStrategy === "FOCUS_DOM" ? "0" : "-1",
			"aria-disabled": isDisabled.value || void 0,
			...handlers
		};
		if (focusAttr) {
			domProps[focusAttr] = isVirtuallyFocused || void 0;
			selectedAttr = "aria-checked";
		}
		if (isHidden.value) {
			domProps.hidden = true;
			domProps["aria-hidden"] = true;
			domProps.style = { display: "none" };
		}
		domProps[selectedAttr] = isSelected.value;
		return withRefCapture(domProps, optionEl, elementRef);
	});
	watch(optionEl, () => {
		if (optionEl.value) optionEl.value._fwOption = reg;
	});
	return {
		optionProps,
		isSelected,
		optionEl,
		isDisabled
	};
}

//#endregion
//#region packages/core/src/useOptionGroup/useOptionGroup.ts
function useOptionGroup(_props, elementRef) {
	const groupEl = elementRef || ref();
	const props = normalizeProps(_props);
	const groupId = useUniqId(FieldTypePrefixes.OptionGroup);
	const isDisabled = createDisabledContext(props.disabled);
	const { labelProps, labelledByProps } = useLabel({
		label: props.label,
		for: groupId,
		targetRef: groupEl
	});
	const groupProps = computed(() => {
		return withRefCapture({
			id: groupId,
			role: "group",
			...labelledByProps.value
		}, groupEl, elementRef);
	});
	return {
		labelProps,
		groupProps,
		groupEl,
		isDisabled
	};
}

//#endregion
//#region packages/core/src/collections/filter.ts
function useDefaultFilter(options = {}) {
	const { caseSensitive = false, debounceMs = 0 } = options;
	const withCaseSensitive = caseSensitive ? (value) => value : (value) => value.toLowerCase();
	const contains = ({ search, option }) => {
		return withCaseSensitive(option.label).includes(withCaseSensitive(search));
	};
	const startsWith = ({ search, option }) => {
		return withCaseSensitive(option.label).startsWith(withCaseSensitive(search));
	};
	const endsWith = ({ search, option }) => {
		return withCaseSensitive(option.label).endsWith(withCaseSensitive(search));
	};
	const equals = ({ search, option }) => {
		return withCaseSensitive(option.label) === withCaseSensitive(search);
	};
	contains.debounceMs = debounceMs;
	startsWith.debounceMs = debounceMs;
	endsWith.debounceMs = debounceMs;
	equals.debounceMs = debounceMs;
	return {
		contains,
		startsWith,
		endsWith,
		equals
	};
}

//#endregion
//#region packages/core/src/useSelect/useSelect.ts
const MENU_OPEN_KEYS = [
	"Enter",
	"Space",
	"ArrowDown",
	"ArrowUp"
];
function useSelect(_props) {
	const inputId = useUniqId(FieldTypePrefixes.Select);
	const props = normalizeProps(_props, ["schema"]);
	const field = useFormField({
		path: props.name,
		initialValue: toValue(props.modelValue) ?? toValue(props.value),
		disabled: props.disabled,
		schema: props.schema
	});
	const triggerEl = ref();
	const { fieldValue, setValue, errorMessage, isDisabled } = field;
	const isMutable = () => !isDisabled.value && !toValue(props.readonly);
	const { labelProps, labelledByProps } = useLabel({
		label: props.label,
		for: inputId
	});
	let lastRecentlySelectedOption;
	const { listBoxProps, isPopupOpen, renderedOptions, isShiftPressed, listBoxEl, selectedOption, selectedOptions, listBoxId, findFocusedOption } = useListBox({
		labeledBy: () => labelledByProps.value["aria-labelledby"],
		disabled: isDisabled,
		autofocusOnOpen: true,
		isValueSelected,
		handleToggleValue: toggleValue,
		label: props.label,
		multiple: props.multiple,
		orientation: props.orientation,
		onToggleAll: toggleAll,
		onToggleBefore: toggleBefore,
		onToggleAfter: toggleAfter
	});
	const { element: inputEl } = useConstraintsValidator({
		type: "select",
		required: props.required,
		value: fieldValue,
		source: triggerEl
	});
	const { updateValidity } = useInputValidity({
		field,
		inputEl
	});
	const { descriptionProps, describedByProps } = createDescribedByProps({
		inputId,
		description: props.description
	});
	const { accessibleErrorProps, errorMessageProps } = useErrorMessage({
		inputId,
		errorMessage
	});
	function isSingle() {
		const isMultiple = toValue(props.multiple);
		return !isMultiple;
	}
	function isValueSelected(value) {
		const values = normalizeArrayable(fieldValue.value ?? []);
		return values.some((item) => isEqual(item, value));
	}
	function toggleValue(optionValue, force) {
		if (!isMutable()) return;
		if (isSingle()) {
			lastRecentlySelectedOption = optionValue;
			setValue(optionValue);
			updateValidity();
			isPopupOpen.value = false;
			return;
		}
		if (!isShiftPressed.value) {
			lastRecentlySelectedOption = optionValue;
			const nextValue = toggleValueSelection(fieldValue.value ?? [], optionValue, force);
			setValue(nextValue);
			updateValidity();
			return;
		}
		let lastRecentIdx = renderedOptions.value.findIndex((opt) => isEqual(opt.getValue(), lastRecentlySelectedOption));
		const targetIdx = renderedOptions.value.findIndex((opt) => isEqual(opt.getValue(), optionValue));
		if (targetIdx === -1) return;
		lastRecentIdx = lastRecentIdx === -1 ? 0 : lastRecentIdx;
		const startIdx = Math.min(lastRecentIdx, targetIdx);
		const endIdx = Math.min(Math.max(lastRecentIdx, targetIdx), renderedOptions.value.length - 1);
		selectRange(startIdx, endIdx);
	}
	function selectRange(start, end) {
		const nextValue = renderedOptions.value.slice(start, end + 1).map((opt) => opt.getValue());
		setValue(nextValue);
		updateValidity();
	}
	function toggleBefore() {
		if (isSingle() || !isMutable()) return;
		const focusedIdx = renderedOptions.value.findIndex((opt) => opt.isFocused());
		if (focusedIdx < 0) return;
		const startIdx = 0;
		const endIdx = Math.min(focusedIdx, renderedOptions.value.length - 1);
		selectRange(startIdx, endIdx);
	}
	function toggleAfter() {
		if (isSingle() || !isMutable()) return;
		const focusedIdx = renderedOptions.value.findIndex((opt) => opt.isFocused());
		const startIdx = Math.max(0, focusedIdx);
		const endIdx = renderedOptions.value.length - 1;
		selectRange(startIdx, endIdx);
	}
	function toggleAll() {
		if (isSingle() || !isMutable()) return;
		const isAllSelected = renderedOptions.value.every((opt) => opt.isSelected());
		if (isAllSelected) {
			setValue([]);
			updateValidity();
			return;
		}
		setValue(renderedOptions.value.map((opt) => opt.getValue()));
		updateValidity();
	}
	const handlers = {
		onClick() {
			if (isDisabled.value) return;
			isPopupOpen.value = !isPopupOpen.value;
		},
		onKeydown(e) {
			if (isDisabled.value) return;
			if (!isPopupOpen.value && MENU_OPEN_KEYS.includes(e.code)) {
				e.preventDefault();
				isPopupOpen.value = true;
				return;
			}
		}
	};
	const triggerProps = computed(() => {
		return withRefCapture({
			...labelledByProps.value,
			...describedByProps.value,
			...accessibleErrorProps.value,
			id: inputId,
			tabindex: isDisabled.value ? "-1" : "0",
			type: triggerEl.value?.tagName === "BUTTON" ? "button" : void 0,
			role: "combobox",
			"aria-haspopup": "listbox",
			"aria-expanded": isPopupOpen.value,
			"aria-disabled": isDisabled.value || void 0,
			"aria-activedescendant": findFocusedOption()?.id ?? void 0,
			"aria-controls": listBoxId,
			...handlers
		}, triggerEl);
	});
	if (process.env.NODE_ENV !== "production") registerField(field, "Select");
	return exposeField({
		isPopupOpen,
		triggerProps,
		labelProps,
		listBoxProps,
		errorMessageProps,
		descriptionProps,
		listBoxEl,
		selectedOption,
		selectedOptions
	}, field);
}

//#endregion
//#region packages/core/src/helpers/useControlButtonProps/useControlButtonProps.ts
function useControlButtonProps(props) {
	const buttonEl = shallowRef();
	const buttonProps = computed(() => {
		const isBtn = isButtonElement(buttonEl.value);
		const { disabled,...rest } = props();
		return withRefCapture({
			type: isBtn ? "button" : void 0,
			role: isBtn ? void 0 : "button",
			[isBtn ? "disabled" : "aria-disabled"]: disabled || void 0,
			tabindex: "-1",
			...rest
		}, buttonEl);
	});
	return buttonProps;
}

//#endregion
//#region packages/core/src/useComboBox/useComboBox.ts
function useComboBox(_props, collectionOptions) {
	const props = normalizeProps(_props, ["schema", "onNewValue"]);
	const inputEl = ref();
	const buttonEl = ref();
	const inputValue = ref("");
	const inputId = useUniqId(FieldTypePrefixes.ComboBox);
	const isReadOnly = () => toValue(props.readonly);
	const field = useFormField({
		path: props.name,
		initialValue: toValue(props.modelValue) ?? toValue(props.value),
		disabled: props.disabled,
		schema: props.schema
	});
	const { fieldValue, setValue, errorMessage, isDisabled, setTouched } = field;
	const { labelProps, labelledByProps } = useLabel({
		label: props.label,
		for: inputId,
		targetRef: inputEl
	});
	const { validityDetails } = useInputValidity({
		field,
		inputEl,
		disableHtmlValidation: props.disableHtmlValidation
	});
	const { descriptionProps, describedByProps } = createDescribedByProps({
		inputId,
		description: props.description
	});
	const { accessibleErrorProps, errorMessageProps } = useErrorMessage({
		inputId,
		errorMessage
	});
	const { listBoxId, listBoxProps, isPopupOpen, listBoxEl, selectedOption, focusNext, focusPrev, findFocusedOption, renderedOptions, isEmpty: isEmpty$1, focusFirst: focusFirstOption, focusLast: focusLastOption } = useListBox({
		labeledBy: () => labelledByProps.value["aria-labelledby"],
		focusStrategy: "FOCUS_ATTR_SELECTED",
		disabled: isDisabled,
		label: props.label,
		multiple: false,
		orientation: props.orientation,
		isValueSelected: (value) => {
			return isEqual(fieldValue.value, value);
		},
		handleToggleValue: (value) => {
			if (isReadOnly()) return;
			setValue(value);
			inputValue.value = selectedOption.value?.label ?? "";
			isPopupOpen.value = false;
		}
	});
	const handlers = {
		onInput(evt) {
			inputValue.value = evt.target.value;
		},
		async onBlur(evt) {
			setTouched(true);
			if (isReadOnly()) return;
			let relatedTarget = evt.relatedTarget;
			if (relatedTarget) {
				relatedTarget = relatedTarget.closest("[role=\"option\"]");
				const opt = renderedOptions.value.find((opt$1) => opt$1.id === relatedTarget?.id);
				if (opt) {
					setValue(opt.getValue());
					inputValue.value = opt.getLabel() ?? "";
					isPopupOpen.value = false;
				}
				return;
			}
			findClosestOptionAndSetValue(inputValue.value, false);
		},
		onKeydown(evt) {
			if (isDisabled.value) return;
			if (hasKeyCode(evt, "Escape")) {
				evt.preventDefault();
				if (!isPopupOpen.value) inputValue.value = "";
				else isPopupOpen.value = false;
				return;
			}
			if (hasKeyCode(evt, "Enter")) {
				if (isPopupOpen.value && !isReadOnly()) {
					evt.preventDefault();
					const option = findFocusedOption();
					if (option) {
						option.toggleSelected();
						return;
					}
					if (inputValue.value) findClosestOptionAndSetValue(inputValue.value);
					return;
				}
				return;
			}
			if (["ArrowDown", "ArrowUp"].includes(evt.code)) {
				evt.preventDefault();
				if (!isPopupOpen.value) {
					isPopupOpen.value = true;
					return;
				}
				hasKeyCode(evt, "ArrowDown") ? focusNext() : focusPrev();
				return;
			}
			if (hasKeyCode(evt, "End")) {
				focusLastOption();
				return;
			}
			if (hasKeyCode(evt, "Home")) {
				focusFirstOption();
				return;
			}
			if (hasKeyCode(evt, "Tab")) {
				isPopupOpen.value = false;
				findClosestOptionAndSetValue(inputValue.value);
				return;
			}
			if (!isPopupOpen.value) isPopupOpen.value = true;
		},
		onFocus() {
			if (toValue(props.openOnFocus)) isPopupOpen.value = true;
		}
	};
	function findClosestOptionAndSetValue(search, addNew = true) {
		let item = renderedOptions.value.find((i) => i?.getLabel() === search);
		if (!item) item = renderedOptions.value.find((i) => i?.getLabel() === search.trim());
		if (props.onNewValue && addNew && !isReadOnly()) {
			const newOptionValue = props.onNewValue(inputValue.value);
			if (newOptionValue) {
				setValue(newOptionValue.value);
				inputValue.value = newOptionValue.label;
				return;
			}
		}
		if (!item) item = renderedOptions.value.find((i) => isEqual(i?.getValue(), fieldValue.value));
		if (item) {
			inputValue.value = item?.getLabel() ?? "";
			setValue(item?.getValue());
			return;
		}
	}
	/**
	* Handles the click event on the button element.
	*/
	function onButtonClick() {
		if (isDisabled.value) return;
		isPopupOpen.value = !isPopupOpen.value;
	}
	const buttonProps = useControlButtonProps(() => ({
		id: `${inputId}-btn`,
		disabled: isDisabled.value,
		type: "button",
		"aria-haspopup": "listbox",
		"aria-expanded": isPopupOpen.value,
		"aria-activedescendant": findFocusedOption()?.id ?? void 0,
		"aria-controls": listBoxId,
		onClick: onButtonClick
	}));
	const inputProps = computed(() => {
		return withRefCapture({
			id: inputId,
			type: "text",
			role: "combobox",
			"aria-haspopup": "listbox",
			"aria-controls": listBoxId,
			"aria-expanded": isPopupOpen.value ? "true" : "false",
			"aria-activedescendant": findFocusedOption()?.id ?? void 0,
			disabled: isDisabled.value ? true : void 0,
			value: inputValue.value,
			...propsToValues(props, [
				"name",
				"placeholder",
				"required",
				"readonly"
			]),
			...accessibleErrorProps.value,
			...describedByProps.value,
			...handlers
		}, inputEl);
	});
	const filter = collectionOptions?.filter;
	if (filter) {
		function updateHiddenState(textValue) {
			if (!filter) return;
			renderedOptions.value.forEach((opt) => {
				opt.setHidden(!filter({
					option: {
						value: opt.getValue(),
						label: opt.getLabel()
					},
					search: textValue
				}));
			});
		}
		watch(inputValue, debounce(filter.debounceMs, updateHiddenState));
	}
	if (process.env.NODE_ENV !== "production") registerField(field, "ComboBox");
	return exposeField({
		labelProps,
		inputProps,
		inputEl,
		errorMessageProps,
		listBoxProps,
		isPopupOpen,
		listBoxEl,
		descriptionProps,
		validityDetails,
		buttonEl,
		buttonProps,
		inputValue,
		selectedOption,
		isListEmpty: isEmpty$1
	}, field);
}

//#endregion
//#region packages/core/src/useHiddenField/useHiddenField.ts
function useHiddenField(_props) {
	const props = normalizeProps(_props);
	const field = useFormField({
		disabled: props.disabled,
		path: props.name,
		initialValue: toValue(props.value)
	});
	useInputValidity({ field });
	watch(() => toValue(props.value), (value) => {
		field.setValue(value);
	});
	if (process.env.NODE_ENV !== "production") registerField(field, "Hidden");
	return exposeField({}, field);
}

//#endregion
//#region packages/core/src/useHiddenField/HiddenField.ts
/**
* A helper component that renders a hidden field. You can build your own with `useHiddenField`.
*/
const HiddenField = /* @__PURE__ */ defineComponent({
	name: "HiddenField",
	props: {
		name: String,
		value: null,
		disabled: Boolean
	},
	setup(props) {
		useHiddenField(props);
		return () => [];
	}
});

//#endregion
//#region packages/core/src/useCustomField/useCustomField.ts
function useCustomField(_props, elementRef) {
	const props = normalizeProps(_props, ["schema"]);
	const controlId = useUniqId(FieldTypePrefixes.CustomField);
	const controlEl = elementRef || shallowRef();
	const field = useFormField({
		path: props.name,
		initialValue: toValue(props.modelValue) ?? toValue(props.value),
		disabled: props.disabled,
		schema: props.schema
	});
	const { errorMessage, isDisabled } = field;
	const { updateValidity } = useInputValidity({ field });
	const { labelProps, labelledByProps } = useLabel({
		for: controlId,
		label: props.label,
		targetRef: controlEl
	});
	const { descriptionProps, describedByProps } = createDescribedByProps({
		inputId: controlId,
		description: props.description
	});
	const { accessibleErrorProps, errorMessageProps } = useErrorMessage({
		inputId: controlId,
		errorMessage
	});
	const controlProps = computed(() => withRefCapture({
		...propsToValues(props, ["name", "readonly"]),
		...labelledByProps.value,
		...describedByProps.value,
		...accessibleErrorProps.value,
		"aria-readonly": toValue(props.readonly) ? "true" : void 0,
		"aria-disabled": isDisabled.value ? "true" : void 0,
		id: controlId
	}, controlEl, elementRef));
	if (process.env.NODE_ENV !== "production") registerField(field, "Custom");
	return exposeField({
		labelProps,
		descriptionProps,
		errorMessageProps,
		controlProps,
		validate: updateValidity
	}, field);
}

//#endregion
//#region packages/core/src/useOtpField/types.ts
const OtpContextKey = Symbol("otp-context");

//#endregion
//#region packages/core/src/useOtpField/utils.ts
const acceptMapRegex = {
	all: /./,
	numeric: /^\d+$/,
	alphanumeric: /^[a-zA-Z0-9]+$/
};
function isValueAccepted(value, accept) {
	const regex = acceptMapRegex[accept];
	return regex.test(value);
}
const DEFAULT_MASK = "•";

//#endregion
//#region packages/core/src/useOtpField/useOtpField.ts
function useOtpField(_props) {
	const props = normalizeProps(_props, ["schema", "onCompleted"]);
	const controlEl = ref();
	const id = useUniqId(FieldTypePrefixes.OTPField);
	function withPrefix(value) {
		const prefix = toValue(props.prefix);
		if (!prefix) return value ?? "";
		value = value ?? "";
		if (value.startsWith(prefix)) return value;
		return `${prefix}${value}`;
	}
	function getRequiredLength() {
		const prefix = toValue(props.prefix) || "";
		const length = getLength();
		return prefix.length + length;
	}
	const field = useFormField({
		path: props.name,
		initialValue: withPrefix(toValue(props.modelValue) ?? toValue(props.value)),
		disabled: props.disabled,
		schema: props.schema
	});
	function getLength() {
		const propLength = toValue(props.length);
		if (propLength) return propLength;
		const prefix = toValue(props.prefix);
		return prefix ? 4 : 6;
	}
	const inputsState = ref(withPrefix(toValue(props.modelValue) ?? toValue(props.value)).split(""));
	const { element: inputEl } = useConstraintsValidator({
		type: "text",
		maxLength: getRequiredLength(),
		minLength: getRequiredLength(),
		required: props.required,
		value: () => field.fieldValue.value?.length === getRequiredLength() ? field.fieldValue.value : "",
		source: controlEl
	});
	const { validityDetails } = useInputValidity({
		inputEl,
		field,
		disableHtmlValidation: props.disableHtmlValidation
	});
	const { descriptionProps, describedByProps } = createDescribedByProps({
		inputId: id,
		description: props.description
	});
	const { labelProps, labelledByProps } = useLabel({
		label: props.label,
		targetRef: controlEl,
		for: id
	});
	const { errorMessageProps, accessibleErrorProps } = useErrorMessage({
		inputId: id,
		errorMessage: field.errorMessage
	});
	const controlProps = computed(() => {
		return withRefCapture({
			id,
			role: "group",
			...labelledByProps.value,
			...describedByProps.value,
			...accessibleErrorProps.value
		}, controlEl);
	});
	function createFocusHandler(direction) {
		return () => {
			if (!controlEl.value) return;
			const slots = Array.from(controlEl.value.querySelectorAll("[data-otp-slot][tabindex=\"0\"]"));
			const currentSlot = controlEl.value.querySelector("[data-otp-slot]:focus");
			if (!currentSlot) {
				slots[0]?.focus();
				return;
			}
			const currentIndex = slots.indexOf(currentSlot);
			if (currentIndex === -1) {
				slots[0]?.focus();
				return;
			}
			const nextSlot = slots[currentIndex + (direction === "next" ? 1 : -1)];
			nextSlot?.focus();
		};
	}
	const focusNext = createFocusHandler("next");
	const focusPrevious = createFocusHandler("previous");
	const fieldSlots = computed(() => {
		const prefix = toValue(props.prefix) || "";
		const length = prefix.length + getLength();
		return Array.from({ length }, (_, index) => ({
			value: inputsState.value[index] ?? "",
			disabled: prefix.length ? prefix.length > index : field.isDisabled.value,
			readonly: toValue(props.readonly),
			accept: toValue(props.accept),
			masked: prefix.length <= index && !!toValue(props.mask)
		}));
	});
	function getActiveSlotIndex(event) {
		const currentSlot = event.target.closest("[data-otp-slot]");
		const slots = Array.from(controlEl.value?.querySelectorAll("[data-otp-slot]") ?? []);
		if (!currentSlot) return -1;
		return slots.indexOf(currentSlot);
	}
	function focusIndex(index) {
		const slots = Array.from(controlEl.value?.querySelectorAll("[data-otp-slot]") ?? []);
		slots[index]?.focus();
	}
	watch(field.fieldValue, (value) => {
		if (!value) {
			inputsState.value = withPrefix("").split("");
			return;
		}
		const expected = withPrefix(inputsState.value.join(""));
		if (expected === value) return;
		inputsState.value = value.split("");
	});
	function onPaste(event) {
		if (toValue(props.readonly) || field.isDisabled.value) {
			blockEvent(event);
			return;
		}
		const text = event.clipboardData?.getData("text/plain") || "";
		blockEvent(event);
		fillSlots(text, event);
	}
	function fillSlots(text, event) {
		text = text.trim();
		if (!text.length) {
			const currentIndex$1 = getActiveSlotIndex(event);
			if (currentIndex$1 === -1) return;
			inputsState.value[currentIndex$1] = "";
			updateFieldValue();
			return;
		}
		if (!isValueAccepted(text, toValue(props.accept) || "alphanumeric")) return;
		const prefixed = withPrefix(text).split("");
		if (prefixed.length === getRequiredLength()) {
			prefixed.forEach((value, index) => {
				inputsState.value[index] = value;
			});
			focusIndex(getRequiredLength() - 1);
			updateFieldValue();
			return;
		}
		const currentIndex = getActiveSlotIndex(event);
		if (currentIndex === -1) return;
		const prefixLength = (toValue(props.prefix) || "").length;
		const maxLength = getRequiredLength();
		const availableSlots = maxLength - currentIndex;
		const textToFill = text.slice(0, availableSlots);
		if (currentIndex >= prefixLength) for (let i = 0; i < textToFill.length; i++) {
			const char = textToFill[i];
			inputsState.value[currentIndex + i] = char;
		}
		focusIndex(Math.min(currentIndex + textToFill.length, getRequiredLength() - 1));
		updateFieldValue();
	}
	function updateFieldValue() {
		const nextValue = inputsState.value.join("");
		const isCompleted = nextValue?.length === getRequiredLength();
		field.setValue(nextValue);
		if (isCompleted) nextTick(() => {
			props.onCompleted?.(nextValue);
		});
	}
	let registeredSlots = 0;
	provide(OtpContextKey, {
		getMaskCharacter: () => {
			const mask = toValue(props.mask);
			return typeof mask === "string" ? mask[0] : DEFAULT_MASK;
		},
		useSlotRegistration() {
			const slotId = useUniqId(FieldTypePrefixes.OTPSlot);
			const index = registeredSlots++;
			return {
				id: slotId,
				focusNext,
				focusPrevious,
				isLast() {
					return index === getRequiredLength() - 1;
				},
				handlePaste: onPaste,
				setValue: fillSlots
			};
		},
		onBlur() {
			field.setTouched(true);
		}
	});
	if (process.env.NODE_ENV !== "production") registerField(field, "OTP");
	return exposeField({
		controlProps,
		labelProps,
		descriptionProps,
		validityDetails,
		fieldSlots,
		errorMessageProps
	}, field);
}

//#endregion
//#region packages/core/src/useOtpField/useOtpSlot.ts
function useOtpSlot(_props) {
	const props = normalizeProps(_props);
	const slotEl = ref();
	const isDisabled = createDisabledContext(props.disabled);
	const context = inject(OtpContextKey, null);
	const registration = context?.useSlotRegistration();
	if (!context) {
		if (process.env.NODE_ENV !== "production") warn("OtpSlot must be used within an OtpField");
	}
	function withMask(value) {
		if (!toValue(props.masked) || !value) return value ?? "";
		return context?.getMaskCharacter().repeat(value.length) ?? "";
	}
	function setElementValue(value) {
		if (!slotEl.value) return;
		if (isInputElement(slotEl.value)) {
			slotEl.value.value = value;
			return;
		}
		slotEl.value.textContent = value;
	}
	const handlers = {
		onPaste(e) {
			registration?.handlePaste(e);
		},
		onBlur() {
			context?.onBlur();
		},
		onKeydown(e) {
			if (hasKeyCode(e, "Backspace") || hasKeyCode(e, "Delete")) {
				blockEvent(e);
				setElementValue("");
				registration?.setValue("", e);
				registration?.focusPrevious();
				return;
			}
			if (hasKeyCode(e, "Enter")) {
				blockEvent(e);
				registration?.focusNext();
				return;
			}
			if (hasKeyCode(e, "ArrowLeft")) {
				blockEvent(e);
				registration?.focusPrevious();
				return;
			}
			if (hasKeyCode(e, "ArrowRight")) {
				blockEvent(e);
				registration?.focusNext();
				return;
			}
		},
		onBeforeinput(e) {
			if (!e.data) return;
			blockEvent(e);
			const text = e.data.trim();
			if (toValue(props.readonly) || isDisabled.value || !text) return;
			if (isValueAccepted(text, toValue(props.accept) || "alphanumeric")) setTimeout(() => {
				setElementValue(text);
				registration?.setValue(text, e);
			}, 0);
		},
		onChange(e) {
			if (!slotEl.value) return;
			if (isInputElement(slotEl.value)) {
				setElementValue(slotEl.value.value);
				registration?.setValue(slotEl.value.value, e);
				return;
			}
			setElementValue(slotEl.value.textContent ?? "");
			registration?.setValue(slotEl.value.textContent ?? "", e);
		}
	};
	const slotProps = computed(() => {
		const isInput = isInputElement(slotEl.value);
		const baseProps = {
			[isInput ? "readonly" : "aria-readonly"]: toValue(props.readonly),
			[isInput ? "disabled" : "aria-disabled"]: toValue(props.disabled),
			"data-otp-slot": true,
			spellcheck: false,
			tabindex: isDisabled.value ? "-1" : "0",
			autocorrect: "off",
			autocomplete: "one-time-code",
			autocapitalize: "off",
			enterkeyhint: registration?.isLast() ? "done" : "next",
			...handlers
		};
		if (toValue(props.accept) === "numeric") baseProps.inputmode = "numeric";
		if (!isInput) {
			baseProps.role = "textbox";
			baseProps["aria-label"] = toValue(props.value) || "Enter a character";
			baseProps["aria-multiline"] = "false";
			baseProps.contenteditable = isDisabled.value ? "false" : isFirefox() ? "true" : "plaintext-only";
		} else {
			baseProps.value = toValue(props.value);
			baseProps.type = toValue(props.masked) ? "password" : "text";
		}
		return withRefCapture(baseProps, slotEl);
	});
	return {
		slotProps,
		key: registration?.id ?? useId(),
		value: computed(() => withMask(toValue(props.value)))
	};
}
/**
* A helper component that renders an OTP slot. You can build your own with `useOtpSlot`.
*/
const OtpSlot = /* @__PURE__ */ defineComponent({
	name: "OtpSlot",
	props: [
		"value",
		"disabled",
		"readonly",
		"accept",
		"masked",
		"as"
	],
	setup(props) {
		const { slotProps, value, key } = useOtpSlot(props);
		return () => h(props.as || "input", {
			...slotProps.value,
			key
		}, value.value);
	}
});

//#endregion
//#region packages/core/src/useDateTime/constants.ts
function isEditableSegmentType(type) {
	return ![
		"era",
		"timeZoneName",
		"literal"
	].includes(type);
}
function isOptionalSegmentType(type) {
	const optionalTypes = [
		"dayPeriod",
		"weekday",
		"era"
	];
	return optionalTypes.includes(type);
}
function segmentTypeToDurationLike(type) {
	const map = {
		year: "years",
		month: "months",
		day: "days",
		hour: "hours",
		minute: "minutes",
		second: "seconds",
		dayPeriod: "hours",
		weekday: "days"
	};
	return map[type];
}
function getSegmentTypePlaceholder(type) {
	const map = {
		year: "YYYY",
		month: "MM",
		day: "DD",
		hour: "--",
		minute: "--",
		second: "--",
		dayPeriod: "AM",
		weekday: "ddd"
	};
	return map[type];
}
function isNumericByDefault(type) {
	const map = {
		year: true,
		month: true,
		day: true,
		hour: true,
		minute: true,
		second: true
	};
	return map[type] ?? false;
}
function getOrderedSegmentTypes() {
	return [
		"year",
		"month",
		"day",
		"hour",
		"minute",
		"second"
	];
}
function isEqualPart(min, max, part) {
	const editablePart = part;
	const parts = getOrderedSegmentTypes();
	const idx = parts.indexOf(editablePart);
	if (idx === -1) return false;
	return parts.slice(0, idx).every((p) => min[p] === max[p]) && min[editablePart] === max[editablePart];
}

//#endregion
//#region packages/core/src/useDateTime/temporalPartial.ts
function createTemporalPartial(calendar, timeZone, min, max) {
	if (min && max) {
		const diff = Math.round(max.compare(min) / 2);
		const zonedDateTime$1 = min.add({ milliseconds: diff }).set({
			hour: 0,
			minute: 0,
			second: 0,
			millisecond: 0
		});
		zonedDateTime$1["~fw_temporal_partial"] = {};
		const parts = getOrderedSegmentTypes();
		parts.forEach((part) => {
			zonedDateTime$1["~fw_temporal_partial"][part] = isEqualPart(min, max, part);
		});
		return zonedDateTime$1;
	}
	const zonedDateTime = toCalendar(now(timeZone), calendar).set({
		hour: 0,
		minute: 0,
		second: 0,
		millisecond: 0
	});
	zonedDateTime["~fw_temporal_partial"] = {};
	return zonedDateTime;
}
function toTemporalPartial(value, setParts) {
	const clone = value.copy();
	clone["~fw_temporal_partial"] = isTemporalPartial(value) ? value["~fw_temporal_partial"] : {};
	if (setParts) setParts.forEach((part) => {
		clone["~fw_temporal_partial"][part] = true;
	});
	return clone;
}
function isTemporalPartial(value) {
	return isObject(value["~fw_temporal_partial"]);
}
function isTemporalPartSet(value, part) {
	return part in value["~fw_temporal_partial"] && value["~fw_temporal_partial"][part] === true;
}

//#endregion
//#region packages/core/src/useDateTime/useDateTimeSegmentGroup.ts
const DateTimeSegmentGroupKey = Symbol("DateTimeSegmentGroupKey");
function useDateTimeSegmentGroup({ formatter, temporalValue, formatOptions, direction, locale, controlEl, readonly: readonly$1, min, max, onValueChange, onTouched, dispatchEvent }) {
	const renderedSegments = ref([]);
	const parser = useNumberParser(locale, {
		maximumFractionDigits: 0,
		useGrouping: false
	});
	const { setPart, addToPart } = useDateArithmetic({
		currentDate: temporalValue,
		min,
		max
	});
	const segments = computed(() => {
		const date = toValue(temporalValue);
		let parts = formatter.value.formatToParts(date.toDate());
		if (isTemporalPartial(date)) for (const part of parts) {
			if (!isEditableSegmentType(part.type)) continue;
			if (!isTemporalPartSet(date, part.type)) part.value = getSegmentTypePlaceholder(part.type) ?? part.value;
		}
		if (toValue(readonly$1)) parts = parts.map((part) => {
			return {
				...part,
				readonly: true
			};
		});
		return parts;
	});
	function getRequiredParts() {
		return segments.value.filter((part) => {
			return isEditableSegmentType(part.type) || isOptionalSegmentType(part.type);
		}).map((part) => part.type);
	}
	function isAllPartsSet(value) {
		return segments.value.every((part) => {
			if (!isEditableSegmentType(part.type) || isOptionalSegmentType(part.type)) return true;
			return isTemporalPartSet(value, part.type);
		});
	}
	function withAllPartsSet(value) {
		if (isTemporalPartial(value) && isAllPartsSet(value)) return value.copy();
		return value;
	}
	function onSegmentDone() {
		focusNextSegment();
	}
	function useDateSegmentRegistration(segment) {
		renderedSegments.value.push(segment);
		onBeforeUnmount(() => {
			renderedSegments.value = renderedSegments.value.filter((s) => s.id !== segment.id);
		});
		function increment() {
			const type = segment.getType();
			const date = addToPart(type, 1);
			onValueChange(withAllPartsSet(date));
		}
		function decrement() {
			const type = segment.getType();
			const date = addToPart(type, -1);
			onValueChange(withAllPartsSet(date));
		}
		function setValue(value) {
			const type = segment.getType();
			const date = setPart(type, value);
			onValueChange(withAllPartsSet(date));
		}
		function isLockedByRange() {
			const type = segment.getType();
			const minDate = toValue(min);
			const maxDate = toValue(max);
			if (!minDate || !maxDate) return false;
			return isEqualPart(minDate, maxDate, type);
		}
		function getMetadata() {
			const type = segment.getType();
			const date = toValue(temporalValue);
			const maxPartsRecord = {
				day: date.calendar.getDaysInMonth(date),
				month: date.calendar.getMonthsInYear(date),
				year: 9999,
				hour: toValue(formatOptions)?.hour12 ? 12 : 23,
				minute: 59,
				second: 59
			};
			const minPartsRecord = {
				day: 1,
				month: 1,
				year: 0,
				hour: 0,
				minute: 0,
				second: 0
			};
			const maxLengths = {
				day: 2,
				month: 2,
				year: 4,
				hour: 2,
				minute: 2,
				second: 2
			};
			return {
				min: minPartsRecord[type] ?? null,
				max: maxPartsRecord[type] ?? null,
				maxLength: maxLengths[type] ?? null
			};
		}
		function clear() {
			const type = segment.getType();
			const date = toValue(temporalValue);
			const next = toTemporalPartial(date, !isTemporalPartial(date) ? getRequiredParts() : []);
			next["~fw_temporal_partial"][type] = false;
			onValueChange(next);
		}
		function isNumeric() {
			const type = segment.getType();
			const options = toValue(formatOptions);
			if (type === "literal") return false;
			const optionFormat = options?.[type];
			if (!optionFormat) return isNumericByDefault(type);
			return optionFormat === "numeric" || optionFormat === "2-digit";
		}
		function isLast() {
			return renderedSegments.value.at(-1)?.id === segment.id;
		}
		function focusNext() {
			if (isLast()) return;
			focusNextSegment();
		}
		return {
			increment,
			decrement,
			setValue,
			parser,
			onSegmentDone,
			getMetadata,
			onDone: onSegmentDone,
			clear,
			onTouched,
			isLast,
			focusNext,
			isNumeric,
			isLockedByRange,
			dispatchEvent
		};
	}
	function focusBasedOnDirection(evt) {
		const dir = toValue(direction) ?? "ltr";
		if (hasKeyCode(evt, "ArrowLeft")) return dir === "ltr" ? focusPreviousSegment() : focusNextSegment();
		if (hasKeyCode(evt, "ArrowRight")) return dir === "ltr" ? focusNextSegment() : focusPreviousSegment();
	}
	function getFocusedSegment() {
		return renderedSegments.value.find((s) => s.getElem() === document.activeElement);
	}
	function getSegmentElements() {
		return Array.from(controlEl.value?.querySelectorAll("[data-segment-type]") || []);
	}
	function focusNextSegment() {
		const focusedElement = getFocusedSegment()?.getElem();
		if (!focusedElement) return;
		const segmentElements = getSegmentElements();
		const currentIndex = segmentElements.indexOf(focusedElement);
		const nextIndex = currentIndex + 1;
		for (let i = nextIndex; i < segmentElements.length; i++) {
			const element = segmentElements[i];
			if (element.tabIndex === 0) {
				element.focus();
				return;
			}
		}
	}
	function focusPreviousSegment() {
		const focusedElement = getFocusedSegment()?.getElem();
		if (!focusedElement) return;
		const segmentElements = getSegmentElements();
		const currentIndex = segmentElements.indexOf(focusedElement);
		const previousIndex = currentIndex - 1;
		for (let i = previousIndex; i >= 0; i--) {
			const element = segmentElements[i];
			if (element.tabIndex === 0) {
				element.focus();
				return;
			}
		}
	}
	function onKeydown(evt) {
		if (hasKeyCode(evt, "ArrowLeft") || hasKeyCode(evt, "ArrowRight")) {
			blockEvent(evt);
			focusBasedOnDirection(evt);
			return;
		}
	}
	useEventListener(controlEl, "keydown", onKeydown);
	provide(DateTimeSegmentGroupKey, { useDateSegmentRegistration });
	return {
		segments,
		useDateSegmentRegistration
	};
}
function useDateArithmetic({ currentDate, min, max }) {
	function clampDate(date) {
		const minDate = toValue(min);
		const maxDate = toValue(max);
		if (minDate && date.compare(minDate) < 0) return toValue(currentDate);
		if (maxDate && date.compare(maxDate) > 0) return toValue(currentDate);
		return date;
	}
	function setPart(part, value) {
		const date = toValue(currentDate);
		if (!isEditableSegmentType(part)) return date;
		if (part === "dayPeriod") return date;
		const newDate = date.set({ [part]: value });
		if (isTemporalPartial(date)) newDate["~fw_temporal_partial"] = {
			...date["~fw_temporal_partial"],
			[part]: true
		};
		return newDate;
	}
	function addToPart(part, diff) {
		const date = toValue(currentDate);
		if (!isEditableSegmentType(part)) return date;
		if (part === "dayPeriod") diff = diff * 12;
		const durationPart = segmentTypeToDurationLike(part);
		if (!durationPart) return date;
		if (isTemporalPartial(date)) {
			let newDate = date;
			if (isTemporalPartSet(date, part)) newDate = date.add({ [durationPart]: diff });
			else newDate = part === "dayPeriod" ? date : date.set({ [part]: part === "year" ? date.year : 1 });
			newDate["~fw_temporal_partial"] = {
				...date["~fw_temporal_partial"],
				[part]: true
			};
			return clampDate(newDate);
		}
		const day = date.day;
		const month = date.month;
		const year = date.year;
		return clampDate(date.add({ [durationPart]: diff }).set({
			day: part !== "day" && part !== "weekday" ? day : void 0,
			month: part !== "month" ? month : void 0,
			year: part !== "year" ? year : void 0
		}));
	}
	return {
		setPart,
		addToPart
	};
}

//#endregion
//#region packages/core/src/useDateTime/useTemporalStore.ts
function useTemporalStore(init) {
	const model = init.model;
	function normalizeNullish(value) {
		if (isNullOrUndefined(value)) return createTemporalPartial(toValue(init.calendar), toValue(init.timeZone), toValue(init.min), toValue(init.max));
		return value;
	}
	const temporalVal = shallowRef(normalizeNullish(fromDateToCalendarZonedDateTime(model.get(), toValue(init.calendar), toValue(init.timeZone))));
	watch(model.get, (value) => {
		if (!value && isTemporalPartial(temporalVal.value)) return;
		temporalVal.value = normalizeNullish(fromDateToCalendarZonedDateTime(value, toValue(init.calendar), toValue(init.timeZone)));
	});
	function toDate(value) {
		if (isNullOrUndefined(value)) return value;
		if (value instanceof Date) return value;
		const zonedDateTime = toZonedDateTime(value, toValue(init.timeZone));
		if (!zonedDateTime) return zonedDateTime;
		return zonedDateTime.toDate();
	}
	const temporalValue = computed({
		get: () => temporalVal.value,
		set: (value) => {
			temporalVal.value = value;
			model.set?.(isTemporalPartial(value) ? void 0 : toDate(value));
		}
	});
	return temporalValue;
}
function fromDateToCalendarZonedDateTime(date, calendar, timeZone) {
	const zonedDt = toZonedDateTime(date, timeZone);
	if (!zonedDt) return zonedDt;
	return toCalendar(toTimeZone(zonedDt, timeZone), calendar);
}
function toZonedDateTime(value, timeZone) {
	if (isNullOrUndefined(value)) return value;
	if (value instanceof Date) value = fromDate(value, timeZone);
	return value;
}

//#endregion
//#region packages/core/src/useDateTime/useDateField.ts
function useDateField(_props) {
	const props = normalizeProps(_props, ["schema"]);
	const controlEl = shallowRef();
	const { locale, direction, timeZone, calendar } = useLocale(props.locale, {
		calendar: () => toValue(props.calendar),
		timeZone: () => toValue(props.timeZone)
	});
	const formatter = useDateFormatter(locale, props.formatOptions);
	const controlId = useUniqId(FieldTypePrefixes.DateTimeField);
	const field = useFormField({
		path: props.name,
		disabled: props.disabled,
		initialValue: toValue(props.modelValue) ?? toValue(props.value),
		schema: props.schema
	});
	const { element: inputEl } = useConstraintsValidator({
		type: "date",
		required: props.required,
		value: field.fieldValue,
		source: controlEl,
		min: props.min,
		max: props.max
	});
	useInputValidity({
		field,
		inputEl
	});
	const min = computed(() => fromDateToCalendarZonedDateTime(toValue(props.min), calendar.value, timeZone.value));
	const max = computed(() => fromDateToCalendarZonedDateTime(toValue(props.max), calendar.value, timeZone.value));
	const temporalValue = useTemporalStore({
		calendar,
		timeZone,
		locale,
		model: {
			get: () => field.fieldValue.value,
			set: (value) => field.setValue(value)
		},
		min,
		max
	});
	function onValueChange(value) {
		temporalValue.value = value;
	}
	const { segments } = useDateTimeSegmentGroup({
		formatter,
		locale,
		formatOptions: props.formatOptions,
		direction,
		controlEl,
		temporalValue,
		readonly: props.readonly,
		onValueChange,
		onTouched: () => field.setTouched(true),
		min,
		max,
		dispatchEvent: (type) => inputEl.value?.dispatchEvent(new Event(type))
	});
	const { labelProps, labelledByProps } = useLabel({
		for: controlId,
		label: props.label,
		targetRef: controlEl
	});
	const { descriptionProps, describedByProps } = createDescribedByProps({
		inputId: controlId,
		description: props.description
	});
	const { errorMessageProps, accessibleErrorProps } = useErrorMessage({
		inputId: controlId,
		errorMessage: field.errorMessage
	});
	const calendarProps = computed(() => {
		const propsObj = {
			label: toValue(props.label),
			locale: locale.value,
			name: void 0,
			calendar: calendar.value,
			timeZone: timeZone.value,
			min: toValue(props.min),
			max: toValue(props.max),
			field
		};
		return propsObj;
	});
	const controlProps = computed(() => {
		return withRefCapture({
			id: controlId,
			role: "group",
			...labelledByProps.value,
			...describedByProps.value,
			...accessibleErrorProps.value,
			"aria-disabled": field.isDisabled.value || void 0
		}, controlEl);
	});
	if (process.env.NODE_ENV !== "production") registerField(field, "Date");
	return exposeField({
		controlProps,
		descriptionProps,
		labelProps,
		errorMessageProps,
		segments,
		calendarProps,
		direction
	}, field);
}

//#endregion
//#region packages/core/src/useDateTime/useTimeField.ts
function getDefaultFormatOptions() {
	return {
		hour: "2-digit",
		minute: "2-digit",
		hour12: false
	};
}
function useTimeField(_props) {
	const props = normalizeProps(_props, ["schema"]);
	const controlEl = shallowRef();
	const { locale, direction, calendar, timeZone } = useLocale(props.locale);
	const formatOptions = computed(() => merge(getDefaultFormatOptions(), toValue(props.formatOptions) || {}));
	const isDisabled = createDisabledContext(props.disabled);
	const formatter = useDateFormatter(locale, formatOptions);
	const controlId = useUniqId(FieldTypePrefixes.DateTimeField);
	const field = useFormField({
		path: props.name,
		disabled: props.disabled,
		initialValue: toValue(props.modelValue) ?? toValue(props.value),
		schema: props.schema
	});
	const { element: inputEl } = useConstraintsValidator({
		type: "time",
		required: props.required,
		value: field.fieldValue,
		source: controlEl,
		min: props.min,
		max: props.max
	});
	useInputValidity({
		field,
		inputEl
	});
	const temporalValue = useTemporalStore({
		calendar,
		timeZone,
		locale,
		model: {
			get: () => timeStringToDate(field.fieldValue.value),
			set: (value) => field.setValue(dateToTimeString(value, formatOptions.value))
		}
	});
	function onValueChange(value) {
		temporalValue.value = value;
	}
	const { segments } = useDateTimeSegmentGroup({
		formatter,
		locale,
		formatOptions: props.formatOptions,
		direction,
		controlEl,
		temporalValue,
		readonly: props.readonly,
		onValueChange,
		onTouched: () => field.setTouched(true),
		dispatchEvent: (type) => inputEl.value?.dispatchEvent(new Event(type))
	});
	const { labelProps, labelledByProps } = useLabel({
		for: controlId,
		label: props.label,
		targetRef: controlEl
	});
	const { descriptionProps, describedByProps } = createDescribedByProps({
		inputId: controlId,
		description: props.description
	});
	const { errorMessageProps, accessibleErrorProps } = useErrorMessage({
		inputId: controlId,
		errorMessage: field.errorMessage
	});
	const controlProps = computed(() => {
		return withRefCapture({
			id: controlId,
			role: "group",
			...labelledByProps.value,
			...describedByProps.value,
			...accessibleErrorProps.value,
			"aria-disabled": isDisabled.value || void 0
		}, controlEl);
	});
	if (process.env.NODE_ENV !== "production") registerField(field, "Time");
	return exposeField({
		controlProps,
		descriptionProps,
		labelProps,
		errorMessageProps,
		segments,
		direction
	}, field);
}
function timeStringToDate(time) {
	if (!time) return null;
	const [hours, minutes, seconds] = time.split(":").map(Number);
	const now$1 = new Date();
	now$1.setHours(hours);
	now$1.setMinutes(minutes);
	now$1.setMilliseconds(0);
	if (!Number.isNaN(seconds) && !isNullOrUndefined(seconds)) now$1.setSeconds(seconds);
	return now$1;
}
function dateToTimeString(date, formatOptions) {
	const hours = date?.getHours();
	const minutes = date?.getMinutes();
	const seconds = date?.getSeconds();
	if (Number.isNaN(hours) || Number.isNaN(minutes) || isNullOrUndefined(hours) || isNullOrUndefined(minutes)) return void 0;
	if (formatOptions?.second && !Number.isNaN(seconds) && !isNullOrUndefined(seconds)) return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
	return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

//#endregion
//#region packages/core/src/useDateTime/useDateTimeSegment.ts
function useDateTimeSegment(_props) {
	const props = normalizeProps(_props);
	const id = useUniqId(FieldTypePrefixes.DateTimeSegment);
	const segmentEl = shallowRef();
	const segmentGroup = inject(DateTimeSegmentGroupKey, null);
	const isDisabled = createDisabledContext(props.disabled);
	if (!segmentGroup) throw new Error("DateTimeSegmentGroup is not provided");
	const { increment, decrement, setValue, getMetadata, onDone, parser, clear, onTouched, isLast, focusNext, isNumeric, isLockedByRange, dispatchEvent } = segmentGroup.useDateSegmentRegistration({
		id,
		getElem: () => segmentEl.value,
		getType: () => toValue(props.type)
	});
	let currentInput = "";
	function isNonMutable() {
		return !isEditableSegmentType(toValue(props.type)) || isDisabled.value || toValue(props.readonly) || isLockedByRange();
	}
	function isSpinOnly() {
		return !!toValue(props.spinOnly);
	}
	const handlers = {
		onFocus() {
			currentInput = "";
		},
		onBeforeinput(evt) {
			if (isNonMutable() || isSpinOnly()) {
				blockEvent(evt);
				return;
			}
			if (isNullOrUndefined(evt.data)) return;
			blockEvent(evt);
			if (!isNumeric()) return;
			const { min, max, maxLength } = getMetadata();
			const nextValue = currentInput + evt.data;
			const parsed = parser.parse(nextValue);
			if (isNullOrUndefined(min) || isNullOrUndefined(max) || isNullOrUndefined(maxLength)) return;
			if (Number.isNaN(parsed) || parsed > max) return;
			currentInput = nextValue;
			if (segmentEl.value) segmentEl.value.textContent = currentInput;
			if (currentInput.length >= maxLength || parsed * 10 > max) {
				if (parsed < min) {
					if (segmentEl.value) segmentEl.value.textContent = min.toString();
				}
				onDone();
			}
		},
		onBlur() {
			onTouched();
			nextTick(() => {
				dispatchEvent("blur");
			});
			if (isSpinOnly()) {
				currentInput = "";
				return;
			}
			const { min, max } = getMetadata();
			if (isNullOrUndefined(min) || isNullOrUndefined(max)) return;
			const parsed = parser.parse(segmentEl.value?.textContent || "");
			if (!Number.isNaN(parsed) && parsed >= min && parsed <= max) setValue(parsed);
			currentInput = "";
		},
		onKeydown(evt) {
			if (isNonMutable()) return;
			if (hasKeyCode(evt, "Enter")) {
				blockEvent(evt);
				focusNext();
				return;
			}
			if (hasKeyCode(evt, "ArrowUp")) {
				blockEvent(evt);
				if (!isNonMutable()) increment();
				return;
			}
			if (hasKeyCode(evt, "ArrowDown")) {
				blockEvent(evt);
				if (!isNonMutable()) decrement();
				return;
			}
			if (hasKeyCode(evt, "Backspace") || hasKeyCode(evt, "Delete")) {
				blockEvent(evt);
				if (!isNonMutable()) clear();
			}
		}
	};
	const segmentProps = computed(() => {
		const ceValue = isFirefox() ? "true" : "plaintext-only";
		const domProps = {
			id,
			tabindex: isNonMutable() ? -1 : 0,
			contenteditable: isNonMutable() || isSpinOnly() ? void 0 : ceValue,
			"aria-disabled": isNonMutable(),
			"data-segment-type": toValue(props.type),
			"aria-label": isNonMutable() ? void 0 : toValue(props.type),
			"aria-readonly": toValue(props.readonly) ? true : void 0,
			autocorrect: isNonMutable() || isSpinOnly() ? void 0 : "off",
			autocomplete: isNonMutable() || isSpinOnly() ? void 0 : "off",
			spellcheck: isNonMutable() || isSpinOnly() ? void 0 : false,
			enterkeyhint: isNonMutable() || isSpinOnly() ? void 0 : isLast() ? "done" : "next",
			inputmode: isSpinOnly() ? void 0 : "none",
			style: isSpinOnly() ? void 0 : { caretColor: "transparent" },
			...isSpinOnly() ? {
				onKeydown: handlers.onKeydown,
				onBlur: handlers.onBlur
			} : handlers
		};
		if (isNumeric()) {
			const { min, max } = getMetadata();
			const value = parser.parse(toValue(props.value));
			domProps.role = "spinbutton";
			domProps.inputmode = isSpinOnly() ? void 0 : "numeric";
			domProps["aria-valuemin"] = min ?? void 0;
			domProps["aria-valuemax"] = max ?? void 0;
			domProps["aria-valuenow"] = Number.isNaN(value) ? void 0 : value;
			domProps["aria-valuetext"] = Number.isNaN(value) ? "Empty" : value.toString();
		}
		if (isNonMutable() && domProps.style) domProps.style.pointerEvents = "none";
		return withRefCapture(domProps, segmentEl);
	});
	return {
		key: computed(() => `${id}-${toValue(props.type)}`),
		segmentProps
	};
}
/**
* A helper component that renders a datetime segment. You can build your own with `useDateTimeSegment`.
*/
const DateTimeSegment = /* @__PURE__ */ defineComponent({
	name: "DateTimeSegment",
	props: [
		"type",
		"value",
		"disabled",
		"readonly",
		"spinOnly"
	],
	setup(props) {
		const { segmentProps, key } = useDateTimeSegment(props);
		return () => h("span", {
			...segmentProps.value,
			key: key.value
		}, props.value);
	}
});

//#endregion
//#region packages/core/src/useFileField/types.ts
const FileEntryCollectionKey = Symbol("FileEntryCollection");

//#endregion
//#region packages/core/src/useFileField/useFileField.ts
function useFileField(_props) {
	let idCounter = 0;
	const props = normalizeProps(_props, ["schema", "onUpload"]);
	const inputEl = ref();
	const entries = ref([]);
	const inputId = useUniqId(FieldTypePrefixes.FileField);
	const dropzoneEl = ref();
	const abortControllers = new Map();
	const overridePickOptions = ref();
	const isUploading = computed(() => entries.value.some((e) => e.isUploading));
	const field = useFormField({
		path: props.name,
		disabled: props.disabled,
		schema: props.schema
	});
	const { element: fakeInputEl } = useConstraintsValidator({
		type: "text",
		source: inputEl,
		required: props.required,
		value: () => normalizeArrayable(field.fieldValue.value ?? []).length > 0 ? "_" : ""
	});
	const { validityDetails } = useInputValidity({
		inputEl: fakeInputEl,
		field
	});
	const { accessibleErrorProps, errorMessageProps } = useErrorMessage({
		inputId,
		errorMessage: field.errorMessage
	});
	function isMultiple() {
		return toValue(props.multiple) ?? false;
	}
	function setValue(value, idx) {
		if (!isMultiple()) {
			field.setValue(value);
			return;
		}
		if (isNullOrUndefined(idx) || idx === -1) return;
		const nextValue = normalizeArrayable(field.fieldValue.value ?? []);
		nextValue[idx] = value;
		field.setValue(nextValue);
	}
	function updateFieldValue() {
		if (isMultiple()) {
			field.setValue(entries.value.map((e) => e.uploadResult ?? e.file));
			return;
		}
		if (entries.value[0]) {
			field.setValue(entries.value[0].uploadResult ?? entries.value[0].file);
			return;
		}
		field.setValue(void 0);
	}
	async function processFiles(fileList) {
		if (!isMultiple()) {
			fileList = fileList.slice(0, 1);
			entries.value = [];
		}
		for (const file of fileList) {
			const key = `${inputId}-${idCounter++}`;
			const entry = {
				id: key,
				file: markRaw(file),
				isUploading: false
			};
			entries.value.push(entry);
			if (!props.onUpload) {
				setValue(file, entries.value.findIndex((e) => e.id === entry.id));
				continue;
			}
			const reEntry = entries.value.find((e) => e.id === entry.id);
			if (!reEntry) continue;
			entry.isUploading = true;
			const controller = new AbortController();
			abortControllers.set(entry.id, controller);
			props.onUpload({
				file,
				key: entry.id,
				signal: controller.signal
			}).then((result) => {
				if (result) {
					entry.uploadResult = result;
					setValue(result, entries.value.findIndex((e) => e.id === entry.id));
				}
			}).finally(() => {
				abortControllers.delete(entry.id);
				reEntry.isUploading = false;
			});
		}
	}
	function onBlur() {
		field.setTouched(true);
	}
	function onChange(evt) {
		overridePickOptions.value = void 0;
		if (field.isDisabled.value) return;
		field.setTouched(true);
		processFiles(Array.from(evt.target.files ?? []));
		if (inputEl.value) inputEl.value.value = "";
	}
	async function onClick(evt) {
		overridePickOptions.value = void 0;
		if (field.isDisabled.value) {
			blockEvent(evt);
			return;
		}
		inputEl.value?.showPicker();
	}
	function onCancel() {
		overridePickOptions.value = void 0;
	}
	const inputProps = computed(() => {
		return withRefCapture({
			id: inputId,
			type: "file",
			tabindex: -1,
			...propsToValues(props, [
				"name",
				"accept",
				"multiple",
				"required",
				"disabled"
			]),
			...overridePickOptions.value,
			onBlur,
			onChange,
			onCancel,
			webkitdirectory: isMultiple() ? toValue(props.allowDirectory) : void 0,
			style: { display: "none" }
		}, inputEl);
	});
	const triggerProps = useControlButtonProps(() => ({
		id: `${inputId}-trigger`,
		disabled: field.isDisabled.value,
		...accessibleErrorProps.value,
		onClick,
		onBlur
	}));
	const removeButtonProps = useControlButtonProps(() => ({
		id: `${inputId}-remove`,
		ariaLabel: toValue(props.removeButtonLabel) ?? "Remove file",
		disabled: field.isDisabled.value,
		onClick: () => remove()
	}));
	const isDragging = ref(false);
	const dropzoneHandlers = {
		onDragenter(evt) {
			blockEvent(evt);
		},
		onDragover(evt) {
			blockEvent(evt);
			isDragging.value = true;
		},
		onDragleave(evt) {
			blockEvent(evt);
			isDragging.value = false;
		},
		onDrop(evt) {
			blockEvent(evt);
			if (field.isDisabled.value) return;
			processFiles(Array.from(evt.dataTransfer?.files ?? []));
		},
		onClick(e) {
			if (field.isDisabled.value) {
				blockEvent(e);
				return;
			}
			if (e.target === dropzoneEl.value) inputEl.value?.showPicker();
		}
	};
	const dropzoneProps = computed(() => {
		return withRefCapture({
			role: "group",
			"data-dragover": isDragging.value,
			"aria-label": toValue(props.label),
			...dropzoneHandlers
		}, dropzoneEl);
	});
	function clear() {
		entries.value = [];
		for (const controller of abortControllers.values()) controller.abort();
		abortControllers.clear();
		if (inputEl.value) inputEl.value.value = "";
		updateFieldValue();
	}
	async function showPicker(opts) {
		overridePickOptions.value = opts;
		await nextTick();
		inputEl.value?.showPicker();
	}
	function remove(key) {
		if (key instanceof Event) key = void 0;
		if (key && typeof key === "object") key = key.id;
		if (key) {
			const controller = abortControllers.get(key);
			controller?.abort();
			removeFirst(entries.value, (f) => f.id === key);
			abortControllers.delete(key);
			updateFieldValue();
			return;
		}
		const entry = entries.value.pop();
		if (entry) {
			const controller = abortControllers.get(entry.id);
			controller?.abort();
			abortControllers.delete(entry.id);
		}
		updateFieldValue();
	}
	if (process.env.NODE_ENV !== "production") registerField(field, "File");
	provide(FileEntryCollectionKey, {
		removeEntry: remove,
		isDisabled: () => field.isDisabled.value,
		getRemoveButtonLabel: () => toValue(props.removeButtonLabel) ?? "Remove file"
	});
	return exposeField({
		inputProps,
		inputEl,
		triggerProps,
		dropzoneProps,
		errorMessageProps,
		validityDetails,
		entries: readonly(entries),
		entry: computed(() => entries.value[entries.value.length - 1]),
		clear,
		remove,
		isDragging,
		removeButtonProps,
		isUploading,
		showPicker
	}, field);
}

//#endregion
//#region packages/core/src/useFileField/useFileEntry.ts
function useFileEntry(_props) {
	const props = normalizeProps(_props);
	const collection = inject(FileEntryCollectionKey);
	const previewEl = ref();
	const currentObjectURL = shallowRef();
	if (process.env.NODE_ENV !== "production") {
		if (!collection) warn("File entries require a parent FileField to be used, some features may not work as expected.");
	}
	watch(props.file, () => {
		currentObjectURL.value = void 0;
	});
	function remove() {
		collection?.removeEntry(toValue(props.id));
	}
	const isUploaded = computed(() => {
		return !toValue(props.isUploading) && toValue(props.uploadResult);
	});
	const removeButtonProps = useControlButtonProps(() => ({
		onClick: remove,
		disabled: toValue(props.isUploading) || collection?.isDisabled(),
		"aria-label": collection?.getRemoveButtonLabel()
	}));
	function createPreviewProps() {
		if (!currentObjectURL.value) currentObjectURL.value = URL.createObjectURL(toValue(props.file));
		const file = toValue(props.file);
		if (previewEl.value?.tagName === "IMG" || file.type.startsWith("image/")) return {
			as: "img",
			src: currentObjectURL.value,
			alt: file.name
		};
		if (previewEl.value?.tagName === "VIDEO" || file.type.startsWith("video/")) return {
			as: "video",
			"aria-label": file.name,
			src: currentObjectURL.value,
			controls: false,
			muted: true,
			loop: true,
			autoplay: true,
			playsinline: true
		};
		return {};
	}
	onUnmounted(() => {
		if (currentObjectURL.value) URL.revokeObjectURL(currentObjectURL.value);
		currentObjectURL.value = void 0;
	});
	const previewProps = computed(() => {
		return withRefCapture(createPreviewProps(), previewEl);
	});
	return {
		name: computed(() => toValue(props.file).name),
		remove,
		isUploaded,
		previewProps,
		removeButtonProps
	};
}
/**
* A helper component that renders a span. You can build your own with `useFileEntry`.
*/
const FileEntryImpl = /* @__PURE__ */ defineComponent({
	name: "FileEntry",
	props: [
		"id",
		"file",
		"isUploading",
		"uploadResult",
		"as"
	],
	setup(props, { slots }) {
		const { remove, isUploaded, removeButtonProps, previewProps, name } = useFileEntry(props);
		return () => h(props.as || "span", { key: props.id }, { default: () => slots.default?.({
			remove,
			name: name.value,
			isUploaded: isUploaded.value,
			isUploading: props.isUploading,
			hasPreview: !!previewProps.value.as,
			removeButtonProps: removeButtonProps.value,
			previewProps: previewProps.value
		}) });
	}
});
const FileEntry = FileEntryImpl;

//#endregion
//#region packages/core/src/useCalendar/constants.ts
const CalendarContextKey = Symbol("CalendarContext");
const YEAR_CELLS_COUNT = 9;

//#endregion
//#region packages/core/src/useCalendar/useCalendarView.ts
function useCalendarView(_props, context) {
	const props = normalizeProps(_props);
	const viewType = shallowRef("weeks");
	const { days, weekDays } = useCalendarDaysView(context, props.weekDayFormat);
	const { months, monthFormatter } = useCalendarMonthsView(context, props.monthFormat);
	const { years, yearFormatter } = useCalendarYearsView(context, props.yearFormat);
	const currentView = computed(() => {
		if (viewType.value === "weeks") return {
			type: "weeks",
			days: days.value,
			weekDays: weekDays.value
		};
		if (viewType.value === "months") return {
			type: "months",
			months: months.value
		};
		return {
			type: "years",
			years: years.value
		};
	});
	function setView(type) {
		viewType.value = type;
	}
	const viewLabel = computed(() => {
		if (viewType.value === "weeks") return `${monthFormatter.value.format(context.getFocusedDate().toDate())} ${yearFormatter.value.format(context.getFocusedDate().toDate())}`;
		if (viewType.value === "months") return yearFormatter.value.format(context.getFocusedDate().toDate());
		return `${yearFormatter.value.format(years.value[0].value.toDate())} - ${yearFormatter.value.format(years.value[years.value.length - 1].value.toDate())}`;
	});
	return {
		currentView,
		setView,
		viewLabel
	};
}
function useCalendarDaysView({ weekInfo, getFocusedDate, getSelectedDate, locale, timeZone, calendar, getMinDate, getMaxDate }, daysOfWeekFormat) {
	const dayFormatter = useDateFormatter(locale, () => ({ weekday: toValue(daysOfWeekFormat) ?? "short" }));
	const dayNumberFormatter = useDateFormatter(locale, () => ({ day: "numeric" }));
	const days = computed(() => {
		const current = getSelectedDate();
		const focused = getFocusedDate();
		const startOfMonth = focused.set({
			day: 1,
			hour: 0,
			minute: 0,
			second: 0,
			millisecond: 0
		});
		const firstDayOfWeek = weekInfo.value.firstDay;
		const startDayOfWeek = startOfMonth.toDate().getDay();
		const daysToSubtract = (startDayOfWeek - firstDayOfWeek + 7) % 7;
		const firstDay = startOfMonth.subtract({ days: daysToSubtract });
		const gridDays = 42;
		const rightNow = toCalendar(now(timeZone.value), calendar.value);
		const minDate = getMinDate();
		const maxDate = getMaxDate();
		const rightNowDate = toCalendarDate(rightNow);
		const focusedDate = toCalendarDate(focused);
		const currentDate = toCalendarDate(current);
		return Array.from({ length: gridDays }, (_, i) => {
			const dayOfMonth = firstDay.add({ days: i });
			let disabled = false;
			if (minDate && dayOfMonth.compare(minDate) < 0) disabled = true;
			if (maxDate && dayOfMonth.compare(maxDate) > 0) disabled = true;
			const domDate = toCalendarDate(dayOfMonth);
			return {
				value: dayOfMonth,
				label: dayNumberFormatter.value.format(dayOfMonth.toDate()),
				dayOfMonth: dayOfMonth.day,
				isToday: rightNowDate.compare(domDate) === 0,
				selected: currentDate.compare(domDate) === 0,
				isOutsideMonth: domDate.month !== focusedDate.month,
				focused: focusedDate.compare(domDate) === 0,
				disabled,
				type: "day"
			};
		});
	});
	const weekDays = computed(() => {
		let focused = getFocusedDate();
		const daysPerWeek = 7;
		const firstDayOfWeek = weekInfo.value.firstDay;
		const currentDayOfWeek = focused.toDate().getDay();
		const daysToSubtract = (currentDayOfWeek - firstDayOfWeek + 7) % 7;
		focused = focused.subtract({ days: daysToSubtract });
		const days$1 = [];
		for (let i = 0; i < daysPerWeek; i++) days$1.push(dayFormatter.value.format(focused.add({ days: i }).toDate()));
		return days$1;
	});
	return {
		days,
		weekDays,
		dayFormatter
	};
}
function useCalendarMonthsView({ getFocusedDate, locale, getSelectedDate, getMinDate, getMaxDate }, monthFormat) {
	const monthFormatter = useDateFormatter(locale, () => ({ month: toValue(monthFormat) ?? "long" }));
	const months = computed(() => {
		const focused = getFocusedDate();
		const current = getSelectedDate();
		const minDate = getMinDate();
		const maxDate = getMaxDate();
		return Array.from({ length: focused.calendar.getMonthsInYear(focused) }, (_, i) => {
			const date = focused.set({
				month: i + 1,
				day: 1,
				hour: 0,
				minute: 0,
				second: 0,
				millisecond: 0
			});
			let disabled = false;
			if (minDate && minDate.month < date.month) disabled = true;
			if (maxDate && maxDate.month > date.month) disabled = true;
			const cell = {
				type: "month",
				label: monthFormatter.value.format(date.toDate()),
				value: date,
				monthOfYear: date.month,
				selected: date.month === current.month && date.year === current.year,
				focused: focused.month === date.month && focused.year === date.year,
				disabled
			};
			return cell;
		});
	});
	return {
		months,
		monthFormatter
	};
}
function useCalendarYearsView({ getFocusedDate, locale, getSelectedDate, getMinDate, getMaxDate }, yearFormat) {
	const yearFormatter = useDateFormatter(locale, () => ({ year: toValue(yearFormat) ?? "numeric" }));
	const years = computed(() => {
		const focused = getFocusedDate();
		const current = getSelectedDate();
		const minDate = getMinDate();
		const maxDate = getMaxDate();
		return Array.from({ length: YEAR_CELLS_COUNT }, (_, i) => {
			const startYear = Math.floor(focused.year / YEAR_CELLS_COUNT) * YEAR_CELLS_COUNT;
			const date = focused.set({
				year: startYear + i,
				month: 1,
				day: 1,
				hour: 0,
				minute: 0,
				second: 0,
				millisecond: 0
			});
			let disabled = false;
			if (minDate && minDate.year < date.year) disabled = true;
			if (maxDate && maxDate.year > date.year) disabled = true;
			const cell = {
				type: "year",
				label: yearFormatter.value.format(date.toDate()),
				value: date,
				year: date.year,
				selected: date.year === current.year,
				focused: focused.year === date.year,
				disabled
			};
			return cell;
		});
	});
	return {
		years,
		yearFormatter
	};
}

//#endregion
//#region packages/core/src/usePicker/usePicker.ts
const PickerContextKey = Symbol("PickerContext");
function usePicker(_props) {
	const props = normalizeProps(_props);
	const pickerEl = ref();
	const disabled = createDisabledContext(props.disabled);
	const { isOpen } = usePopoverController(pickerEl, { disabled });
	const pickerProps = computed(() => {
		return withRefCapture({
			role: "dialog",
			"aria-modal": "true",
			"aria-label": toValue(props.label)
		}, pickerEl);
	});
	function onOpen() {
		isOpen.value = true;
	}
	const pickerTriggerProps = useControlButtonProps(() => {
		return {
			"aria-label": toValue(props.label),
			disabled: disabled.value,
			onClick: onOpen
		};
	});
	provide(PickerContextKey, {
		isOpen: () => isOpen.value,
		close: () => isOpen.value = false
	});
	return {
		isOpen,
		pickerProps,
		pickerTriggerProps,
		pickerEl
	};
}

//#endregion
//#region packages/core/src/useCalendar/useCalendar.ts
function useCalendar(_props) {
	const props = normalizeProps(_props, ["field", "schema"]);
	const { weekInfo, locale, calendar, timeZone, direction } = useLocale(props.locale, {
		calendar: () => toValue(props.calendar),
		timeZone: () => toValue(props.timeZone)
	});
	const pickerContext = inject(PickerContextKey, null);
	const calendarId = useUniqId(FieldTypePrefixes.Calendar);
	const gridId = `${calendarId}-g`;
	const calendarEl = ref();
	const gridEl = ref();
	const calendarLabelEl = ref();
	const field = props.field ?? useFormField({
		path: props.name,
		disabled: props.disabled,
		initialValue: toValue(props.modelValue) ?? toValue(props.value),
		schema: props.schema
	});
	const temporalValue = useTemporalStore({
		calendar,
		timeZone,
		locale,
		model: {
			get: () => field.fieldValue.value,
			set: (value) => field.setValue(value)
		}
	});
	if (!props.field) {
		const { element } = useConstraintsValidator({
			type: "date",
			value: field.fieldValue,
			source: calendarEl,
			required: props.required
		});
		useInputValidity({
			field,
			inputEl: element
		});
	}
	const isDisabled = field.isDisabled || createDisabledContext(props.disabled);
	const selectedDate = computed(() => temporalValue.value ?? toCalendar(now(toValue(timeZone)), calendar.value));
	const focusedDay = shallowRef();
	function getFocusedOrSelected() {
		if (focusedDay.value) return focusedDay.value;
		return selectedDate.value;
	}
	const min = computed(() => fromDateToCalendarZonedDateTime(toValue(props.min), calendar.value, timeZone.value));
	const max = computed(() => fromDateToCalendarZonedDateTime(toValue(props.max), calendar.value, timeZone.value));
	const context = {
		weekInfo,
		locale,
		calendar,
		timeZone,
		getSelectedDate: () => selectedDate.value,
		getFocusedDate: getFocusedOrSelected,
		setDate,
		setFocusedDate: async (date) => {
			if (isDisabled.value || toValue(props.readonly)) return;
			focusedDay.value = date;
			await nextTick();
			focusCurrent();
		},
		getMinDate: () => min.value,
		getMaxDate: () => max.value
	};
	provide(CalendarContextKey, context);
	const { currentView, setView, viewLabel: gridLabel } = useCalendarView({
		weekDayFormat: props.weekDayFormat,
		monthFormat: props.monthFormat,
		yearFormat: props.yearFormat
	}, context);
	function setDate(date, view) {
		if (isDisabled.value || toValue(props.readonly)) return;
		temporalValue.value = date;
		focusedDay.value = date;
		if (view) setView(view);
		else if (currentView.value.type === "weeks") pickerContext?.close();
	}
	const handleKeyDown = useCalendarKeyboard(context, currentView);
	const pickerHandlers = { onKeydown(e) {
		const handled = handleKeyDown(e);
		if (handled) {
			blockEvent(e);
			return;
		}
		if (hasKeyCode(e, "Escape")) pickerContext?.close();
		if (hasKeyCode(e, "Tab")) pickerContext?.close();
	} };
	function focusCurrent() {
		const currentlySelected = gridEl.value?.querySelector("[tabindex=\"0\"]");
		if (currentlySelected) {
			currentlySelected.focus();
			return;
		}
	}
	watch(() => pickerContext?.isOpen(), async (value) => {
		if (pickerContext && !value) {
			focusedDay.value = void 0;
			setView("weeks");
			return;
		}
		if (!focusedDay.value) focusedDay.value = selectedDate.value.copy();
		await nextTick();
		focusCurrent();
	}, { immediate: true });
	const calendarProps = computed(() => {
		return withRefCapture({
			id: calendarId,
			...pickerHandlers,
			role: "application",
			dir: direction.value
		}, calendarEl);
	});
	const nextButtonProps = useControlButtonProps(() => ({
		id: `${calendarId}-next`,
		"aria-label": "Next",
		disabled: isDisabled.value || toValue(props.readonly),
		onClick: () => {
			if (currentView.value.type === "weeks") {
				context.setFocusedDate(context.getFocusedDate().add({ months: 1 }));
				return;
			}
			if (currentView.value.type === "months") {
				context.setFocusedDate(context.getFocusedDate().add({ years: 1 }));
				return;
			}
			context.setFocusedDate(currentView.value.years[currentView.value.years.length - 1].value.add({ years: 1 }));
		}
	}));
	const previousButtonProps = useControlButtonProps(() => ({
		id: `${calendarId}-previous`,
		"aria-label": "Previous",
		disabled: isDisabled.value || toValue(props.readonly),
		onClick: () => {
			if (currentView.value.type === "weeks") {
				context.setFocusedDate(context.getFocusedDate().subtract({ months: 1 }));
				return;
			}
			if (currentView.value.type === "months") {
				context.setFocusedDate(context.getFocusedDate().subtract({ years: 1 }));
				return;
			}
			context.setFocusedDate(currentView.value.years[0].value.subtract({ years: 1 }));
		}
	}));
	const { labelProps: monthYearLabelBaseProps, labelledByProps } = useLabel({
		targetRef: gridEl,
		for: gridId,
		label: gridLabel
	});
	function isAllowedView(view) {
		return toValue(props.allowedViews)?.includes(view) ?? true;
	}
	const gridLabelProps = computed(() => {
		return withRefCapture({
			...monthYearLabelBaseProps.value,
			"aria-live": "polite",
			tabindex: "-1",
			onClick: () => {
				if (isDisabled.value || toValue(props.readonly)) return;
				if (currentView.value.type === "weeks") {
					if (isAllowedView("months")) setView("months");
					return;
				}
				if (currentView.value.type === "months") {
					if (isAllowedView("years")) setView("years");
					return;
				}
			}
		}, calendarLabelEl);
	});
	const gridProps = computed(() => {
		return withRefCapture({
			id: gridId,
			role: "grid",
			...labelledByProps.value
		}, gridEl);
	});
	if (process.env.NODE_ENV !== "production") {
		if (!props.field) registerField(field, "Calendar");
	}
	return exposeField({
		calendarProps,
		gridProps,
		selectedDate,
		focusedDate: focusedDay,
		currentView,
		setView,
		gridLabelProps,
		nextButtonProps,
		previousButtonProps,
		gridLabel
	}, field);
}
function useCalendarKeyboard(context, currentPanel) {
	function withCheckedBounds(fn) {
		const date = fn();
		if (!date) return void 0;
		const minDate = context.getMinDate();
		const maxDate = context.getMaxDate();
		if (date && (minDate && date.compare(minDate) < 0 || maxDate && date.compare(maxDate) > 0)) return void 0;
		return date;
	}
	function getIncrement(direction) {
		const panelType = currentPanel.value.type;
		if (panelType === "weeks") {
			if (direction === "up" || direction === "down") return { weeks: 1 };
			return { days: 1 };
		}
		if (panelType === "months") {
			if (direction === "up" || direction === "down") return { months: 3 };
			return { months: 1 };
		}
		if (direction === "up" || direction === "down") return { years: 3 };
		return { years: 1 };
	}
	const shortcuts = {
		ArrowLeft: {
			fn: () => context.getFocusedDate().subtract(getIncrement("left")),
			type: "focus"
		},
		ArrowRight: {
			fn: () => context.getFocusedDate().add(getIncrement("right")),
			type: "focus"
		},
		ArrowUp: {
			fn: () => context.getFocusedDate().subtract(getIncrement("up")),
			type: "focus"
		},
		ArrowDown: {
			fn: () => context.getFocusedDate().add(getIncrement("down")),
			type: "focus"
		},
		Enter: {
			fn: () => context.getFocusedDate(),
			type: "select"
		},
		PageDown: {
			fn: () => {
				const type = currentPanel.value.type;
				if (type === "weeks") return context.getFocusedDate().add({ months: 1 });
				if (type === "months") return context.getFocusedDate().add({ years: 1 });
				return context.getFocusedDate().add({ years: YEAR_CELLS_COUNT });
			},
			type: "focus"
		},
		PageUp: {
			fn: () => {
				const type = currentPanel.value.type;
				if (type === "weeks") return context.getFocusedDate().subtract({ months: 1 });
				if (type === "months") return context.getFocusedDate().subtract({ years: 1 });
				return context.getFocusedDate().subtract({ years: YEAR_CELLS_COUNT });
			},
			type: "focus"
		},
		Home: {
			fn: () => {
				const current = context.getFocusedDate();
				const type = currentPanel.value.type;
				if (type === "weeks") {
					if (current.day === 1) return current.subtract({ months: 1 }).set({ day: 1 });
					return current.set({ day: 1 });
				}
				if (type === "months") {
					if (current.month === 1) return current.subtract({ years: 1 }).set({ month: 1 });
					return current.set({ month: 1 });
				}
				return current.set({ year: current.year - YEAR_CELLS_COUNT });
			},
			type: "focus"
		},
		End: {
			type: "focus",
			fn: () => {
				const type = currentPanel.value.type;
				const current = context.getFocusedDate();
				if (type === "weeks") {
					if (current.day === current.calendar.getDaysInMonth(current)) return current.add({ months: 1 }).set({ day: 1 });
					return current.set({ day: current.calendar.getDaysInMonth(current) });
				}
				if (type === "months") {
					if (current.month === current.calendar.getMonthsInYear(current)) return current.add({ years: 1 }).set({ month: 1 });
					return current.set({ month: current.calendar.getMonthsInYear(current) });
				}
				return current.set({ year: current.year + YEAR_CELLS_COUNT });
			}
		},
		Escape: {
			type: "focus",
			fn: () => {
				const selected = context.getSelectedDate();
				const focused = context.getFocusedDate();
				if (selected.compare(focused) !== 0) return context.getSelectedDate();
				return void 0;
			}
		}
	};
	function handleKeyDown(e) {
		const shortcut = shortcuts[e.code];
		if (!shortcut) return false;
		const newDate = withCheckedBounds(shortcut.fn);
		if (!newDate) return false;
		if (shortcut.type === "focus") context.setFocusedDate(newDate);
		else {
			const panelType = currentPanel.value.type;
			context.setDate(newDate, panelType === "years" ? "months" : panelType === "months" ? "weeks" : void 0);
		}
		return true;
	}
	return handleKeyDown;
}

//#endregion
//#region packages/core/src/useCalendar/useCalendarCell.ts
function useCalendarCell(_props) {
	const props = normalizeProps(_props);
	const cellEl = shallowRef();
	const calendarCtx = inject(CalendarContextKey, null);
	const isDisabled = createDisabledContext(props.disabled);
	function handlePointerDown(e) {
		if (isDisabled.value) {
			blockEvent(e);
			return;
		}
	}
	function handleClick(e) {
		if (isDisabled.value) {
			blockEvent(e);
			return;
		}
		const type = toValue(props.type);
		const nextPanel = type === "month" ? "weeks" : type === "year" ? "months" : void 0;
		calendarCtx?.setDate(toValue(props.value), nextPanel);
	}
	const cellProps = computed(() => {
		const isFocused = toValue(props.focused);
		return withRefCapture({
			onClick: handleClick,
			onPointerdown: handlePointerDown,
			"aria-selected": toValue(props.selected),
			"aria-disabled": isDisabled.value,
			tabindex: isDisabled.value || !isFocused ? "-1" : "0"
		}, cellEl);
	});
	const label = computed(() => toValue(props.label));
	return {
		cellProps,
		label,
		key: computed(() => `${toValue(props.type)}-${toValue(props.value).toString()}`)
	};
}
/**
* A helper component that renders a calendar cell. You can build your own with `useCalendarCell`.
*/
const CalendarCell = /* @__PURE__ */ defineComponent({
	name: "CalendarCell",
	inheritAttrs: true,
	props: [
		"value",
		"selected",
		"disabled",
		"focused",
		"label",
		"type",
		"monthOfYear",
		"year"
	],
	setup(props) {
		const { cellProps, label, key } = useCalendarCell(props);
		return () => h("span", {
			...cellProps.value,
			key: key.value
		}, label.value);
	}
});

//#endregion
//#region packages/core/src/useFormRepeater/useFormRepeater.ts
function useFormRepeater(_props) {
	const id = useUniqId(FieldTypePrefixes.FormRepeater);
	let counter = 0;
	const form = inject(FormKey, null);
	const repeaterProps = normalizeProps(_props);
	const getPath = () => toValue(repeaterProps.name);
	const getPathValue = () => form?.getValue(getPath()) || [];
	let lastControlledValueSnapshot;
	const records = ref(buildRecords());
	function generateRecord() {
		return `${id}-${counter++}`;
	}
	function buildRecords() {
		const pathArray = getPathValue();
		const length = Math.max(fromNumberish(repeaterProps.min) ?? 0, pathArray.length);
		lastControlledValueSnapshot = cloneDeep(pathArray);
		return Array.from({ length }).fill(null).map(generateRecord);
	}
	if (process.env.NODE_ENV !== "production") {
		if (!getPath()) warn("\"name\" prop is required for useFormRepeater");
	}
	async function mutateWith(mutate) {
		mutate();
		await nextTick();
		lastControlledValueSnapshot = cloneDeep(getPathValue());
	}
	function canAdd(count = 1) {
		const max = fromNumberish(repeaterProps.max);
		if (!max) return true;
		return records.value.length + count <= max;
	}
	function canRemove(count = 1) {
		const min = fromNumberish(repeaterProps.min);
		if (!min) return true;
		return records.value.length - count >= min;
	}
	function add(count = 1) {
		if (!canAdd(count)) {
			if (process.env.NODE_ENV !== "production") warn(`Cannot add ${count} item(s) to repeater since max is ${toValue(repeaterProps.max)}`);
			return;
		}
		mutateWith(() => {
			for (let i = 0; i < count; i++) records.value.push(generateRecord());
		});
	}
	function remove(index) {
		if (!canRemove()) {
			if (process.env.NODE_ENV !== "production") warn(`Cannot remove item from repeater since min is ${toValue(repeaterProps.min)}`);
			return;
		}
		mutateWith(() => {
			records.value.splice(index, 1);
		});
	}
	function insert(index) {
		if (!canAdd()) {
			if (process.env.NODE_ENV !== "production") warn(`Cannot insert item to repeater since max is ${toValue(repeaterProps.max)}`);
			return;
		}
		if (index < 0 || index > records.value.length) {
			if (process.env.NODE_ENV !== "production") warn(`Cannot insert item at index ${index} since it is out of bounds`);
			return;
		}
		mutateWith(() => {
			records.value.splice(index, 0, generateRecord());
		});
	}
	function move(from, to) {
		if (from === to) {
			if (process.env.NODE_ENV !== "production") warn("Cannot move item to the same index");
			return;
		}
		if (from < 0 || from >= records.value.length || to < 0 || to >= records.value.length) {
			if (process.env.NODE_ENV !== "production") warn(`Cannot move item from ${from} to ${to} since it is out of bounds`);
			return;
		}
		mutateWith(() => {
			records.value.splice(to, 0, records.value.splice(from, 1)[0]);
		});
	}
	function swap(indexA, indexB) {
		if (indexA === indexB) {
			if (process.env.NODE_ENV !== "production") warn("Cannot swap item with itself");
			return;
		}
		if (indexA < 0 || indexA >= records.value.length || indexB < 0 || indexB >= records.value.length) {
			if (process.env.NODE_ENV !== "production") warn(`Cannot swap item from ${indexA} to ${indexB} since it is out of bounds`);
			return;
		}
		mutateWith(() => {
			const newRecords = [...records.value];
			newRecords[indexA] = records.value[indexB];
			newRecords[indexB] = records.value[indexA];
			records.value = newRecords;
		});
	}
	const addButtonProps = createBtnProps({
		label: () => toValue(repeaterProps.addButtonLabel) ?? "Add",
		onClick: () => add(),
		disabled: () => !canAdd()
	});
	function createIterationButtonProps(index) {
		const removeButtonProps = createBtnProps({
			label: () => toValue(repeaterProps.removeButtonLabel) ?? "Remove",
			disabled: () => !canRemove(),
			onClick: () => remove(toValue(index))
		});
		const moveUpButtonProps = createBtnProps({
			label: () => toValue(repeaterProps.moveUpButtonLabel) ?? "Move up",
			disabled: () => toValue(index) === 0,
			onClick: () => {
				move(toValue(index), toValue(index) - 1);
			}
		});
		const moveDownButtonProps = createBtnProps({
			label: () => toValue(repeaterProps.moveDownButtonLabel) ?? "Move down",
			disabled: () => toValue(index) === records.value.length - 1,
			onClick: () => {
				const idx = toValue(index);
				move(idx, idx + 1);
			}
		});
		return {
			removeButtonProps,
			moveUpButtonProps,
			moveDownButtonProps
		};
	}
	const Iteration = defineComponent({
		name: "FormRepeaterIteration",
		props: {
			as: {
				type: String,
				required: false
			},
			index: Number
		},
		setup(props, { slots, attrs }) {
			createPathPrefixer((path) => isNullOrUndefined(path) ? void 0 : prefixPath(getPath(), `${props.index}${path ? `.${path}` : ""}`));
			const { removeButtonProps, moveUpButtonProps, moveDownButtonProps } = createIterationButtonProps(() => props.index);
			const getSlotProps = () => ({
				removeButtonProps: removeButtonProps.value,
				moveUpButtonProps: moveUpButtonProps.value,
				moveDownButtonProps: moveDownButtonProps.value
			});
			return () => {
				if (props.as) return h(props.as, attrs, { default: () => slots.default?.(getSlotProps()) });
				return slots.default?.(getSlotProps());
			};
		}
	});
	if (form) onMounted(() => {
		watch(getPathValue, (value) => {
			if (!isEqual(value, lastControlledValueSnapshot)) records.value = buildRecords();
		});
	});
	return {
		items: readonly(records),
		addButtonProps,
		add,
		swap,
		remove,
		move,
		insert,
		Iteration
	};
}
function createBtnProps(_props) {
	const props = normalizeProps(_props, ["onClick"]);
	const buttonEl = ref();
	const buttonProps = computed(() => {
		const isBtnTag = buttonEl.value?.tagName === "BUTTON";
		return withRefCapture({
			"aria-label": toValue(props.label),
			type: isBtnTag ? "button" : void 0,
			role: isBtnTag ? void 0 : "button",
			onClick: props.onClick,
			disabled: isBtnTag ? toValue(props.disabled) ?? void 0 : void 0,
			"aria-disabled": isBtnTag ? void 0 : toValue(props.disabled) ?? void 0
		}, buttonEl);
	});
	return buttonProps;
}

//#endregion
export { CalendarCell, CheckboxGroupKey, DateTimeSegment, FileEntry, FileEntryCollectionKey, FormContextKey, FormGroupKey, FormKey, HiddenField, ListManagerKey, OtpContextKey, OtpSlot, PickerContextKey, RadioGroupKey, SliderInjectionKey, configure, dateToString, exposeField, getConfig, normalizePath, useCalendar, useCalendarCell, useCalendarKeyboard, useCalendarView, useCheckbox, useCheckboxGroup, useComboBox, useConstraintsValidator, useCustomField, useDateField, useDateTimeSegment, useDefaultFilter, useFileEntry, useFileField, useForm, useFormContext, useFormField, useFormGroup, useFormRepeater, useHiddenField, useInputValidity, useListBox, useLocale, useNumberField, useOption, useOptionGroup, useOtpField, useOtpSlot, usePicker, useRadio, useRadioGroup, useSearchField, useSelect, useSlider, useSliderThumb, useSwitch, useTextField, useTimeField, version };